import React, { useState, useEffect, useRef, startTransition } from 'react'
import { PlusIcon, TrashIcon, ArrowDownIcon } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { MobilePage, MobilePageHeader, mobile } from '../components/mobile'
import { useNavigate, useSearchParams } from 'react-router-dom'
import ScheduleView from '../components/ScheduleView'
import { BillingDayOfMonthSelect } from '../components/BillingDayOfMonthSelect'
import { convertLeadToCustomer } from '../lib/leadConversion'
import { fetchServiceItemsGrouped, ServiceItemGrouped } from '../utils/serviceItems'
import { ServiceItemSelect } from '../components/service/ServiceItemSelect'
import {
  getEntityActivities,
  ActivityLog,
  logActivity,
  ActivityTypes,
  logActivityOnLinkedParents,
} from '../utils/activityLogger'
import {
  VisitGroupEditor,
  ContractVisitGroup,
  validateVisitGroups,
  describeVisitGroupCadence,
} from '../components/VisitGroupEditor'
import { VisitScheduleBoard } from '../components/VisitScheduleBoard'
import {
  getGroupAssignments,
  syncGroupServiceIds,
  VisitGroupServiceAssignment,
  describeContractServiceSchedule,
} from '../lib/visitGroupBoardOps'
import { useVisitGroupTemplates } from '../hooks/useVisitGroupTemplates'
import { useCombinationRules } from '../hooks/useCombinationRules'
import ConsistencyWarnings from '../components/agreement/ConsistencyWarnings'
import { checkConsistency, splitIssues, getDefaultTemplateTypeForServiceType } from '../utils/consistencyRules'
import { SERVICE_TYPE_OPTIONS, isDsnyServiceType } from '../lib/serviceTypeOptions'
import { AgreementState, IssueTarget, DocumentData, PricingMode, ContractType } from '../types/agreement'
import { buildDefaultDocumentData } from '../lib/proposalDocumentTemplates'
import { normalizeUnitType } from '../lib/unitTypeHelpers'
import { buildRateCardLineItems, hasRateCardSeed } from '../lib/rateCardLineItems'
import { computeEndDateFromMonths } from '../lib/contractDuration'
import { draftDsnyWorkSpecFrequencies } from '../lib/workSpecAutoDraft'
import { CONTRACT_STATUS_OPTIONS, contractWorkflowType } from '../lib/contractStatusWorkflow'
import {
  deriveDsnyContractArtifacts,
  persistContractVisitGroups,
  buildDsnyVisitBoardServices,
  buildDsnyVisitGroupsFromSchedule,
  buildContractServiceIdMap,
  countUnresolvedVisitGroupAssignments,
  finalizeDsnyVisitGroupsAfterSeed,
  findDsnyPackageBoardId,
  buildDsnyChildItemsByType,
  syncVisitGroupAssigneesFromScheduleRows,
  preserveVisitGroupTargetAssignees,
  attachMissingNonWeeklyBoardServices,
  ensureDsnyVisitGroupsForDisplay,
} from '../lib/dsnyContractPersistence'
import { ContractAssignmentFields } from '../components/contract/ContractAssignmentFields'
import { ContractPropertyScopeFields } from '../components/contract/ContractPropertyScopeFields'
import {
  syncContractProperties,
  type ContractScopeType,
} from '../api/turnoverContracts'
import { useDivision } from '../contexts/DivisionContext'
import { resolveOperationalDivisionId } from '../lib/operationalDivisions'
import type { AssignmentSource } from '../api/jobAssignmentDefaults'
import {
  fetchPackageChildItems,
  buildPackageChildServices,
} from '../lib/serviceItemPackages'
import DocumentDataEditor from '../components/agreement/DocumentDataEditor'
import { LineItemUnitTypeSelect } from '../components/agreement/LineItemUnitTypeSelect'
import { PricingPresentationFields } from '../components/agreement/PricingPresentationFields'
import MilestoneScheduleEditor from '../components/agreement/MilestoneScheduleEditor'
import {
  createEmptyMilestone,
  fetchProposalMilestones,
  saveContractMilestones,
} from '../lib/milestoneBilling'
import { BillingMilestone } from '../types/agreement'
import {
  DSNY_PACKAGE_SERVICE_ID,
  applyDsnyApiDaysToRows,
  defaultDsnyScheduleRowsForKind,
  detectDsnyPackageKind,
  findBuildingUpkeepItem,
  findDsnyPackageItem,
  findExteriorMaintenanceItem,
  isDsnyScheduleValid,
  listCatalogItemsForCustomDsnyRows,
  mapProposalLineItemsToServicesAndLineItems,
  rebaseDsnyRowsForPackageKind,
  removeDsnyPackagePair,
  removeLinkedLineItem,
  resolveLineItemRecurrenceForConsistency,
  resolveDsnyCombineTarget,
  dsnyRowNeedsCombineTarget,
  expandDsnyPackageServiceIdMap,
  isScheduleDerivedContractService,
  syncDsnyPackageInState,
  syncNonWeeklyDsnyLineItems,
  toUuidOrNull,
  upsertLinkedLineItem,
  type DsnyPackageKind,
  type DsnyCombineTarget,
} from '../lib/contractServiceLineItems'
import {
  DsnyVisitRepAssignment,
  visitTargetFromSelectValue,
  visitTargetSelectValue,
} from '../components/contract/DsnyVisitRepAssignment'
import {
  fetchCrewsAndEmployees,
  type CrewOption,
  type EmployeeOption,
} from '../api/jobWorkflow'
import { formatEmployeeLabel } from '../lib/formatEmployeeLabel'
import { getDSNYPickupDays, pickupSchedulesToPickupDayNames, serviceDayNumbersToPickupDayNames } from '../utils/dsnyHelpers'
import {
  DsnyRowRecurrenceType,
  DsnyMonthlyMode,
  RECURRENCE_TYPE_OPTIONS,
  OCCURRENCE_OPTIONS,
  WEEKDAY_OPTIONS,
  getRowRecurrenceType,
  isRowWeekly,
  isSnapToClosestRow,
  buildRowRecurrenceFields,
} from '../lib/dsnyRowRecurrence'

interface Customer {
  id: string
  company_name?: string
  contact_first_name?: string
  contact_last_name?: string
}

interface Property {
  id: string
  name?: string
  address?: string
  city?: string
  state?: string
  customer_id: string
}

interface Proposal {
  id: string
  title: string
  proposal_number: string
  customer_id: string
  property_id: string
  total_amount?: number
  status: string
}

interface ServiceCategory {
  id: string
  name: string
  operational_division_id: string
}

interface ServiceItem {
  id: string
  service_category_id: string
  name: string
  description?: string
  unit_type: string
  base_price: number
  is_active: boolean
  categories?: {
    id: string
    name: string
  }
}

type DsnyScheduleRowState = {
  id: string
  type: 'garbage' | 'recycling' | 'organics' | 'bulk' | 'hallway_cleaning' | 'custom'
  name: string
  days: number[]
  combine_with_dsny: boolean
  /** undefined = not chosen yet (custom rows); null = separate visit */
  combine_target?: DsnyCombineTarget
  assigned_crew_id?: string | null
  assigned_employee_id?: string | null
  /** Catalogue item for custom schedule lines (drives checklist). */
  service_item_id?: string
  // Recurrence override (custom + hallway rows only; pickup rows stay weekly)
  recurrence_type?: DsnyRowRecurrenceType
  monthly_mode?: DsnyMonthlyMode
  recurrence_weekday?: number | null
  recurrence_occurrence?: number | null
  recurrence_day_of_month?: number | null
  snap_to_closest_visit?: boolean
}

function normalizeRowDays(days: unknown): number[] {
  if (!Array.isArray(days)) return []
  return days.filter((d): d is number => typeof d === 'number' && d >= 0 && d <= 6)
}

export function ContractCreate() {
  const navigate = useNavigate()
  const { divisions } = useDivision()
  const [searchParams] = useSearchParams()
  const returnTo = searchParams.get('returnTo')
  const [loading, setLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<
    'overview' | 'services' | 'billing' | 'notes'
  >('overview')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [properties, setProperties] = useState<Property[]>([])
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [serviceCategories, setServiceCategories] = useState<ServiceCategory[]>([])
  const [proposalLineItems, setProposalLineItems] = useState<any[]>([]) // Store proposal line items for linking
  const [serviceItems, setServiceItems] = useState<ServiceItem[]>([])
  const [maintenanceDSNYCategoryId, setMaintenanceDSNYCategoryId] = useState<string>('')
  const [crews, setCrews] = useState<CrewOption[]>([])
  const [employees, setEmployees] = useState<EmployeeOption[]>([])
  const [filteredProposals, setFilteredProposals] = useState<Proposal[]>([])
  const [showCreateCustomer, setShowCreateCustomer] = useState(false)
  const [showCreateProperty, setShowCreateProperty] = useState(false)
  const [showAddServiceModal, setShowAddServiceModal] = useState(false)
  const [recurringScheduleType, setRecurringScheduleType] = useState<
    'maintenance_dsny' | 'other' | null
  >(null)

  // Store the proposal_line_item_id to link when DSNY schedule is created
  const [dsnyProposalLinkId, setDsnyProposalLinkId] = useState<string | null>(null)
  // Price of the proposal's DSNY package line item; wins over the catalog base price
  const [dsnyProposalAmount, setDsnyProposalAmount] = useState<number | null>(null)
  const [dsnyPackageKind, setDsnyPackageKind] = useState<DsnyPackageKind>('building_upkeep')
  // Billing totals auto-calculate from line items unless the user overrides them
  const [billingAmountOverride, setBillingAmountOverride] = useState(false)
  // Once the user edits PDF work specs by hand, stop auto-drafting them from the schedule
  const [workSpecsCustomized, setWorkSpecsCustomized] = useState(false)

  // Dialog states for linking confirmations
  const [linkDialog, setLinkDialog] = useState<{
    show: boolean
    type: 'service-to-proposal' | 'line-item-to-service' | 'dsny-to-proposal'
    service?: any
    lineItem?: ContractLineItem
    matchingProposalItem?: any
    matchingService?: any
    onConfirm: () => void
    onCancel: () => void
  } | null>(null)

  // State for linking jobs to contract modal
  const [linkJobsModal, setLinkJobsModal] = useState<{
    show: boolean
    contractId: string
    jobs: Array<{
      id: string
      job_number: string
      title: string
      scheduled_date?: string
      status: string
    }>
  } | null>(null)
  const [selectedJobsToLink, setSelectedJobsToLink] = useState<Set<string>>(new Set())

  const [editingServiceId, setEditingServiceId] = useState<string | null>(null)

  // DSNY Schedule Rows for Maintenance DSNY recurring.
  // `combine_with_dsny` controls whether a row shares a visit/job with the other DSNY
  // pickup rows that fall on the same day. Hallway Cleaning and custom rows default to
  // FALSE so they generate their own separate visit; pickup rows default to TRUE.
  const [dsnyScheduleRows, setDsnyScheduleRows] = useState<Array<DsnyScheduleRowState>>([])

  // Auto-fetch DSNY schedule when property is selected and rows are initialized
  const [dsnyScheduleFetched, setDsnyScheduleFetched] = useState(false)
  const [fetchingDSNY, setFetchingDSNY] = useState(false)
  const [notification, setNotification] = useState<{
    message: string
    type: 'success' | 'error'
  } | null>(null)

  // Visit Groups - derived live from the schedule on the Services & Schedule tab and persisted on submit
  const [visitGroups, setVisitGroups] = useState<ContractVisitGroup[]>([])
  // Tracks whether the auto-suggester has already run, so we don't keep overwriting user edits
  const [visitGroupsSeeded, setVisitGroupsSeeded] = useState(false)
  // Once the user manually customizes groups (drag/drop or advanced editor), stop auto re-deriving
  const [visitGroupsCustomized, setVisitGroupsCustomized] = useState(false)
  // Expanded state for the "Customize visits" board within the Services & Schedule tab
  const [showVisitBoard, setShowVisitBoard] = useState(false)
  const [advancedVisitGroupId, setAdvancedVisitGroupId] = useState<string | null>(null)
  // Library of reusable visit-group templates
  const { templates: visitGroupTemplates, saveGroupAsTemplate } = useVisitGroupTemplates()
  // Global combination rules for auto-deriving visit groups
  const { suggestGroupsDayCentric } = useCombinationRules()

  useEffect(() => {
    let cancelled = false
    fetchCrewsAndEmployees().then(({ crews: crewOptions, employees: employeeOptions }) => {
      if (cancelled) return
      setCrews(crewOptions)
      setEmployees(employeeOptions)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const toggleDayForRow = (rowId: string, day: number) => {
    setDsnyScheduleRows((rows) =>
      rows.map((row) => {
        if (row.id !== rowId) return row
        const days = normalizeRowDays(row.days)
        return {
          ...row,
          days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort(),
        }
      })
    )
  }

  const addDsnyScheduleRow = () => {
    setDsnyScheduleRows((rows) => [
      ...rows,
      {
        id: `custom_${Date.now()}`,
        type: 'custom',
        name: '',
        days: [],
        // No visit target until the user chooses — do not default to Separate.
        combine_with_dsny: false,
        service_item_id: '',
        recurrence_type: 'weekly',
        monthly_mode: 'nth_weekday',
        recurrence_weekday: null,
        recurrence_occurrence: null,
        recurrence_day_of_month: null,
        snap_to_closest_visit: false,
      },
    ])
  }

  const updateDsnyScheduleRow = (rowId: string, patch: Partial<DsnyScheduleRowState>) => {
    setDsnyScheduleRows((rows) =>
      rows.map((row) => {
        if (row.id !== rowId) return row
        const next = { ...row, ...patch }
        if (Object.prototype.hasOwnProperty.call(patch, 'combine_target')) {
          next.combine_with_dsny = patch.combine_target === 'dsny'
        }
        return next
      })
    )
  }

  const updateDsnyRowAssignments = (
    rowIds: string[],
    patch: Pick<DsnyScheduleRowState, 'assigned_crew_id'> |
      Pick<DsnyScheduleRowState, 'assigned_employee_id'>
  ) => {
    const serviceIds = new Set(
      dsnyScheduleRows
        .filter((row) => rowIds.includes(row.id))
        .map((row) => `dsny_${row.id}`)
    )
    setDsnyScheduleRows((rows) =>
      rows.map((row) => (rowIds.includes(row.id) ? { ...row, ...patch } : row))
    )
    setVisitGroups((groups) =>
      groups.map((group) =>
        getGroupAssignments(group).some((assignment) => serviceIds.has(assignment.service_id))
          ? { ...group, ...patch }
          : group
      )
    )
  }

  const removeDsnyScheduleRow = (rowId: string) => {
    setDsnyScheduleRows((rows) => rows.filter((row) => row.id !== rowId))
  }

  const updateDsnyScheduleRowName = (rowId: string, name: string) => {
    setDsnyScheduleRows((rows) => rows.map((row) => (row.id === rowId ? { ...row, name } : row)))
  }

  const selectCustomDsnyCatalogItem = (rowId: string, serviceItemId: string) => {
    const options = listCatalogItemsForCustomDsnyRows(serviceItemsGrouped)
    const item = options.find((o) => o.id === serviceItemId)
    updateDsnyScheduleRow(rowId, {
      service_item_id: serviceItemId || '',
      name: item?.name || '',
    })
  }

  // Day names for schedule checkboxes
  const dayNames = [
    { key: 0, label: 'Sunday' },
    { key: 1, label: 'Monday' },
    { key: 2, label: 'Tuesday' },
    { key: 3, label: 'Wednesday' },
    { key: 4, label: 'Thursday' },
    { key: 5, label: 'Friday' },
    { key: 6, label: 'Saturday' },
  ]

  // Add Service Modal state
  const [newService, setNewService] = useState({
    service_type: '',
    service_name: '',
    amount: 0,
    description: '',
    scheduled_date: '',
    scheduled_time: '',
    recurrence_type: 'none', // Changed default to 'none' for one-time projects
    recurrence_days: [] as number[], // For weekly/bi-weekly: days of week (0-6)
    recurrence_day_of_month: null as number | null, // For monthly/quarterly: day of month (1-31)
    recurrence_weekday: null as number | null, // For monthly/quarterly: day of week (0-6) when using "nth weekday"
    recurrence_occurrence: null as number | null, // For monthly/quarterly: 1st, 2nd, 3rd, 4th, last (1-5, 5=last)
    recurrence_month: null as number | null, // For yearly: month (1-12)
    recurrence_interval_months: 1, // For custom: every N months
    recurrence_interval_weeks: 1, // For custom: every N weeks
    recurrence_interval_days: 1, // For custom: every N days
    dsny_integration: false,
    // DSNY schedules
    garbage_schedule: [] as number[],
    recycling_schedule: [] as number[],
    organics_schedule: [] as number[],
    bulk_schedule: [] as number[],
    interior_cleaning_schedule: [] as number[],
  })

  // Services with individual schedules
  type ContractServiceRow = {
    id: string
    service_item_id: string
    service_type: string
    service_name: string
    amount: number
    description?: string
    scheduled_date?: string | null
    scheduled_time?: string | null
    is_maintenance: boolean
    proposal_line_item_id?: string
    schedule: {
      recurrence_type: string | null
      recurrence_days: number[]
      recurrence_day_of_month?: number | null
      recurrence_weekday?: number | null
      recurrence_occurrence?: number | null
      recurrence_month?: number | null
      recurrence_interval_months?: number
      recurrence_interval_weeks?: number
      recurrence_interval_days?: number
      dsny_integration: boolean
      combine_target?: DsnyCombineTarget
      garbage_schedule: number[]
      recycling_schedule: number[]
      organics_schedule: number[]
      bulk_schedule: number[]
      interior_cleaning_schedule: number[]
    }
  }

  const [contractServices, setContractServices] = useState<ContractServiceRow[]>([])

  // Line items for billing
  interface ContractLineItem {
    id: string
    service_item_id?: string
    proposal_line_item_id?: string // Links back to proposal line item for reporting
    contract_service_id?: string // Backend link to contract_services table
    description: string
    quantity: number
    is_variable: boolean // Checkbox for variable items (per hour, per unit, etc.)
    unit_type: string
    unit_price: number
    total_price: number
    sort_order: number
    linked_service_id?: string // Frontend link to service in Services & Schedule tab (for display/mapping)
  }

  const [lineItems, setLineItems] = useState<ContractLineItem[]>([])
  const contractServicesRef = useRef(contractServices)
  const lineItemsRef = useRef(lineItems)
  contractServicesRef.current = contractServices
  lineItemsRef.current = lineItems

  const [serviceItemsGrouped, setServiceItemsGrouped] = useState<ServiceItemGrouped[]>([])
  const customDsnyCatalogOptions = listCatalogItemsForCustomDsnyRows(serviceItemsGrouped)
  const [showServiceSelectionModal, setShowServiceSelectionModal] = useState(false)
  const [selectedServicesForImport, setSelectedServicesForImport] = useState<Set<string>>(new Set())
  const [activities, setActivities] = useState<ActivityLog[]>([])
  // Contract duration entry mode (UI-only; not persisted). 'months' derives the end
  // date from start + N months - 1 day; 'end_date' lets the user pick an exact end date.
  const [durationMode, setDurationMode] = useState<'months' | 'end_date'>('months')
  const [durationMonths, setDurationMonths] = useState(12)
  const [billingMilestones, setBillingMilestones] = useState<BillingMilestone[]>([])

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    template_type: '', // Template type (maintenance, cleaning, etc.)
    contract_type: '', // Contract type (one_time, recurring) - blank by default; user must choose
    service_type: '',
    customer_id: '',
    property_id: '',
    proposal_id: '',
    parent_contract_id: '', // For contract renewals
    original_contract_id: '', // Original contract in renewal chain
    total_amount: 0, // Per-period amount or total if one-time
    contract_total_amount: 0, // Total contract value (for divided billing)
    billing_frequency: '',
    billing_day_of_month: 1,
    billing_method: 'per_period' as 'per_period' | 'divide_total' | 'actual_usage',
    payment_method: '' as '' | 'cash' | 'check' | 'ach' | 'credit_card',
    // per_period = total_amount is billed each period
    // divide_total = contract_total_amount ÷ number of periods
    // actual_usage = invoices generated based on actual time/sqft/visits
    is_recurring: false,
    recurrence_type: 'weekly',
    recurrence_interval: 1,
    recurrence_days: [] as number[],
    recurrence_end_date: '',
    recurrence_end_count: 0,
    status: 'draft',
    start_date: '',
    end_date: '',
    cancel_date: '',
    payment_terms: 'Net 30',
    late_fee_percentage: 0,
    sales_tax_status: '' as '' | 'taxable' | 'exempt',
    cancellation_terms: '',
    notes: '',
    // Comprehensive Schedule System
    master_weekly_schedule: [] as number[], // Days of week we visit (0-6)
    garbage_schedule: [] as number[], // Days for garbage prep
    recycling_schedule: [] as number[], // Days for recycling prep
    bulk_schedule: [] as number[], // Days for bulk prep
    organics_schedule: [] as number[], // Days for organics prep
    interior_cleaning_schedule: [] as number[], // Days for interior cleaning
    // Dynamic manual schedules
    manual_schedules: [] as Array<{
      id: string
      name: string
      description: string
      color: string
      days: number[]
    }>,
    dsny_integration: false,
    // Legacy fields for compatibility
    dsny_pickup_days: [] as string[],
    dsny_collection_types: [] as string[],
    // Document generation
    scope_of_work: '',
    document_data: {} as DocumentData,
    // Required on Services tab — nothing selected until the user chooses.
    assignment_source: '' as AssignmentSource | '',
    default_assigned_crew_id: '',
    default_assigned_employee_id: '',
  })

  const [scopeType, setScopeType] = useState<ContractScopeType>('single_property')
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([])

  useEffect(() => {
    if (scopeType === 'single_property' && formData.property_id) {
      setSelectedPropertyIds([formData.property_id])
    }
  }, [formData.property_id, scopeType])

  useEffect(() => {
    if (formData.contract_type === 'recurring' && scopeType !== 'single_property') {
      setScopeType('single_property')
      setSelectedPropertyIds(formData.property_id ? [formData.property_id] : [])
    }
  }, [formData.contract_type, formData.property_id, scopeType])

  const [propertyAssignmentLabels, setPropertyAssignmentLabels] = useState<{
    crewName: string | null
    employeeName: string | null
  } | null>(null)

  useEffect(() => {
    fetchData()
    fetchServiceItems()

    // Check if proposalId is in URL (from ProposalView/ProposalEdit)
    const proposalId = searchParams.get('proposalId')
    if (proposalId) {
      loadProposalData(proposalId)
    }

    // Check if renewing from existing contract
    const renewContractId = searchParams.get('renewContractId')
    if (renewContractId) {
      loadContractForRenewal(renewContractId)
    }
  }, [])

  // Auto-draft PDF work spec frequencies from the DSNY schedule (e.g. garbage 3 days/week)
  // until the user edits the work specs by hand.
  useEffect(() => {
    if (recurringScheduleType !== 'maintenance_dsny' || workSpecsCustomized) return
    setFormData((prev) => {
      const specs = prev.document_data?.workSpecs
      if (!specs || specs.length === 0) return prev
      const drafted = draftDsnyWorkSpecFrequencies(specs, dsnyScheduleRows)
      if (drafted === specs) return prev
      return { ...prev, document_data: { ...prev.document_data, workSpecs: drafted } }
    })
  }, [dsnyScheduleRows, recurringScheduleType, workSpecsCustomized])

  // In "months" duration mode, derive end_date = start_date + N months - 1 day.
  // In "end_date" mode the user picks the end date directly (no auto-derivation).
  useEffect(() => {
    if (durationMode !== 'months' || !formData.start_date) return
    const endDateString = computeEndDateFromMonths(formData.start_date, durationMonths)
    if (!endDateString) return
    setFormData((prev) => (prev.end_date === endDateString ? prev : { ...prev, end_date: endDateString }))
  }, [durationMode, durationMonths, formData.start_date])

  // Seed default DSNY rows when maintenance DSNY mode is selected
  useEffect(() => {
    if (recurringScheduleType === 'maintenance_dsny' && dsnyScheduleRows.length === 0) {
      setDsnyScheduleRows(
        defaultDsnyScheduleRowsForKind(dsnyPackageKind) as DsnyScheduleRowState[]
      )
    }
  }, [recurringScheduleType, dsnyScheduleRows.length, dsnyPackageKind])

  // Auto-fetch DSNY schedule when property is selected and rows are initialized
  useEffect(() => {
    if (
      recurringScheduleType === 'maintenance_dsny' &&
      formData.property_id &&
      dsnyScheduleRows.length > 0 &&
      dsnyScheduleRows.every((row) => normalizeRowDays(row.days).length === 0) &&
      !dsnyScheduleFetched
    ) {
      const selectedProperty = properties.find((p) => p.id === formData.property_id)
      if (selectedProperty?.address) {
        setDsnyScheduleFetched(true)
        fetchDSNYPickupSchedule(selectedProperty.address)
          .then((dsnyData) => {
            setDsnyScheduleRows(
              applyDsnyApiDaysToRows(
                dsnyPackageKind,
                dsnyData.schedules,
                dsnyScheduleRows
              ) as DsnyScheduleRowState[]
            )
          })
          .catch((error) => {
            console.error('Error auto-fetching DSNY schedule:', error)
            setDsnyScheduleFetched(false) // Reset on error so it can retry
          })
      }
    }

    // Reset fetch flag when switching away from maintenance_dsny
    if (recurringScheduleType !== 'maintenance_dsny') {
      setDsnyScheduleFetched(false)
    }
  }, [
    formData.property_id,
    recurringScheduleType,
    properties,
    dsnyScheduleRows.length,
    dsnyScheduleFetched,
    dsnyPackageKind,
  ])

  // Sync DSNY package service + linked catalogue package line item when schedule is configured
  useEffect(() => {
    const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
    const syncOptions = {
      buildingUpkeepItem: packageItem,
      maintenanceDSNYCategoryId,
      proposalLineItemId: dsnyProposalLinkId,
      // Proposal price wins over catalog base price
      amount: dsnyProposalAmount ?? undefined,
    }

    if (recurringScheduleType !== 'maintenance_dsny') {
      const cleared = removeDsnyPackagePair(contractServicesRef.current, lineItemsRef.current)
      const servicesJson = JSON.stringify(cleared.contractServices)
      const lineItemsJson = JSON.stringify(cleared.lineItems)
      if (servicesJson !== JSON.stringify(contractServicesRef.current)) {
        setContractServices(cleared.contractServices)
      }
      if (lineItemsJson !== JSON.stringify(lineItemsRef.current)) {
        setLineItems(cleared.lineItems)
      }
      return
    }

    if (!isDsnyScheduleValid(dsnyScheduleRows)) {
      const cleared = removeDsnyPackagePair(contractServicesRef.current, lineItemsRef.current)
      const servicesJson = JSON.stringify(cleared.contractServices)
      const lineItemsJson = JSON.stringify(cleared.lineItems)
      if (servicesJson !== JSON.stringify(contractServicesRef.current)) {
        setContractServices(cleared.contractServices)
      }
      if (lineItemsJson !== JSON.stringify(lineItemsRef.current)) {
        setLineItems(cleared.lineItems)
      }
      return
    }

    const synced = syncDsnyPackageInState(
      contractServicesRef.current,
      lineItemsRef.current,
      dsnyScheduleRows,
      syncOptions
    )

    const servicesJson = JSON.stringify(synced.contractServices)
    const lineItemsJson = JSON.stringify(synced.lineItems)
    const servicesChanged = servicesJson !== JSON.stringify(contractServicesRef.current)
    const lineItemsChanged = lineItemsJson !== JSON.stringify(lineItemsRef.current)
    if (!servicesChanged && !lineItemsChanged) return
    // Defer package/line-item updates so day-pill clicks paint first and do not
    // compete with main-scroll anchoring while Crew Visits reflows below.
    startTransition(() => {
      if (servicesChanged) setContractServices(synced.contractServices)
      if (lineItemsChanged) setLineItems(synced.lineItems)
    })
  }, [
    recurringScheduleType,
    dsnyScheduleRows,
    maintenanceDSNYCategoryId,
    dsnyProposalLinkId,
    dsnyProposalAmount,
    serviceItemsGrouped,
    dsnyPackageKind,
  ])

  // Keep formData DSNY schedule fields aligned with dsnyScheduleRows (consistency rules + billing)
  useEffect(() => {
    if (recurringScheduleType !== 'maintenance_dsny') return

    const rowDays = (type: string) =>
      normalizeRowDays(dsnyScheduleRows.find((row) => row.type === type)?.days)
    const garbage = rowDays('garbage')
    const recycling = rowDays('recycling')
    const organics = rowDays('organics')
    const bulk = rowDays('bulk')
    // Only a weekly hallway row feeds the weekly interior-cleaning schedule; non-weekly
    // hallway rows generate their own visit and are excluded here.
    const hallwayRow = dsnyScheduleRows.find((row) => row.type === 'hallway_cleaning')
    const interior = hallwayRow && isRowWeekly(hallwayRow) ? normalizeRowDays(hallwayRow.days) : []
    const allService = Array.from(new Set([...garbage, ...recycling, ...organics, ...bulk]))
    const dsnyPickupDayNames = serviceDayNumbersToPickupDayNames(allService)

    setFormData((prev) => {
      const unchanged =
        JSON.stringify(prev.garbage_schedule) === JSON.stringify(garbage) &&
        JSON.stringify(prev.recycling_schedule) === JSON.stringify(recycling) &&
        JSON.stringify(prev.organics_schedule) === JSON.stringify(organics) &&
        JSON.stringify(prev.bulk_schedule) === JSON.stringify(bulk) &&
        JSON.stringify(prev.interior_cleaning_schedule) === JSON.stringify(interior) &&
        JSON.stringify(prev.master_weekly_schedule) === JSON.stringify(allService) &&
        JSON.stringify(prev.recurrence_days) === JSON.stringify(allService) &&
        JSON.stringify(prev.dsny_pickup_days) === JSON.stringify(dsnyPickupDayNames) &&
        prev.dsny_integration === allService.length > 0
      if (unchanged) return prev

      return {
        ...prev,
        dsny_integration: allService.length > 0,
        garbage_schedule: garbage,
        recycling_schedule: recycling,
        organics_schedule: organics,
        bulk_schedule: bulk,
        interior_cleaning_schedule: interior,
        master_weekly_schedule: allService,
        recurrence_days: allService,
        dsny_pickup_days: dsnyPickupDayNames,
        recurrence_type: allService.length > 0 ? 'custom' : prev.recurrence_type,
      }
    })
  }, [recurringScheduleType, dsnyScheduleRows])

  // Auto-fetch DSNY schedule when modal opens for Maintenance DSNY recurring
  useEffect(() => {
    if (
      showAddServiceModal &&
      formData.contract_type === 'recurring' &&
      recurringScheduleType === 'maintenance_dsny' &&
      formData.property_id &&
      newService.dsny_integration &&
      newService.garbage_schedule.length === 0
    ) {
      const selectedProperty = properties.find((p) => p.id === formData.property_id)
      if (selectedProperty?.address) {
        fetchDSNYPickupSchedule(selectedProperty.address)
          .then((dsnyData) => {
            const garbageDays = dsnyData.schedules.garbage || []
            const recyclingDays = dsnyData.schedules.recycling || []
            const organicsDays = dsnyData.schedules.organics || []
            const bulkDays = dsnyData.schedules.bulk || []

            const allCollectionDays = Array.from(
              new Set([...garbageDays, ...recyclingDays, ...organicsDays, ...bulkDays])
            )

            setNewService({
              ...newService,
              recurrence_days:
                allCollectionDays.length > 0 ? allCollectionDays : newService.recurrence_days,
              garbage_schedule: garbageDays,
              recycling_schedule: recyclingDays,
              organics_schedule: organicsDays,
              bulk_schedule: bulkDays,
              interior_cleaning_schedule: [],
            })
          })
          .catch((error) => {
            console.error('Error auto-fetching DSNY schedule:', error)
          })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddServiceModal, recurringScheduleType, formData.property_id])

  const loadProposalData = async (proposalId: string) => {
    try {
      // Fetch proposal with all related data
      const { data: proposal, error } = await supabase
        .from('proposals')
        .select(
          `
          *,
          customer_id,
          property_id,
          proposal_line_items(*)
        `
        )
        .eq('id', proposalId)
        .single()

      if (error) throw error

      if (proposal) {
        // Create the proposal entry
        const proposalEntry = {
          id: proposal.id,
          title: proposal.title || '',
          proposal_number: proposal.proposal_number || '',
          customer_id: proposal.customer_id || '',
          property_id: proposal.property_id || '',
          total_amount: proposal.total || proposal.total_amount || 0,
          status: proposal.status || '',
        }

        // Ensure the proposal is in the proposals array so it shows in the dropdown
        // Use a function to merge with existing proposals
        setProposals((prev) => {
          const exists = prev.some((p) => p.id === proposalId)
          if (!exists) {
            // Add the proposal to the list if it's not already there
            // Put it at the beginning so it's easy to find
            return [proposalEntry, ...prev]
          }
          // If it exists, update it to ensure it has the latest data
          return prev.map((p) => (p.id === proposalId ? proposalEntry : p))
        })

        // Prefer proposal.contract_type (including as_needed); fall back to line-item inference
        const proposalType = proposal.contract_type as string | undefined
        const hasRecurringItems =
          proposal.proposal_line_items?.some(
            (item: any) => item.recurrence_type && item.recurrence_type !== 'one_time'
          ) || proposalType === 'recurring'
        const resolvedContractType: '' | ContractType =
          proposalType === 'as_needed' || proposalType === 'recurring' || proposalType === 'one_time'
            ? proposalType
            : hasRecurringItems
              ? 'recurring'
              : 'one_time'

        // Pre-populate form data from proposal
        // Set proposal_id first to ensure it's available for filteredProposals logic
        setFormData((prev) => ({
          ...prev,
          proposal_id: proposalId, // Set this first
          title: proposal.title || '',
          description: proposal.description || '',
          template_type: proposal.template_type || '',
          service_type: proposal.service_type || proposal.template_type || '',
          contract_type: resolvedContractType,
          is_recurring: resolvedContractType === 'recurring',
          scope_of_work: proposal.scope_of_work || '',
          document_data:
            proposal.document_data && Object.keys(proposal.document_data).length > 0
              ? proposal.document_data
              : buildDefaultDocumentData(proposal.service_type || proposal.template_type || ''),
          customer_id: proposal.customer_id || '',
          property_id: proposal.property_id || '',
          total_amount: proposal.total || proposal.total_amount || 0,
          payment_terms: proposal.payment_terms?.toString() || 'Net 30',
          sales_tax_status: proposal.sales_tax_status || '',
          billing_frequency: proposal.billing_frequency || 'monthly',
          billing_day_of_month: Number((proposal as any).billing_day_of_month) || 1,
          start_date: proposal.start_date || '',
          end_date: proposal.end_date || '',
          dsny_integration:
            Boolean(proposal.dsny_integration) || isDsnyServiceType(proposal.service_type),
          // Phase 8 conversion glue: inherit billing_method / payment_method from proposal
          billing_method: (proposal.billing_method as any) || prev.billing_method || 'per_period',
          payment_method: (proposal.payment_method as any) || (prev as any).payment_method || '',
        }))

        // Preserve the proposal's end date: switch duration entry to "specific end date"
        // so the default months mode doesn't overwrite it with start + 12 months
        if (proposal.end_date) {
          setDurationMode('end_date')
        }

        if (isDsnyServiceType(proposal.service_type) || proposal.dsny_integration) {
          setRecurringScheduleType('maintenance_dsny')
        }

        try {
          const milestones = await fetchProposalMilestones(proposalId)
          setBillingMilestones(milestones)
        } catch {
          setBillingMilestones([])
        }

        // If customer_id is set, fetch properties
        if (proposal.customer_id) {
          const { data: props } = await supabase
            .from('properties')
            .select('id, name, address, customer_id')
            .eq('customer_id', proposal.customer_id)
            .order('name')

          if (props) {
            setProperties(props)
          }
        }

        // Load line items and contract services from proposal
        if (proposal.proposal_line_items && proposal.proposal_line_items.length > 0) {
          setProposalLineItems(proposal.proposal_line_items)

          const buildingUpkeepItem = findBuildingUpkeepItem(serviceItemsGrouped)
          const exteriorMaintenanceItem = findExteriorMaintenanceItem(serviceItemsGrouped)
          const { services, lineItems: importedLineItems, dsnyUpkeep } =
            mapProposalLineItemsToServicesAndLineItems(
              proposal.proposal_line_items,
              proposal,
              {
                serviceItems,
                isDsnyServiceType,
                buildingUpkeepItemId: buildingUpkeepItem?.id,
                exteriorMaintenanceItemId: exteriorMaintenanceItem?.id,
              }
            )

          // Prefer Exterior Maintenance package kind when the proposal billed that item.
          const proposalPackageLine = proposal.proposal_line_items.find(
            (li: any) =>
              (exteriorMaintenanceItem && li.service_item_id === exteriorMaintenanceItem.id) ||
              (buildingUpkeepItem && li.service_item_id === buildingUpkeepItem.id)
          )
          if (proposalPackageLine) {
            setDsnyPackageKind(
              detectDsnyPackageKind(
                { service_item_id: proposalPackageLine.service_item_id },
                serviceItemsGrouped
              )
            )
          }

          if (importedLineItems.length > 0) {
            setLineItems(importedLineItems)
          }
          if (services.length > 0) {
            setContractServices(services)
          }
          // Keep the proposal's DSNY package price so the contract doesn't
          // reset to the catalog base price
          if (dsnyUpkeep) {
            setDsnyProposalAmount(dsnyUpkeep.amount)
          }
        } else {
          setProposalLineItems([])
        }
      }
    } catch (error) {
      console.error('Error loading proposal data:', error)
      alert('Error loading proposal data: ' + (error as any).message)
    }
  }

  const loadContractForRenewal = async (contractId: string) => {
    try {
      const { data: contract, error } = await supabase
        .from('contracts')
        .select(
          `
          *,
          contract_services(*),
          contract_line_items(*)
        `
        )
        .eq('id', contractId)
        .single()

      if (error) throw error

      if (contract) {
        // Calculate new dates: start = old end + 1 day, end = start + 1 year (Dec 31)
        const oldEndDate = contract.end_date ? new Date(contract.end_date) : new Date()
        const newStartDate = new Date(oldEndDate)
        newStartDate.setDate(newStartDate.getDate() + 1)

        const newEndDate = new Date(newStartDate)
        newEndDate.setFullYear(newStartDate.getFullYear() + 1)
        newEndDate.setMonth(11) // December
        newEndDate.setDate(31) // 31st

        // Determine original_contract_id: use parent's original_contract_id if it exists, otherwise use parent's id
        const originalContractId = (contract as any).original_contract_id || contract.id

        // Pre-populate form with contract data
        setFormData((prev) => ({
          ...prev,
          title: `${contract.title} (Renewal)`,
          description: contract.description || '',
          template_type: contract.template_type || '',
          service_type: contract.service_type || '',
          contract_type: contract.contract_type || 'recurring',
          customer_id: contract.customer_id || '',
          property_id: contract.property_id || '',
          proposal_id: contract.proposal_id || '',
          total_amount: contract.total_amount || 0,
          contract_total_amount: contract.contract_total_amount || contract.total_amount || 0,
          billing_frequency: contract.billing_frequency || 'monthly',
          billing_day_of_month: Number((contract as any).billing_day_of_month) || 1,
          billing_method: (contract.billing_method as any) || 'per_period',
          is_recurring: contract.is_recurring || false,
          recurrence_type: contract.recurrence_type || 'weekly',
          recurrence_interval: contract.recurrence_interval || 1,
          recurrence_days: contract.recurrence_days || [],
          recurrence_end_date: contract.recurrence_end_date || '',
          recurrence_end_count: contract.recurrence_end_count || 0,
          start_date: newStartDate.toISOString().split('T')[0],
          end_date: newEndDate.toISOString().split('T')[0],
          payment_terms: contract.payment_terms || 'Net 30',
          late_fee_percentage: contract.late_fee_percentage || 0,
          sales_tax_status: (contract.sales_tax_status as any) || '',
          cancellation_terms: contract.cancellation_terms || '',
          notes: contract.notes || '',
          master_weekly_schedule: contract.master_weekly_schedule || [],
          garbage_schedule: contract.garbage_schedule || [],
          recycling_schedule: contract.recycling_schedule || [],
          bulk_schedule: contract.bulk_schedule || [],
          organics_schedule: contract.organics_schedule || [],
          interior_cleaning_schedule: contract.interior_cleaning_schedule || [],
          dsny_integration: contract.dsny_integration || false,
          dsny_pickup_days: contract.dsny_pickup_days || [],
          dsny_collection_types: contract.dsny_collection_types || [],
        }))

        // Load line items
        if (contract.contract_line_items && contract.contract_line_items.length > 0) {
          const mappedLineItems = contract.contract_line_items.map((item: any, index: number) => ({
            id: `line_item_${Date.now()}_${index}`,
            service_item_id: item.service_item_id || undefined,
            description: item.description || '',
            quantity: item.quantity || 1,
            is_variable: item.is_variable || false,
            unit_type: item.unit_type || 'flat_rate',
            unit_price: item.unit_price || 0,
            total_price: item.total_price || 0,
            sort_order: item.sort_order || index,
          }))
          setLineItems(mappedLineItems)
        }

        // Load services
        if (contract.contract_services && contract.contract_services.length > 0) {
          const mappedServices = contract.contract_services.map((service: any) => ({
            id: `service_${Date.now()}_${Math.random()}`,
            service_item_id: service.service_item_id || '',
            service_type: service.service_type || '',
            service_name: service.service_name || '',
            amount: service.amount || 0,
            description: service.description || '',
            scheduled_date: service.scheduled_date || null,
            scheduled_time: service.scheduled_time || null,
            is_maintenance: service.is_maintenance || false,
            schedule: {
              recurrence_type: service.recurrence_type || null,
              recurrence_days: service.recurrence_days || [],
              dsny_integration: service.dsny_integration || false,
              garbage_schedule: service.garbage_schedule || [],
              recycling_schedule: service.recycling_schedule || [],
              organics_schedule: service.organics_schedule || [],
              bulk_schedule: service.bulk_schedule || [],
              interior_cleaning_schedule: service.interior_cleaning_schedule || [],
            },
          }))
          setContractServices(mappedServices)
        }

        // Fetch properties if customer_id is set
        if (contract.customer_id) {
          const { data: props } = await supabase
            .from('properties')
            .select('id, name, address, customer_id')
            .eq('customer_id', contract.customer_id)
            .order('name')

          if (props) {
            setProperties(props)
          }
        }

        // Store parent contract ID and original contract ID for linking
        setFormData((prev) => ({
          ...prev,
          parent_contract_id: contractId,
          original_contract_id: originalContractId,
        }))
      }
    } catch (error) {
      console.error('Error loading contract for renewal:', error)
      alert('Error loading contract data: ' + (error as any).message)
    }
  }

  const fetchServiceItems = async () => {
    const items = await fetchServiceItemsGrouped()
    setServiceItemsGrouped(items)
    setServiceItems(
      items.flatMap((g) =>
        g.items.map((item) => ({
          ...item,
          categories: g.category,
        }))
      ) as any
    )
  }

  // Billing totals are derived from line items automatically. Line items are the single
  // source (services already sync into them), unless the user explicitly overrides.
  useEffect(() => {
    if (billingAmountOverride) return
    const lineItemsTotal = lineItems.reduce((sum, item) => sum + item.total_price, 0)
    if (lineItemsTotal > 0) {
      setFormData((prev) =>
        prev.contract_total_amount === lineItemsTotal
          ? prev
          : { ...prev, contract_total_amount: lineItemsTotal }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItems, billingAmountOverride])

  // Automatically calculate amount per invoice when billing method is "divide_total"
  useEffect(() => {
    if (
      formData.billing_method === 'divide_total' &&
      formData.billing_frequency !== 'one_time' &&
      formData.billing_frequency !== 'after_each_service'
    ) {
      const periods = calculateBillingPeriods()
      if (periods > 0 && formData.contract_total_amount > 0) {
        const nextTotal = formData.contract_total_amount / periods
        setFormData((prev) =>
          prev.total_amount === nextTotal ? prev : { ...prev, total_amount: nextTotal }
        )
      } else if (formData.contract_total_amount === 0) {
        setFormData((prev) => (prev.total_amount === 0 ? prev : { ...prev, total_amount: 0 }))
      }
    } else if (formData.billing_method === 'per_period' && !billingAmountOverride) {
      setFormData((prev) =>
        prev.total_amount === prev.contract_total_amount
          ? prev
          : { ...prev, total_amount: prev.contract_total_amount }
      )
    }
    // Note: 'actual_usage' method doesn't need total_amount calculation
  }, [
    formData.billing_method,
    formData.billing_frequency,
    formData.contract_total_amount,
    formData.start_date,
    formData.end_date,
    billingAmountOverride,
  ])

  // Line item management functions
  const handleLineItemChange = (index: number, field: string, value: string | number | boolean) => {
    const newLineItems = [...lineItems]
    newLineItems[index] = { ...newLineItems[index], [field]: value }

    // Recalculate total if quantity or unit_price changed (only if not variable)
    if ((field === 'quantity' || field === 'unit_price') && !newLineItems[index].is_variable) {
      newLineItems[index].total_price =
        Number(newLineItems[index].quantity) * Number(newLineItems[index].unit_price)
    } else if (field === 'is_variable') {
      // When toggling variable, reset total_price if variable, recalculate if not
      if (value) {
        newLineItems[index].total_price = 0
        newLineItems[index].quantity = 0
      } else {
        newLineItems[index].total_price =
          Number(newLineItems[index].quantity) * Number(newLineItems[index].unit_price)
      }
    }

    setLineItems(newLineItems)
  }

  const handleServiceItemSelect = (index: number, serviceItemId: string) => {
    if (!serviceItemId) {
      // Clear service item link
      const newLineItems = [...lineItems]
      newLineItems[index] = {
        ...newLineItems[index],
        service_item_id: undefined,
      }
      setLineItems(newLineItems)
      return
    }

    // Find the selected service item
    let selectedItem: { name: string; unit_type: string; base_price: number } | null = null
    for (const group of serviceItemsGrouped) {
      const item = group.items.find((i) => i.id === serviceItemId)
      if (item) {
        selectedItem = item
        break
      }
    }

    if (!selectedItem) {
      return
    }

    // Capture values to avoid TypeScript null checks in callbacks
    const itemName = selectedItem.name
    const itemBasePrice = selectedItem.base_price
    const normalizedUnitType = normalizeUnitType(selectedItem.unit_type)
    const newLineItems = [...lineItems]
    const currentQuantity = Number(newLineItems[index].quantity)

    // Check if there's a matching service with the same service_item_id
    const matchingService = contractServices.find(
      (service) => service.service_item_id === serviceItemId
    )

    if (matchingService) {
      // Show dialog asking if user wants to link
      setLinkDialog({
        show: true,
        type: 'line-item-to-service',
        lineItem: {
          ...newLineItems[index],
          service_item_id: serviceItemId,
          description: itemName,
          unit_type: normalizedUnitType,
          unit_price: itemBasePrice,
          total_price: currentQuantity * itemBasePrice,
        },
        matchingService: matchingService,
        onConfirm: () => {
          // Link the line item to the service
          const updatedLineItems = [...lineItems]
          updatedLineItems[index] = {
            ...updatedLineItems[index],
            service_item_id: serviceItemId,
            description: itemName,
            unit_type: normalizedUnitType,
            unit_price: itemBasePrice,
            total_price: currentQuantity * itemBasePrice,
            linked_service_id: matchingService.id, // Link to service
          }
          setLineItems(updatedLineItems)
          setLinkDialog(null)
        },
        onCancel: () => {
          // Still update the line item but don't link
          const updatedLineItems = [...lineItems]
          updatedLineItems[index] = {
            ...updatedLineItems[index],
            service_item_id: serviceItemId,
            description: itemName,
            unit_type: normalizedUnitType,
            unit_price: itemBasePrice,
            total_price: currentQuantity * itemBasePrice,
          }
          setLineItems(updatedLineItems)
          setLinkDialog(null)
        },
      })
    } else {
      // No matching service, just update the line item
      newLineItems[index] = {
        ...newLineItems[index],
        service_item_id: serviceItemId,
        description: itemName,
        unit_type: normalizedUnitType,
        unit_price: itemBasePrice,
        total_price: currentQuantity * itemBasePrice,
      }
      setLineItems(newLineItems)
    }
  }

  const addLineItem = () => {
    const newItem: ContractLineItem = {
      id: `line_item_${Date.now()}`,
      service_item_id: undefined,
      description: '',
      quantity: 1,
      is_variable: false,
      unit_type: 'flat_rate',
      unit_price: 0,
      total_price: 0,
      sort_order: lineItems.length,
    }
    setLineItems([...lineItems, newItem])
  }

  const removeLineItem = (index: number) => {
    const newLineItems = lineItems.filter((_, i) => i !== index)
    // Recalculate sort_order for remaining items
    const updatedLineItems = newLineItems.map((item, idx) => ({
      ...item,
      sort_order: idx,
    }))
    setLineItems(updatedLineItems)
  }

  const handlePricingModeChange = (mode: PricingMode) => {
    setFormData((prev) => ({
      ...prev,
      document_data: { ...prev.document_data, pricingMode: mode },
      billing_method:
        mode === 'rate_card' && (!prev.billing_method || prev.billing_method === 'per_period')
          ? 'actual_usage'
          : prev.billing_method,
    }))
  }

  const handleSeedRateCard = () => {
    if (!hasRateCardSeed(formData.service_type)) return
    if (
      lineItems.length > 0 &&
      !window.confirm('Replace existing line items with rate card tiers?')
    ) {
      return
    }
    setLineItems(buildRateCardLineItems(formData.service_type) as ContractLineItem[])
  }

  // Open service selection modal
  const openServiceSelectionModal = () => {
    if (contractServices.length === 0 && dsnyScheduleRows.length === 0) {
      setNotification({
        type: 'error',
        message: 'No services to import. Please add services in the Services & Schedule tab first.',
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }
    setSelectedServicesForImport(new Set())
    setShowServiceSelectionModal(true)
  }

  // Import selected services as line items
  const importSelectedServicesAsLineItems = () => {
    if (selectedServicesForImport.size === 0) {
      setNotification({
        type: 'error',
        message: 'Please select at least one service to import.',
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    const newLineItems: ContractLineItem[] = []
    let sortOrder = lineItems.length
    let skippedExisting = 0

    // Import from contractServices (one-time and other recurring services)
    contractServices.forEach((service) => {
      if (selectedServicesForImport.has(service.id)) {
        // Skip services that already have a linked line item (avoids duplicates)
        if (lineItems.some((li) => li.linked_service_id === service.id)) {
          skippedExisting++
          return
        }
        // Check if there's a matching proposal line item with the same service_item_id
        const matchingProposalItem = proposalLineItems.find(
          (propItem) =>
            propItem.service_item_id === service.service_item_id && service.service_item_id
        )

        if (matchingProposalItem) {
          console.log('✅ Linked service to proposal line item:', {
            serviceId: service.id,
            serviceName: service.service_name,
            proposalLineItemId: matchingProposalItem.id,
            proposalDescription: matchingProposalItem.description,
            serviceItemId: service.service_item_id,
            message:
              'This service item matches a proposal line item and will be linked for reporting',
          })
        } else if (service.service_item_id) {
          console.log('ℹ️ Service item not found in proposal:', {
            serviceName: service.service_name,
            serviceItemId: service.service_item_id,
            message: 'No matching proposal line item found with this service_item_id',
          })
        }

        newLineItems.push({
          id: `imported_${service.id}`,
          service_item_id: service.service_item_id || undefined,
          proposal_line_item_id: matchingProposalItem?.id || undefined, // Link to proposal line item if match found
          description: service.service_name || service.description || '',
          quantity: 1,
          is_variable: false,
          unit_type: 'flat_rate',
          unit_price: service.amount || 0,
          total_price: service.amount || 0,
          sort_order: sortOrder++,
          linked_service_id: service.id,
        })
      }
    })

    // For DSNY maintenance, create a single "Building Upkeep" line item
    // (skipped when the auto-sync or proposal import already created one)
    const dsnyLineItemExists = lineItems.some((li) => li.linked_service_id === 'dsny_package')
    if (dsnyLineItemExists && selectedServicesForImport.has('dsny_package')) {
      skippedExisting++
    }
    if (
      recurringScheduleType === 'maintenance_dsny' &&
      selectedServicesForImport.has('dsny_package') &&
      !dsnyLineItemExists
    ) {
      const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
      const defaultPackageName =
        dsnyPackageKind === 'exterior_maintenance' ? 'Exterior Maintenance' : 'Building Upkeep'

      if (!packageItem) {
        console.warn(
          `${defaultPackageName} service item not found in catalog. Available items:`,
          serviceItemsGrouped.flatMap((g) => g.items.map((i) => i.name))
        )
      }

      // Count total days across all DSNY rows
      const totalDays = dsnyScheduleRows.reduce(
        (sum, row) => sum + normalizeRowDays(row.days).length,
        0
      )

      const rawUnitType = packageItem?.unit_type || 'flat_rate'
      const unitType = normalizeUnitType(rawUnitType) // Normalize to match dropdown options
      // Proposal price wins over the catalog base price
      const unitPrice = dsnyProposalAmount ?? packageItem?.base_price ?? 0

      const matchingProposalItem = proposalLineItems.find(
        (propItem) => propItem.service_item_id === packageItem?.id && packageItem?.id
      )

      newLineItems.push({
        id: `dsny_package_${Date.now()}`,
        service_item_id: packageItem?.id || undefined,
        proposal_line_item_id: matchingProposalItem?.id || undefined,
        description: packageItem?.name || defaultPackageName,
        quantity: totalDays > 0 ? 1 : 0,
        is_variable: totalDays === 0,
        unit_type: unitType,
        unit_price: unitPrice,
        total_price: unitPrice * (totalDays > 0 ? 1 : 0),
        sort_order: sortOrder++,
        linked_service_id: 'dsny_package',
      })
    }

    if (newLineItems.length > 0) {
      setLineItems([...lineItems, ...newLineItems])
      setShowServiceSelectionModal(false)
      setSelectedServicesForImport(new Set())
      setNotification({
        type: 'success',
        message:
          `Added ${newLineItems.length} service(s) as line items.` +
          (skippedExisting > 0
            ? ` Skipped ${skippedExisting} already on the list.`
            : ''),
      })
      setTimeout(() => setNotification(null), 3000)
    } else if (skippedExisting > 0) {
      setShowServiceSelectionModal(false)
      setSelectedServicesForImport(new Set())
      setNotification({
        type: 'success',
        message: 'Those services are already on the line item list.',
      })
      setTimeout(() => setNotification(null), 3000)
    }
  }

  const toggleServiceSelection = (serviceId: string) => {
    setSelectedServicesForImport((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId)
      } else {
        newSet.add(serviceId)
      }
      return newSet
    })
  }

  // Calculate billing amounts based on method and duration
  const calculateBillingAmounts = () => {
    // For variable items, use unit_price (represents the amount per billing period)
    // For fixed items, use total_price
    const lineItemsTotal = lineItems.reduce((sum, item) => {
      if (item.is_variable) {
        return sum + (item.unit_price || 0)
      }
      return sum + (item.total_price || 0)
    }, 0)

    // Count only items with quantity > 0 (fixed price items)
    const fixedItems = lineItems.filter((item) => item.quantity > 0 && !item.is_variable)
    const variableItems = lineItems.filter((item) => item.is_variable)

    return {
      lineItemsTotal,
      fixedTotal: fixedItems.reduce((sum, item) => sum + (item.total_price || 0), 0),
      variableTotal: variableItems.reduce((sum, item) => sum + (item.unit_price || 0), 0),
      variableCount: variableItems.length,
      hasVariableItems: variableItems.length > 0,
    }
  }

  // Calculate number of billing periods between start and end date
  const calculateBillingPeriods = () => {
    if (!formData.start_date || !formData.end_date) return 0

    // Parse dates - split the date string to avoid timezone issues
    const parseLocalDate = (dateStr: string) => {
      const [year, month, day] = dateStr.split('-').map(Number)
      return new Date(year, month - 1, day) // month is 0-indexed in Date constructor
    }

    const start = parseLocalDate(formData.start_date)
    const end = parseLocalDate(formData.end_date)

    // For weekly and bi-weekly, use day-based calculation
    const diffTime = end.getTime() - start.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1 // +1 to include both start and end days

    switch (formData.billing_frequency) {
      case 'weekly':
        return Math.ceil(diffDays / 7)
      case 'bi_weekly':
        return Math.ceil(diffDays / 14)
      case 'monthly': {
        // Calculate actual months between dates
        const startYear = start.getFullYear()
        const startMonth = start.getMonth() // 0-11
        const endYear = end.getFullYear()
        const endMonth = end.getMonth() // 0-11

        // Calculate the number of months between dates
        // Example: Jan (0) to Dec (11) = 11 months difference
        // But we need to count billing periods: Jan, Feb, ..., Dec = 12 periods
        const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth)

        // Return the number of months (inclusive of both start and end)
        // For Jan 1 to Dec 31: monthsDiff = 11, so periods = 12 ✓
        return monthsDiff + 1
      }
      case 'quarterly': {
        // Calculate actual months and divide by 3
        const startYear = start.getFullYear()
        const startMonth = start.getMonth()
        const endYear = end.getFullYear()
        const endMonth = end.getMonth()
        const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth)
        // Count quarters: divide months by 3 and round up
        return Math.ceil((monthsDiff + 1) / 3)
      }
      case 'annually': {
        // Calculate actual years between dates
        const startYear = start.getFullYear()
        const endYear = end.getFullYear()
        const yearsDiff = endYear - startYear
        // Add 1 to include both start and end years
        return yearsDiff + 1
      }
      case 'one_time':
        return 1
      default:
        return 0
    }
  }

  // Auto-generate contract title from property address and template type
  useEffect(() => {
    if (formData.property_id && formData.template_type) {
      const selectedProperty = properties.find((p) => p.id === formData.property_id)
      if (selectedProperty?.address) {
        const streetAddress = selectedProperty.address.split(',')[0].trim()
        // Capitalize first letter of each word in template type
        const templateType = formData.template_type
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')

        setFormData((prev) => ({
          ...prev,
          title: `${streetAddress} - ${templateType}`,
        }))
      }
    } else if (!formData.property_id || !formData.template_type) {
      // Clear title if property or template type is not selected
      setFormData((prev) => ({
        ...prev,
        title: '',
      }))
    }
  }, [formData.property_id, formData.template_type, properties])

  useEffect(() => {
    let cancelled = false
    const loadPropertyAssignmentLabels = async () => {
      if (!formData.property_id) {
        setPropertyAssignmentLabels(null)
        return
      }
      const { data: prop } = await supabase
        .from('properties')
        .select('default_assigned_crew_id, default_assigned_employee_id')
        .eq('id', formData.property_id)
        .maybeSingle()
      if (cancelled || !prop) {
        if (!cancelled) setPropertyAssignmentLabels({ crewName: null, employeeName: null })
        return
      }
      const [crewRes, empRes] = await Promise.all([
        prop.default_assigned_crew_id
          ? supabase.from('crews').select('name').eq('id', prop.default_assigned_crew_id).maybeSingle()
          : Promise.resolve({ data: null as { name: string } | null }),
        prop.default_assigned_employee_id
          ? supabase
              .from('employees')
              .select('first_name, last_name')
              .eq('id', prop.default_assigned_employee_id)
              .maybeSingle()
          : Promise.resolve({ data: null as { first_name: string; last_name: string } | null }),
      ])
      if (cancelled) return
      setPropertyAssignmentLabels({
        crewName: crewRes.data?.name || null,
        employeeName: empRes.data
          ? `${empRes.data.first_name || ''} ${empRes.data.last_name || ''}`.trim()
          : null,
      })
    }
    loadPropertyAssignmentLabels()
    return () => {
      cancelled = true
    }
  }, [formData.property_id])

  useEffect(() => {
    if (formData.customer_id) {
      // Filter proposals by customer_id (only show proposals linked to customers, not leads)
      let customerProposals = proposals.filter((p) => p.customer_id === formData.customer_id)

      // If property is selected, filter by property_id (but also include proposals without property_id for backwards compatibility)
      if (formData.property_id) {
        customerProposals = customerProposals.filter(
          (p) => !p.property_id || p.property_id === formData.property_id
        )
      }

      // Always include the proposal that matches formData.proposal_id (if set)
      // This ensures the proposal from URL parameter is always visible in the dropdown
      if (formData.proposal_id) {
        const selectedProposal = proposals.find((p) => p.id === formData.proposal_id)
        if (selectedProposal && !customerProposals.some((p) => p.id === formData.proposal_id)) {
          customerProposals = [selectedProposal, ...customerProposals]
        }
      }

      setFilteredProposals(customerProposals)
    } else {
      // If no customer_id, but proposal_id is set, still show that proposal
      if (formData.proposal_id) {
        const selectedProposal = proposals.find((p) => p.id === formData.proposal_id)
        if (selectedProposal) {
          setFilteredProposals([selectedProposal])
        } else {
          setFilteredProposals([])
        }
      } else {
        setFilteredProposals([])
      }
    }
  }, [formData.customer_id, formData.property_id, formData.proposal_id, proposals])

  // Initialize newService state when modal opens for recurring contracts
  useEffect(() => {
    if (showAddServiceModal && !editingServiceId && formData.contract_type === 'recurring') {
      setNewService((prev) => {
        // Only initialize if it's still set to 'none' (default initial state)
        if (prev.recurrence_type === 'none') {
          return {
            ...prev,
            recurrence_type: 'weekly',
          }
        }
        return prev
      })
    }
  }, [showAddServiceModal, editingServiceId, formData.contract_type])

  // Remove this useEffect - it was incorrectly overwriting the properties state

  const fetchData = async () => {
    try {
      // Fetch customers
      const { data: customersData } = await supabase
        .from('customers')
        .select('id, company_name, contact_first_name, contact_last_name')
        .order('company_name')

      // Fetch properties
      const { data: propertiesData } = await supabase
        .from('properties')
        .select('id, name, address, city, state, customer_id')
        .order('name')

      // Fetch proposals (approved, sent, viewed) - only those linked to customers (not just leads)
      const { data: proposalsData } = await supabase
        .from('proposals')
        .select('id, title, proposal_number, customer_id, property_id, total_amount, status')
        .in('status', ['approved', 'sent', 'viewed'])
        .not('customer_id', 'is', null) // Only proposals with customer_id (not just leads)
        .order('created_at', { ascending: false })

      // Fetch service categories
      const { data: serviceCategoriesData } = await supabase
        .from('service_categories')
        .select('id, name, operational_division_id')
        .order('name')

      // Fetch service items
      const { data: serviceItemsData } = await supabase
        .from('service_items')
        .select('*')
        .eq('is_active', true)
        .order('name')

      // Manually attach category info to service items
      const itemsWithCategories = (serviceItemsData || []).map((item) => {
        const category = serviceCategoriesData?.find((c) => c.id === item.service_category_id)
        return {
          ...item,
          categories: category,
        }
      })

      // Find the "Maintenance - DSNY" category ID - must match exactly
      const maintenanceDSNY = serviceCategoriesData?.find(
        (cat) => cat.name.toLowerCase() === 'maintenance - dsny'
      )
      if (maintenanceDSNY) {
        console.log('Found Maintenance - DSNY category with ID:', maintenanceDSNY.id)
        setMaintenanceDSNYCategoryId(maintenanceDSNY.id)
      } else {
        console.log('Maintenance - DSNY category not found')
        console.log(
          'Available categories:',
          serviceCategoriesData?.map((c) => c.name)
        )
      }

      setCustomers(customersData || [])
      setProperties(propertiesData || [])
      // Merge fetched proposals with any proposals already in state (e.g., from loadProposalData)
      setProposals((prevProposals) => {
        const fetchedProposalIds = new Set((proposalsData || []).map((p: any) => p.id))
        // Keep proposals from prevProposals that aren't in the fetched data
        const existingProposals = prevProposals.filter((p) => !fetchedProposalIds.has(p.id))
        // Combine: existing proposals first (to preserve order), then fetched proposals
        return [...existingProposals, ...(proposalsData || [])]
      })
      setServiceCategories(serviceCategoriesData || [])
      setServiceItems(itemsWithCategories)

      // Debug logging
      console.log('Fetched data:', {
        customers: customersData?.length || 0,
        properties: propertiesData?.length || 0,
        proposals: proposalsData?.length || 0,
        serviceCategories: serviceCategoriesData?.length || 0,
      })
      console.log('Service categories:', serviceCategoriesData)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handleProposalSelect = (proposalId: string) => {
    if (!proposalId) {
      setFormData({ ...formData, proposal_id: '' })
      return
    }
    loadProposalData(proposalId)
  }

  const isMaintenanceService = () => {
    if (!formData.service_type) {
      console.log('ContractCreate - No service type selected')
      return false
    }
    const selectedCategory = serviceCategories.find((cat) => cat.name === formData.service_type)
    console.log('ContractCreate - Selected service type:', formData.service_type)
    console.log('ContractCreate - Selected category:', selectedCategory)
    console.log('ContractCreate - Maintenance division ID:', 'aad2c279-31a9-4f3d-bc5e-195f218b38d7')
    console.log(
      'ContractCreate - Is maintenance service:',
      selectedCategory?.operational_division_id === 'aad2c279-31a9-4f3d-bc5e-195f218b38d7'
    )
    return selectedCategory?.operational_division_id === 'aad2c279-31a9-4f3d-bc5e-195f218b38d7' // Maintenance division ID
  }

  // Manual Schedule Management Functions
  const addManualSchedule = () => {
    const newSchedule = {
      id: `manual_${Date.now()}`,
      name: '',
      description: '',
      color: 'bg-gray-100 border-gray-300 text-gray-800',
      days: [] as number[],
    }
    setFormData((prev) => ({
      ...prev,
      manual_schedules: [...prev.manual_schedules, newSchedule],
    }))
  }

  const updateManualSchedule = (id: string, field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      manual_schedules: prev.manual_schedules.map((schedule) =>
        schedule.id === id ? { ...schedule, [field]: value } : schedule
      ),
    }))
  }

  const removeManualSchedule = (id: string) => {
    setFormData((prev) => ({
      ...prev,
      manual_schedules: prev.manual_schedules.filter((schedule) => schedule.id !== id),
    }))
  }

  const updateManualScheduleDays = (id: string, day: number, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      manual_schedules: prev.manual_schedules.map((schedule) =>
        schedule.id === id
          ? {
              ...schedule,
              days: checked ? [...schedule.days, day] : schedule.days.filter((d) => d !== day),
            }
          : schedule
      ),
    }))
  }

  const fetchDSNYPickupSchedule = async (address: string) => {
    const result = await getDSNYPickupDays(address)
    if (!result) {
      throw new Error(
        'Could not fetch DSNY schedule. Please verify the property address is a valid NYC address and try again.'
      )
    }
    return result
  }

  const handleDSNYFetch = async () => {
    if (!formData.property_id) {
      alert('Please select a property first')
      return
    }

    const selectedProperty = properties.find((p) => p.id === formData.property_id)
    if (!selectedProperty?.address) {
      alert('Selected property does not have an address')
      return
    }

    try {
      const dsnyData = await fetchDSNYPickupSchedule(selectedProperty.address)
      if (dsnyData) {
        // Update form data with DSNY schedule
        const garbageDays = dsnyData.schedules?.garbage || []
        const recyclingDays = dsnyData.schedules?.recycling || []
        const organicsDays = dsnyData.schedules?.organics || []
        const bulkDays = dsnyData.schedules?.bulk || []
        const dsnyPickupDayNames = pickupSchedulesToPickupDayNames(dsnyData.pickupSchedules)

        // Combine all DSNY service days (evening before pickup)
        const allDSNYDays = Array.from(
          new Set([...garbageDays, ...recyclingDays, ...organicsDays, ...bulkDays])
        )

        setFormData((prev) => ({
          ...prev,
          dsny_integration: true,
          garbage_schedule: garbageDays,
          recycling_schedule: recyclingDays,
          organics_schedule: organicsDays,
          bulk_schedule: bulkDays,
          master_weekly_schedule: allDSNYDays,
          recurrence_days: allDSNYDays,
          recurrence_type: 'custom',
          dsny_pickup_days: dsnyPickupDayNames,
          dsny_collection_types:
            prev.dsny_collection_types.length > 0
              ? prev.dsny_collection_types
              : ['garbage', 'recycling'],
        }))

        alert(
          `DSNY schedule loaded for ${selectedProperty.address}!\nZone: ${dsnyData.zone}\nService days set to the evening before each DSNY pickup.`
        )
      }
    } catch (error) {
      console.error('Error fetching DSNY schedule:', error)
      alert('Error fetching DSNY schedule')
    }
  }

  const handleEditService = (serviceId: string) => {
    const serviceToEdit = contractServices.find((s) => s.id === serviceId)
    if (serviceToEdit) {
      setNewService({
        service_type: serviceToEdit.service_item_id || '',
        service_name: serviceToEdit.service_name || '',
        amount: serviceToEdit.amount || 0,
        description: serviceToEdit.description || serviceToEdit.service_name || '',
        scheduled_date: serviceToEdit.scheduled_date || '',
        scheduled_time: serviceToEdit.scheduled_time || '',
        recurrence_type:
          serviceToEdit.schedule?.recurrence_type ||
          (formData.contract_type === 'one_time' ? 'none' : 'weekly'),
        recurrence_days: serviceToEdit.schedule?.recurrence_days || [],
        recurrence_day_of_month: (serviceToEdit.schedule as any)?.recurrence_day_of_month || null,
        recurrence_weekday: (serviceToEdit.schedule as any)?.recurrence_weekday || null,
        recurrence_occurrence: (serviceToEdit.schedule as any)?.recurrence_occurrence || null,
        recurrence_month: (serviceToEdit.schedule as any)?.recurrence_month || null,
        recurrence_interval_months:
          (serviceToEdit.schedule as any)?.recurrence_interval_months || 1,
        recurrence_interval_weeks: (serviceToEdit.schedule as any)?.recurrence_interval_weeks || 1,
        recurrence_interval_days: (serviceToEdit.schedule as any)?.recurrence_interval_days || 1,
        dsny_integration: serviceToEdit.schedule?.dsny_integration || false,
        garbage_schedule: serviceToEdit.schedule?.garbage_schedule || [],
        recycling_schedule: serviceToEdit.schedule?.recycling_schedule || [],
        organics_schedule: serviceToEdit.schedule?.organics_schedule || [],
        bulk_schedule: serviceToEdit.schedule?.bulk_schedule || [],
        interior_cleaning_schedule: serviceToEdit.schedule?.interior_cleaning_schedule || [],
      })
      setEditingServiceId(serviceId)
      setShowAddServiceModal(true)
    }
  }

  /** True when a recurring service still needs cadence (type and/or days) before crew visits can be derived. */
  const serviceNeedsRecurringSchedule = (service: ContractServiceRow): boolean => {
    if (formData.contract_type !== 'recurring') return false
    const rtype = service.schedule?.recurrence_type
    if (!rtype || rtype === 'none' || rtype === 'one_time') return true
    if (rtype === 'weekly' || rtype === 'bi_weekly') {
      return !(service.schedule?.recurrence_days && service.schedule.recurrence_days.length > 0)
    }
    if (rtype === 'monthly') {
      const hasDom = service.schedule?.recurrence_day_of_month != null
      const hasNth =
        service.schedule?.recurrence_weekday != null &&
        service.schedule?.recurrence_occurrence != null
      return !hasDom && !hasNth
    }
    return false
  }

  const openOtherRecurringSchedule = () => {
    setRecurringScheduleType('other')
    // Proposal-imported services often have a name/price but no days — open the first
    // incomplete one so schedule setup is part of this path, not a separate Edit click.
    const firstIncomplete = contractServices.find((s) => serviceNeedsRecurringSchedule(s))
    if (firstIncomplete) {
      window.setTimeout(() => handleEditService(firstIncomplete.id), 0)
    }
  }

  const handleRemoveService = (serviceId: string) => {
    setContractServices(contractServices.filter((s) => s.id !== serviceId))
    setLineItems(removeLinkedLineItem(lineItems, serviceId))
  }

  const handleAddService = () => {
    if (!newService.service_type || !newService.service_name) {
      alert('Please select a service item')
      return
    }

    // For recurring contracts, require a recurrence type
    if (
      formData.contract_type === 'recurring' &&
      (newService.recurrence_type === 'none' || !newService.recurrence_type)
    ) {
      alert('Please select a recurrence type for the service')
      return
    }

    // Recurring "Other" path: require days/cadence so schedule setup is complete in-flow
    if (
      formData.contract_type === 'recurring' &&
      recurringScheduleType === 'other' &&
      newService.recurrence_type !== 'none'
    ) {
      const rtype = newService.recurrence_type
      if (
        (rtype === 'weekly' || rtype === 'bi_weekly') &&
        (!newService.recurrence_days || newService.recurrence_days.length === 0)
      ) {
        alert('Please select at least one day for the recurring schedule')
        return
      }
      if (rtype === 'monthly') {
        const hasDom = newService.recurrence_day_of_month != null
        const hasNth =
          newService.recurrence_weekday != null && newService.recurrence_occurrence != null
        if (!hasDom && !hasNth) {
          alert('Please set a monthly schedule (day of month or weekday occurrence)')
          return
        }
      }
    }

    // If editing, update existing service; otherwise add new
    if (editingServiceId) {
      const updatedServices = contractServices.map((s) => {
        if (s.id === editingServiceId) {
          // Find the selected service item to get its category ID
          const selectedItem = serviceItems.find((item) => item.id === newService.service_type)
          const selectedCategory = serviceCategories.find(
            (c) => c.id === selectedItem?.service_category_id
          )
          const isMaintenance = selectedCategory?.name?.toLowerCase().includes('dsny') || false
          const shouldEnableDSNY =
            recurringScheduleType === 'maintenance_dsny' ||
            (newService.dsny_integration && isMaintenance)

          return {
            ...s,
            service_item_id: newService.service_type,
            service_type: selectedItem?.service_category_id || '',
            service_name: newService.service_name,
            amount: newService.amount || 0,
            description: newService.description || newService.service_name,
            scheduled_date:
              formData.contract_type === 'one_time' ? newService.scheduled_date : null,
            scheduled_time:
              formData.contract_type === 'one_time' ? newService.scheduled_time : null,
            is_maintenance: isMaintenance || recurringScheduleType === 'maintenance_dsny',
            schedule: {
              recurrence_type:
                formData.contract_type === 'one_time'
                  ? null
                  : newService.recurrence_type === 'none'
                    ? null
                    : newService.recurrence_type,
              recurrence_days:
                formData.contract_type === 'one_time'
                  ? []
                  : newService.recurrence_type === 'none'
                    ? []
                    : newService.recurrence_days,
              recurrence_day_of_month: newService.recurrence_day_of_month,
              recurrence_weekday: newService.recurrence_weekday,
              recurrence_occurrence: newService.recurrence_occurrence,
              recurrence_month: newService.recurrence_month,
              recurrence_interval_months: newService.recurrence_interval_months,
              recurrence_interval_weeks: newService.recurrence_interval_weeks,
              recurrence_interval_days: newService.recurrence_interval_days,
              dsny_integration: shouldEnableDSNY,
              garbage_schedule: newService.garbage_schedule || [],
              recycling_schedule: newService.recycling_schedule || [],
              organics_schedule: newService.organics_schedule || [],
              bulk_schedule: newService.bulk_schedule || [],
              interior_cleaning_schedule: newService.interior_cleaning_schedule || [],
            },
          }
        }
        return s
      })
      setContractServices(updatedServices)
      const updatedService = updatedServices.find((s) => s.id === editingServiceId)
      if (updatedService && updatedService.id !== DSNY_PACKAGE_SERVICE_ID) {
        setLineItems((prev) => upsertLinkedLineItem(prev, updatedService))
      }

      // Continue Other recurring flow: open next service that still needs a schedule
      const nextIncomplete =
        recurringScheduleType === 'other'
          ? updatedServices.find(
              (s) => s.id !== editingServiceId && serviceNeedsRecurringSchedule(s)
            )
          : undefined

      const defaultRecurrenceType = formData.contract_type === 'one_time' ? 'none' : 'weekly'
      const isMaintenanceDSNY = recurringScheduleType === 'maintenance_dsny'
      setNewService({
        service_type: '',
        service_name: '',
        amount: 0,
        description: '',
        scheduled_date: '',
        scheduled_time: '',
        recurrence_type: defaultRecurrenceType,
        recurrence_days: [],
        recurrence_day_of_month: null,
        recurrence_weekday: null,
        recurrence_occurrence: null,
        recurrence_month: null,
        recurrence_interval_months: 1,
        recurrence_interval_weeks: 1,
        recurrence_interval_days: 1,
        dsny_integration: isMaintenanceDSNY,
        garbage_schedule: [],
        recycling_schedule: [],
        organics_schedule: [],
        bulk_schedule: [],
        interior_cleaning_schedule: [],
      })
      setEditingServiceId(null)
      setShowAddServiceModal(false)

      if (nextIncomplete) {
        window.setTimeout(() => handleEditService(nextIncomplete.id), 0)
      }
      return
    } else {
      // Find the selected service item to get its category ID
      const selectedItem = serviceItems.find((item) => item.id === newService.service_type)
      const selectedCategory = serviceCategories.find(
        (c) => c.id === selectedItem?.service_category_id
      )
      const isMaintenance = selectedCategory?.name?.toLowerCase().includes('dsny') || false

      // Auto-enable DSNY integration for Maintenance DSNY recurring schedule type
      const shouldEnableDSNY =
        recurringScheduleType === 'maintenance_dsny' ||
        (newService.dsny_integration && isMaintenance)

      const service = {
        id: `service_${Date.now()}`,
        service_item_id: newService.service_type, // Store the service item ID
        service_type: selectedItem?.service_category_id || '', // Store the category ID for database
        service_name: newService.service_name,
        amount: newService.amount || 0,
        description: newService.description || newService.service_name,
        scheduled_date: formData.contract_type === 'one_time' ? newService.scheduled_date : null,
        scheduled_time: formData.contract_type === 'one_time' ? newService.scheduled_time : null,
        is_maintenance: isMaintenance || recurringScheduleType === 'maintenance_dsny',
        schedule: {
          recurrence_type:
            formData.contract_type === 'one_time'
              ? null
              : newService.recurrence_type === 'none'
                ? null
                : newService.recurrence_type,
          recurrence_days:
            formData.contract_type === 'one_time'
              ? []
              : newService.recurrence_type === 'none'
                ? []
                : newService.recurrence_days,
          recurrence_day_of_month: newService.recurrence_day_of_month,
          recurrence_weekday: newService.recurrence_weekday,
          recurrence_occurrence: newService.recurrence_occurrence,
          recurrence_month: newService.recurrence_month,
          recurrence_interval_months: newService.recurrence_interval_months,
          recurrence_interval_days: newService.recurrence_interval_days,
          dsny_integration: shouldEnableDSNY,
          garbage_schedule: newService.garbage_schedule || [],
          recycling_schedule: newService.recycling_schedule || [],
          organics_schedule: newService.organics_schedule || [],
          bulk_schedule: newService.bulk_schedule || [],
          interior_cleaning_schedule: newService.interior_cleaning_schedule || [],
        },
      }

      // Check if there's a matching proposal line item with the same service_item_id
      const matchingProposalItem = proposalLineItems.find(
        (propItem) => propItem.service_item_id === newService.service_type
      )

      if (matchingProposalItem) {
        // Show dialog asking if user wants to link to proposal line item
        setLinkDialog({
          show: true,
          type: 'service-to-proposal',
          service: service,
          matchingProposalItem: matchingProposalItem,
          onConfirm: () => {
            const serviceWithLink = {
              ...service,
              proposal_line_item_id: matchingProposalItem.id,
            }
            setContractServices([...contractServices, serviceWithLink])
            setLineItems((prev) => upsertLinkedLineItem(prev, serviceWithLink))
            setShowAddServiceModal(false)
            setLinkDialog(null)
          },
          onCancel: () => {
            setContractServices([...contractServices, service])
            setLineItems((prev) => upsertLinkedLineItem(prev, service))
            setShowAddServiceModal(false)
            setLinkDialog(null)
          },
        })
        return
      } else {
        // No matching proposal line item, just add the service
        setContractServices([...contractServices, service])
        setLineItems((prev) => upsertLinkedLineItem(prev, service))
        void (async () => {
          const packageItemId = service.service_item_id
          if (!packageItemId) return
          const local = serviceItems.find((i) => i.id === packageItemId) as any
          let isPackage = !!local?.is_package
          if (local && local.is_package == null) {
            const { data } = await supabase
              .from('service_items')
              .select('is_package')
              .eq('id', packageItemId)
              .maybeSingle()
            isPackage = !!data?.is_package
          }
          if (!isPackage) return
          const children = await fetchPackageChildItems(packageItemId)
          if (children.length === 0) return
          const childServices = buildPackageChildServices(children, {
            packageServiceId: service.id,
            serviceType: service.service_type,
            recurrence: {
              recurrence_type: service.schedule?.recurrence_type,
              recurrence_days: service.schedule?.recurrence_days || [],
            },
          })
          setContractServices((prev) => [...prev, ...childServices])
        })()
      }
    }

    // Reset form based on contract type
    const defaultRecurrenceType = formData.contract_type === 'one_time' ? 'none' : 'weekly'
    const isMaintenanceDSNY = recurringScheduleType === 'maintenance_dsny'
    setNewService({
      service_type: '',
      service_name: '',
      amount: 0,
      description: '',
      scheduled_date: '',
      scheduled_time: '',
      recurrence_type: defaultRecurrenceType,
      recurrence_days: [],
      recurrence_day_of_month: null,
      recurrence_weekday: null,
      recurrence_occurrence: null,
      recurrence_month: null,
      recurrence_interval_months: 1,
      recurrence_interval_weeks: 1,
      recurrence_interval_days: 1,
      dsny_integration: isMaintenanceDSNY, // Auto-enable for Maintenance DSNY
      garbage_schedule: [],
      recycling_schedule: [],
      organics_schedule: [],
      bulk_schedule: [],
      interior_cleaning_schedule: [],
    })
    setEditingServiceId(null)
    setShowAddServiceModal(false)
  }

  const isMaintenanceServiceById = (serviceItemId: string) => {
    const selectedItem = serviceItems.find((item) => item.id === serviceItemId)

    if (!selectedItem) {
      console.log('Service item not found for ID:', serviceItemId)
      return false
    }

    if (!selectedItem.categories) {
      console.log('Service item has no category:', selectedItem.name)
      return false
    }

    if (!maintenanceDSNYCategoryId) {
      console.log('Maintenance - DSNY category ID not set')
      return false
    }

    const isDSNY = selectedItem.service_category_id === maintenanceDSNYCategoryId
    console.log('Checking DSNY integration for:', selectedItem.name, {
      itemCategoryId: selectedItem.service_category_id,
      itemCategoryName: selectedItem.categories.name,
      maintenanceDSNYId: maintenanceDSNYCategoryId,
      matches: isDSNY,
    })

    return isDSNY
  }

  // Validation and navigation
  const validateTab = (
    tab: 'overview' | 'services' | 'billing' | 'notes'
  ): boolean => {
    switch (tab) {
      case 'overview':
        return !!(
          formData.customer_id &&
          formData.property_id &&
          formData.service_type &&
          formData.title
        )
      case 'services': {
        if (!formData.contract_type) return false
        // Job Assignment source is required (no silent property_default default).
        if (!formData.assignment_source) return false
        // For DSNY maintenance schedule, check if rows have at least one day selected
        let scheduleValid: boolean
        if (recurringScheduleType === 'maintenance_dsny') {
          scheduleValid = dsnyScheduleRows.some(
            (row) =>
              normalizeRowDays(row.days).length > 0 &&
              (row.type !== 'custom' || !!(row.service_item_id || row.name.trim()))
          )
          if (
            scheduleValid &&
            dsnyScheduleRows.some(
              (row) =>
                (row.type === 'custom' || row.type === 'hallway_cleaning') &&
                dsnyRowNeedsCombineTarget(row)
            )
          ) {
            return false
          }
        } else {
          // For other schedules, check if services are added
          scheduleValid = contractServices.length > 0
        }
        if (!scheduleValid) return false
        // Visit groups live on this tab now; enforce them only for recurring contracts
        // being saved as active (drafts can proceed and finish setup later)
        if (formData.contract_type === 'recurring' && formData.status === 'active') {
          return validateVisitGroups(visitGroups).length === 0
        }
        return true
      }
      case 'billing':
        return formData.billing_frequency !== '' && formData.payment_terms !== ''
      case 'notes':
        return true // Notes are optional
      default:
        return false
    }
  }

  // Build the ordered wizard tab list (visit groups live inside the Services & Schedule tab)
  const getWizardTabs = (): Array<'overview' | 'services' | 'billing' | 'notes'> => {
    return ['overview', 'services', 'billing', 'notes']
  }

  // Build a list of currently-selected services in a shape the auto-suggester / editor understands.
  // For DSNY maintenance contracts we synthesize one entry per dsnyScheduleRows row. Rows with
  // `combine_with_dsny=true` share the DSNY category so they merge into one visit when days match;
  // rows with `combine_with_dsny=false` get a unique pseudo-category so they always stand alone.
  const getEffectiveServicesForVisitGroups = () => {
    const catalogById = new Map<string, any>()
    const catalogByName = new Map<string, any>()
    for (const group of serviceItemsGrouped) {
      for (const item of group.items || []) {
        catalogById.set(item.id, item)
        catalogByName.set(String(item.name || '').trim().toLowerCase(), item)
      }
    }
    const attachWork = (s: any) => {
      const fromId = s.service_item_id ? catalogById.get(s.service_item_id) : null
      const fromName = catalogByName.get(String(s.service_name || s.name || '').trim().toLowerCase())
      const catalog = fromId || fromName
      return {
        ...s,
        service_item_id: s.service_item_id || catalog?.id,
        work_description: catalog?.work_description || null,
        default_checklist: Array.isArray(catalog?.default_checklist)
          ? catalog.default_checklist
          : [],
        is_seasonal: catalog?.is_seasonal,
        season_start_month: catalog?.season_start_month ?? null,
        season_end_month: catalog?.season_end_month ?? null,
      }
    }

    if (recurringScheduleType === 'maintenance_dsny') {
      const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
      return buildDsnyVisitBoardServices(
        dsnyScheduleRows as any,
        contractServices,
        packageItem?.name ||
          (dsnyPackageKind === 'exterior_maintenance'
            ? 'Exterior Maintenance'
            : 'Building Upkeep')
      ).map(attachWork)
    }
    return contractServices.map((s: any) =>
      attachWork({
        id: s.id,
        service_name: s.service_name,
        recurrence_type: s.schedule?.recurrence_type || 'weekly',
        recurrence_days: s.schedule?.recurrence_days || [],
        recurrence_day_of_month: s.schedule?.recurrence_day_of_month ?? null,
        recurrence_weekday: s.schedule?.recurrence_weekday ?? null,
        recurrence_occurrence: s.schedule?.recurrence_occurrence ?? null,
        service_type_id: s.service_type || '',
        service_item_id: s.service_item_id,
        dsny_integration: !!s.schedule?.dsny_integration,
      })
    )
  }

  const getServicesForVisitGroupSeed = () => getEffectiveServicesForVisitGroups()

  // Auto-suggest visit groups using global combination rules.
  // For DSNY maintenance rows we honour the per-row `combine_with_dsny` flag:
  // - combine_with_dsny=TRUE  -> share the DSNY category id (so they merge by (category + days))
  // - combine_with_dsny=FALSE -> unique pseudo category per row (so it always gets its own group)
  const visitGroupsContentSignature = (groups: ContractVisitGroup[]) =>
    JSON.stringify(
      groups.map((g) => ({
        name: g.name,
        recurrence_type: g.recurrence_type,
        recurrence_days: g.recurrence_days || [],
        recurrence_weekday: g.recurrence_weekday ?? null,
        recurrence_occurrence: g.recurrence_occurrence ?? null,
        recurrence_day_of_month: g.recurrence_day_of_month ?? null,
        dsny_integration: !!g.dsny_integration,
        assigned_crew_id: g.assigned_crew_id || null,
        assigned_employee_id: g.assigned_employee_id || null,
        assignments: getGroupAssignments(g).map((a) => ({
          service_id: a.service_id,
          schedule_mode: a.schedule_mode,
          recurrence_type: a.recurrence_type ?? null,
          recurrence_days: a.recurrence_days || [],
          recurrence_weekday: a.recurrence_weekday ?? null,
          recurrence_occurrence: a.recurrence_occurrence ?? null,
          recurrence_day_of_month: a.recurrence_day_of_month ?? null,
          recurrence_nearest_visit: a.recurrence_nearest_visit ?? null,
        })),
      }))
    )

  /** Drop standalone copies of services that already piggyback on another group. */
  const healPiggybackDuplicates = (groups: ContractVisitGroup[]): ContractVisitGroup[] => {
    const piggybackServiceIds = new Set<string>()
    for (const g of groups) {
      for (const a of getGroupAssignments(g)) {
        if (a.schedule_mode === 'piggyback') piggybackServiceIds.add(a.service_id)
      }
    }
    if (piggybackServiceIds.size === 0) return groups

    let changed = false
    let next = groups.map((g) => {
      const current = getGroupAssignments(g)
      const filtered = current.filter(
        (a) => !(piggybackServiceIds.has(a.service_id) && a.schedule_mode !== 'piggyback')
      )
      if (filtered.length !== current.length) {
        changed = true
        return syncGroupServiceIds({ ...g, service_assignments: filtered })
      }
      return g
    })

    const pruned = next.filter((g) => getGroupAssignments(g).length > 0)
    if (pruned.length !== next.length) {
      changed = true
      next = pruned
    }
    return changed ? next : groups
  }

  const seedVisitGroupsFromServices = () => {
    const services = getServicesForVisitGroupSeed()
    if (services.length === 0) {
      setVisitGroups((prev) => (prev.length === 0 ? prev : []))
      return
    }

    const servicesWithCategory = services.map((s: any) => ({
      ...s,
      service_type_id:
        s.service_type_id ||
        s.category_id ||
        (s.combine_target === 'dsny'
          ? maintenanceDSNYCategoryId
          : s.combine_target === 'hallway'
            ? 'hallway_cleaning'
            : `standalone_${s.id}`),
    }))

    const suggestions = suggestGroupsDayCentric(
      servicesWithCategory,
      '',
      formData.start_date || null,
      formData.end_date || null
    )
    const finalized =
      recurringScheduleType === 'maintenance_dsny'
        ? finalizeDsnyVisitGroupsAfterSeed(
            suggestions,
            findDsnyPackageBoardId(contractServices),
            getEffectiveServicesForVisitGroups() as any,
            dsnyScheduleRows as any,
            contractServices
          )
        : suggestions
    const next = healPiggybackDuplicates(finalized)
    setVisitGroups((prev) =>
      visitGroupsContentSignature(prev) === visitGroupsContentSignature(next) ? prev : next
    )
  }

  // Live derivation: keep the Crew Visits preview in sync with the schedule as the user
  // edits it, until they customize the groups manually. After customization, changes to
  // the schedule surface a "Re-derive" prompt instead of silently overwriting their work.
  //
  // For maintenance DSNY (Building Upkeep AND Exterior Maintenance), board services
  // come from dsnyScheduleRows only — do NOT also depend on contractServices. Package
  // sync updates contractServices on every day toggle and would reseed a second time,
  // remounting Crew Visits mid-layout and blanking the Services tab.
  useEffect(() => {
    if (formData.contract_type !== 'recurring') return
    if (visitGroupsCustomized) return
    const services = getEffectiveServicesForVisitGroups()
    if (services.length === 0) {
      startTransition(() => {
        setVisitGroups((prev) => (prev.length === 0 ? prev : []))
        setVisitGroupsSeeded(false)
      })
      return
    }
    // Debounce + transition: day toggles rebuild Crew Visits; doing it in the
    // same turn as the click can leave main scrolled into empty white space.
    const t = window.setTimeout(() => {
      startTransition(() => {
        seedVisitGroupsFromServices()
        setVisitGroupsSeeded(true)
      })
    }, 120)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dsnyScheduleRows,
    dsnyPackageKind,
    recurringScheduleType === 'maintenance_dsny' ? null : contractServices,
    recurringScheduleType,
    visitGroupsCustomized,
    formData.contract_type,
    formData.start_date,
    formData.end_date,
    maintenanceDSNYCategoryId,
    serviceItemsGrouped,
  ])

  // Marks visit groups as user-customized (board drag/drop or advanced editor edits)
  const handleVisitGroupsManualChange = (groups: ContractVisitGroup[]) => {
    setVisitGroups(groups)
    setVisitGroupsCustomized(true)
  }

  // Explicit re-derive: rebuild groups from the schedule, discarding manual customization
  const handleReDeriveVisitGroups = () => {
    if (
      visitGroupsCustomized &&
      !window.confirm(
        'Re-deriving will rebuild crew visits from the schedule and discard your manual changes. Continue?'
      )
    ) {
      return
    }
    const priorGroups = visitGroups
    const priorRows = dsnyScheduleRows
    seedVisitGroupsFromServices()
    setVisitGroups((seeded) => preserveVisitGroupTargetAssignees(priorGroups, seeded))
    setDsnyScheduleRows((rows) => {
      const exterior =
        priorRows.find(
          (r) =>
            resolveDsnyCombineTarget(r as any) === 'dsny' &&
            (r.assigned_crew_id || r.assigned_employee_id)
        ) || priorGroups.find((g) => g.dsny_integration && (g.assigned_crew_id || g.assigned_employee_id))
      const interior =
        priorRows.find(
          (r) =>
            resolveDsnyCombineTarget(r as any) === 'hallway' &&
            (r.assigned_crew_id || r.assigned_employee_id)
        ) ||
        priorGroups.find(
          (g) =>
            /^Hallway Cleaning\b/i.test(g.name || '') &&
            (g.assigned_crew_id || g.assigned_employee_id)
        )
      return rows.map((row) => {
        const target = resolveDsnyCombineTarget(row as any)
        if (target === 'dsny' && exterior) {
          return {
            ...row,
            assigned_crew_id:
              (exterior as any).assigned_crew_id || row.assigned_crew_id || null,
            assigned_employee_id:
              (exterior as any).assigned_employee_id || row.assigned_employee_id || null,
          }
        }
        if (target === 'hallway' && interior) {
          return {
            ...row,
            assigned_crew_id:
              (interior as any).assigned_crew_id || row.assigned_crew_id || null,
            assigned_employee_id:
              (interior as any).assigned_employee_id || row.assigned_employee_id || null,
          }
        }
        return row
      })
    })
    setVisitGroupsSeeded(true)
    setVisitGroupsCustomized(false)
  }

  // After the initial seed, keep non-weekly rows represented on the board.
  // Piggyback-combine rows attach to the weekly host — never as an isolated monthly group.
  useEffect(() => {
    if (recurringScheduleType !== 'maintenance_dsny' || !visitGroupsSeeded) return
    const services = getServicesForVisitGroupSeed() as any[]
    if (services.length === 0) return

    setVisitGroups((prev) => {
      const attached = attachMissingNonWeeklyBoardServices(prev, services, {
        contractId: '',
        contractStartDate: formData.start_date || null,
        contractEndDate: formData.end_date || null,
      })
      const healed = healPiggybackDuplicates(attached)
      return visitGroupsContentSignature(prev) === visitGroupsContentSignature(healed)
        ? prev
        : healed
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dsnyScheduleRows, recurringScheduleType, visitGroupsSeeded, formData.start_date, formData.end_date])

  // Keep Exterior/Interior visit-group dropdowns in sync with schedule assignees.
  useEffect(() => {
    if (recurringScheduleType !== 'maintenance_dsny' || !visitGroupsSeeded) return
    setVisitGroups((prev) => {
      const next = syncVisitGroupAssigneesFromScheduleRows(prev, dsnyScheduleRows as any)
      const same =
        next.length === prev.length &&
        next.every(
          (g, i) =>
            g.assigned_crew_id === prev[i]?.assigned_crew_id &&
            g.assigned_employee_id === prev[i]?.assigned_employee_id
        )
      return same ? prev : next
    })
  }, [dsnyScheduleRows, recurringScheduleType, visitGroupsSeeded])

  const handleNext = () => {
    if (!validateTab(activeTab)) {
      if (activeTab === 'services' && !formData.assignment_source) {
        alert('Choose a Job Assignment source before continuing.')
        return
      }
      if (
        activeTab === 'services' &&
        recurringScheduleType === 'maintenance_dsny' &&
        dsnyScheduleRows.some(
          (row) =>
            (row.type === 'custom' || row.type === 'hallway_cleaning') &&
            dsnyRowNeedsCombineTarget(row)
        )
      ) {
        alert('Choose a Visit target for every schedule line before continuing.')
        return
      }
      alert('Please fill in all required fields before continuing')
      return
    }

    const tabs = getWizardTabs()
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1])
    }
  }

  // Handle selecting DSNY schedule type - check for matching proposal line items
  const handleSelectDSNYSchedule = () => {
    const buildingUpkeepItem = findBuildingUpkeepItem(serviceItemsGrouped)
    const exteriorMaintenanceItem = findExteriorMaintenanceItem(serviceItemsGrouped)
    const packageItem =
      findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped) ||
      buildingUpkeepItem ||
      exteriorMaintenanceItem

    // Check if there's a matching proposal line item (by service_item_id OR by description)
    let matchingProposalItem: any = null

    if (proposalLineItems.length > 0) {
      const packageIds = [buildingUpkeepItem?.id, exteriorMaintenanceItem?.id].filter(
        Boolean
      ) as string[]
      matchingProposalItem = proposalLineItems.find(
        (propItem: any) =>
          propItem.service_item_id && packageIds.includes(propItem.service_item_id)
      )

      if (!matchingProposalItem) {
        matchingProposalItem = proposalLineItems.find((propItem: any) => {
          const desc = (propItem.description || '').toLowerCase().trim()
          return (
            desc.includes('building upkeep') ||
            desc.includes('building maintenance') ||
            desc.includes('exterior maintenance') ||
            desc === 'building upkeep' ||
            desc === 'building maintenance'
          )
        })
      }

      if (matchingProposalItem) {
        setDsnyPackageKind(
          detectDsnyPackageKind(
            {
              service_item_id: matchingProposalItem.service_item_id,
              service_name: matchingProposalItem.description,
            },
            serviceItemsGrouped
          )
        )
      }
    }

    const linkPackageItem =
      (matchingProposalItem &&
        (matchingProposalItem.service_item_id === exteriorMaintenanceItem?.id
          ? exteriorMaintenanceItem
          : matchingProposalItem.service_item_id === buildingUpkeepItem?.id
            ? buildingUpkeepItem
            : packageItem)) ||
      packageItem

    if (matchingProposalItem && linkPackageItem) {
      // Show dialog asking if user wants to link to proposal line item
      setLinkDialog({
        show: true,
        type: 'dsny-to-proposal',
        service: {
          id: 'dsny_package',
          service_item_id: linkPackageItem.id,
          service_name: linkPackageItem.name,
        },
        matchingProposalItem: matchingProposalItem,
        onConfirm: () => {
          // Set the recurring schedule type and store the proposal_line_item_id for later
          setRecurringScheduleType('maintenance_dsny')
          // Store the linking info in a ref or state for use when creating the service
          setDsnyProposalLinkId(matchingProposalItem.id)
          setLinkDialog(null)
        },
        onCancel: () => {
          // Set the recurring schedule type without linking
          setRecurringScheduleType('maintenance_dsny')
          setDsnyProposalLinkId(null)
          setLinkDialog(null)
        },
      })
    } else {
      // No matching proposal line item, just set the schedule type
      setRecurringScheduleType('maintenance_dsny')
      setDsnyProposalLinkId(null)
    }
  }

  const handleBack = () => {
    const tabs = getWizardTabs()
    const currentIndex = tabs.indexOf(activeTab)
    if (currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1])
    }
  }

  const handleSubmit = async () => {
    // Required-field checks (these are not in the consistency rules engine, since
    // the engine is reserved for cross-field issues; required fields are just
    // marked with `*` and HTML `required`, but we backstop here for safety).
    const missing: { field: string; tab: string; message: string }[] = []
    if (!formData.contract_type) {
      missing.push({
        field: 'contract_type',
        tab: 'services',
        message: 'Pick a contract type (one-time or recurring).',
      })
    }
    if (!formData.assignment_source) {
      missing.push({
        field: 'assignment_source',
        tab: 'services',
        message: 'Choose a Job Assignment source before saving.',
      })
    }
    if (!formData.billing_frequency) {
      missing.push({
        field: 'billing_frequency',
        tab: 'billing',
        message: 'Pick a billing frequency.',
      })
    }
    if (formData.contract_type === 'recurring' && !formData.start_date) {
      missing.push({
        field: 'start_date',
        tab: 'billing',
        message: 'Recurring contracts require a start date.',
      })
    }
    if (formData.contract_type === 'recurring' && !formData.end_date) {
      missing.push({
        field: 'end_date',
        tab: 'billing',
        message: 'Recurring contracts require an end date (set a start date and duration, or pick an end date).',
      })
    }
    if (missing.length > 0) {
      const first = missing[0]
      setNotification({ type: 'error', message: first.message })
      setTimeout(() => setNotification(null), 6000)
      setActiveTab(first.tab as any)
      return
    }

    if (
      recurringScheduleType === 'maintenance_dsny' &&
      dsnyScheduleRows.some(
        (row) =>
          (row.type === 'custom' || row.type === 'hallway_cleaning') &&
          dsnyRowNeedsCombineTarget(row)
      )
    ) {
      setNotification({
        type: 'error',
        message: 'Choose a Visit target for every schedule line before saving.',
      })
      setTimeout(() => setNotification(null), 6000)
      setActiveTab('services')
      return
    }

    // Phase 6: enforce consistency-rule blockers before save
    const { blockers } = splitIssues(checkConsistency(agreementState, 'contract'))
    if (blockers.length > 0) {
      const first = blockers[0]
      setNotification({
        type: 'error',
        message: `Please fix ${blockers.length} issue${blockers.length === 1 ? '' : 's'} before saving. First: ${first.message}`,
      })
      setTimeout(() => setNotification(null), 6000)
      if (first.target?.tab) setActiveTab(first.target.tab as any)
      return
    }

    // Gate "Create as Active" on visit-group validity for recurring contracts.
    // Drafts/pending_signature/etc. are allowed through — the gate also runs on Mark Signed later.
    if (
      formData.contract_type === 'recurring' &&
      formData.status === 'active'
    ) {
      const errors = validateVisitGroups(visitGroups)
      if (errors.length > 0) {
        const proceed = window.confirm(
          [
            'This recurring contract cannot be activated yet because the visit groups are not fully configured:',
            '',
            ...errors.map((e) => `  • ${e}`),
            '',
            'Click OK to save the contract as a draft instead, or Cancel to go back and finish setup.',
          ].join('\n')
        )
        if (!proceed) {
          setActiveTab('services')
          setShowVisitBoard(true)
          return
        }
        // Downgrade to draft so the contract still saves
        setFormData((prev) => ({ ...prev, status: 'draft' }))
        // continue submission below — but use a local override so we don't race React state
        ;(formData as any).status = 'draft'
      }
    }

    setLoading(true)

    try {
      // Debug logging
      console.log('Form data before submission:', formData)

      const contractData = {
        ...formData,
        operational_division_id: resolveOperationalDivisionId(formData.service_type, divisions),
        contract_number: '', // Will be auto-generated
        template_type: formData.template_type || null,
        contract_type:
          formData.contract_type || (formData.is_recurring ? 'recurring' : 'one_time'),
        is_recurring: formData.contract_type === 'recurring',
        customer_id: formData.customer_id || null,
        property_id: selectedPropertyIds[0] || formData.property_id || null,
        scope_type: scopeType,
        proposal_id: formData.proposal_id || null,
        parent_contract_id: formData.parent_contract_id || null,
        original_contract_id: formData.original_contract_id || null,
        renewal_date: formData.parent_contract_id ? new Date().toISOString().split('T')[0] : null,
        total_amount: formData.total_amount || null,
        contract_total_amount: formData.contract_total_amount || null,
        billing_frequency: formData.billing_frequency || null,
        billing_day_of_month: Number((formData as any).billing_day_of_month) || 1,
        billing_method:
          formData.billing_frequency === 'progress' ? 'progress' : formData.billing_method || null,
        payment_method: formData.payment_method || null,
        sales_tax_status: formData.sales_tax_status || null,
        recurrence_end_count: formData.recurrence_end_count || null,
        late_fee_percentage: formData.late_fee_percentage || null,
        recurrence_end_date: formData.recurrence_end_date || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        cancel_date:
          formData.status === 'cancelled' && formData.cancel_date ? formData.cancel_date : null,
        signed_date: ['active', 'signed', 'in_process'].includes(formData.status)
          ? new Date().toISOString().split('T')[0]
          : null,
        // Comprehensive schedule system (now enabled after schema update)
        master_weekly_schedule: formData.master_weekly_schedule || [],
        garbage_schedule: formData.garbage_schedule || [],
        recycling_schedule: formData.recycling_schedule || [],
        bulk_schedule: formData.bulk_schedule || [],
        organics_schedule: formData.organics_schedule || [],
        interior_cleaning_schedule: formData.interior_cleaning_schedule || [],
        // Dynamic manual schedules
        manual_schedules: formData.manual_schedules || [],
        // Legacy fields for backward compatibility
        dsny_pickup_days: formData.dsny_pickup_days || [],
        dsny_collection_types: formData.dsny_collection_types || [],
        // Notes as JSONB array (initialize as empty array for new contracts)
        notes: [],
        assignment_source: formData.assignment_source,
        default_assigned_crew_id:
          formData.assignment_source === 'custom'
            ? formData.default_assigned_crew_id || null
            : null,
        default_assigned_employee_id:
          formData.assignment_source === 'custom'
            ? formData.default_assigned_employee_id || null
            : null,
      }

      console.log('Contract data to submit:', contractData)

      const { data, error } = await supabase
        .from('contracts')
        .insert(contractData)
        .select()
        .single()

      if (error) {
        console.error('❌ Contract creation error:', error)
        console.error('Contract data that failed:', contractData)
        throw error
      }

      if (!data || !data.id) {
        console.error('❌ Contract creation returned no data or no ID')
        throw new Error('Contract creation failed: No data returned')
      }

      await syncContractProperties(
        data.id,
        selectedPropertyIds.length > 0
          ? selectedPropertyIds
          : formData.property_id
            ? [formData.property_id]
            : [],
        selectedPropertyIds[0] || formData.property_id || null
      )

      console.log('✅ Contract created successfully:', {
        id: data.id,
        contract_number: data.contract_number,
        title: data.title,
        customer_id: data.customer_id,
        property_id: data.property_id,
        proposal_id: data.proposal_id,
        status: data.status,
      })

      if (formData.billing_frequency === 'progress') {
        const contractTotal =
          Number(formData.contract_total_amount) || Number(formData.total_amount) || 0
        await saveContractMilestones(data.id, billingMilestones, contractTotal)
      }

      const contractDescription = `Contract "${data.title}" (${data.contract_number}) created`
      await logActivity({
        activity_type: ActivityTypes.CONTRACT_CREATED,
        entity_type: 'contract',
        entity_id: data.id,
        description: contractDescription,
        metadata: {
          customer_id: data.customer_id,
          property_id: data.property_id,
          proposal_id: data.proposal_id,
        },
      })
      await logActivityOnLinkedParents({
        parents: {
          customer_id: data.customer_id,
          property_id: data.property_id,
        },
        activity_type: ActivityTypes.CONTRACT_CREATED,
        description: contractDescription,
        metadata: {
          child_entity_type: 'contract',
          child_entity_id: data.id,
          contract_number: data.contract_number,
        },
      })

      // Check for existing jobs from the same proposal that could be linked
      let shouldShowLinkModal = false
      if (formData.proposal_id && data?.id) {
        try {
          const { data: existingJobs, error: jobsError } = await supabase
            .from('jobs')
            .select('id, job_number, title, scheduled_date, status')
            .eq('proposal_id', formData.proposal_id)
            .is('contract_id', null) // Only jobs not already linked to a contract

          if (!jobsError && existingJobs && existingJobs.length > 0) {
            // Show modal to link jobs
            setLinkJobsModal({
              show: true,
              contractId: data.id,
              jobs: existingJobs,
            })
            shouldShowLinkModal = true
          }
        } catch (error) {
          console.error('Error checking for existing jobs:', error)
          // Continue with normal flow if check fails
        }
      }

      // Note: when the link-jobs modal is shown we still persist services and visit
      // groups below; navigation is deferred via the `!shouldShowLinkModal` guard later.
      // (Previously this returned early here, which left DSNY contracts created from a
      // proposal with zero contract_services and no visit groups.)

      // Save contract services if any exist
      // For maintenance_dsny, convert DSNY schedule rows to services
      let servicesToSave = [...contractServices]
      // Map to track linked_service_id -> actual contract_service.id after save
      const serviceIdMap = new Map<string, string>()

      // For DSNY maintenance, create the Building Maintenance service
      // Use dsnyProposalLinkId (set when user selected DSNY schedule and confirmed linking)
      const hasDSNYPackageLineItem = lineItems.some(
        (item) => item.linked_service_id === 'dsny_package'
      )

      if (recurringScheduleType === 'maintenance_dsny' && dsnyScheduleRows.length > 0 && data?.id) {
        const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
        const buildingUpkeepItem = findBuildingUpkeepItem(serviceItemsGrouped)
        const exteriorMaintenanceItem = findExteriorMaintenanceItem(serviceItemsGrouped)
        if (!packageItem) {
          console.warn(
            '⚠️ DSNY package service item not found in catalog. Available items:',
            serviceItemsGrouped.flatMap((g) => g.items.map((i) => i.name))
          )
        }

        // Use the dsnyProposalLinkId that was set when user selected DSNY schedule
        const proposalLineItemId = dsnyProposalLinkId || undefined

        // Reuse price/proposal link from any existing package row, then strip
        // schedule-derived services so deriveDsnyContractArtifacts is the only
        // source (prevents duplicate package rows from proposal sync).
        const packageIds = [packageItem?.id, buildingUpkeepItem?.id, exteriorMaintenanceItem?.id].filter(
          Boolean
        ) as string[]
        const statePackageService =
          servicesToSave.find((s) => s.id === 'dsny_package') ||
          servicesToSave.find((s) => s.service_item_id && packageIds.includes(s.service_item_id))
        servicesToSave = servicesToSave.filter(
          (s) =>
            !isScheduleDerivedContractService(s, buildingUpkeepItem, exteriorMaintenanceItem)
        )

        // Single source of truth: schedule rows in, services out (shared with Edit)
        const { services: derivedDsnyServices } = deriveDsnyContractArtifacts(dsnyScheduleRows, {
          buildingUpkeepItem: packageItem,
          packageKind: dsnyPackageKind,
          maintenanceDSNYCategoryId,
          proposalLineItemId:
            statePackageService?.proposal_line_item_id || proposalLineItemId || undefined,
          amount: statePackageService?.amount ?? dsnyProposalAmount ?? undefined,
          forcePackage: hasDSNYPackageLineItem || !!proposalLineItemId || !!statePackageService,
          childItemsByType: buildDsnyChildItemsByType(serviceItemsGrouped),
        })
        servicesToSave = [...servicesToSave, ...derivedDsnyServices]
      }

      // Save contract services and get their IDs back
      if (servicesToSave.length > 0 && data?.id) {
        try {
          console.log('Saving contract services:', servicesToSave)

          const serviceInserts = servicesToSave.map((service) => {
            // Check if there's a matching proposal line item with the same service_item_id
            const matchingProposalItem = service.service_item_id
              ? proposalLineItems.find(
                  (propItem) => propItem.service_item_id === service.service_item_id
                )
              : null

            if (matchingProposalItem && service.id !== 'dsny_package') {
              console.log('✅ Linking contract service to proposal line item:', {
                serviceId: service.id,
                serviceName: service.service_name,
                proposalLineItemId: matchingProposalItem.id,
                serviceItemId: service.service_item_id,
              })
            }

            const combineTarget = service.schedule?.combine_target
            return {
              contract_id: data.id,
              // uuid columns: sentinel strings like "maintenance_dsny" poison the
              // whole multi-row insert (22P02), so coerce non-uuids to null
              service_type_id: toUuidOrNull(service.service_type),
              service_name: service.service_name,
              service_item_id: toUuidOrNull(service.service_item_id),
              amount: service.amount || 0,
              recurrence_type: service.schedule?.recurrence_type || null,
              recurrence_days: service.schedule?.recurrence_days || [],
              dsny_integration: service.schedule?.dsny_integration || false,
              garbage_schedule: service.schedule?.garbage_schedule || [],
              recycling_schedule: service.schedule?.recycling_schedule || [],
              organics_schedule: service.schedule?.organics_schedule || [],
              bulk_schedule: service.schedule?.bulk_schedule || [],
              interior_cleaning_schedule: service.schedule?.interior_cleaning_schedule || [],
              // Visit target + monthly cadence — required for load-time piggyback heal
              combine_target:
                combineTarget === 'dsny' || combineTarget === 'hallway' || combineTarget === null
                  ? combineTarget
                  : null,
              recurrence_weekday: service.schedule?.recurrence_weekday ?? null,
              recurrence_occurrence: service.schedule?.recurrence_occurrence ?? null,
              recurrence_day_of_month: service.schedule?.recurrence_day_of_month ?? null,
              proposal_line_item_id:
                (service as any).proposal_line_item_id || matchingProposalItem?.id || null, // Link to proposal line item
            }
          })

          const { data: savedServices, error: servicesError } = await supabase
            .from('contract_services')
            .insert(serviceInserts)
            .select('id, service_name, service_item_id')

          if (servicesError) {
            console.error('Error saving contract services:', servicesError)
            throw servicesError
          }

          // Map temp ids → persisted UUIDs by service_name (not insert-return order).
          if (savedServices) {
            const built = buildContractServiceIdMap(servicesToSave, savedServices)
            built.forEach((uuid, tempId) => serviceIdMap.set(tempId, uuid))
          }
          if (recurringScheduleType === 'maintenance_dsny') {
            expandDsnyPackageServiceIdMap(serviceIdMap, dsnyScheduleRows)
          }

          console.log('Contract services saved successfully, ID mapping:', serviceIdMap)
        } catch (servicesError) {
          console.error('Error saving services:', servicesError)
          // DSNY visit groups need the service UUID map — do not continue without it.
          if (recurringScheduleType === 'maintenance_dsny') {
            throw servicesError
          }
          // Non-DSNY: don't fail the whole operation, just log the error
        }
      }

      // Save visit groups (recurring contracts only). DSNY temp ids (`dsny_<rowId>`)
      // resolve through serviceIdMap to the $0 child contract_services uuids.
      if (
        formData.contract_type === 'recurring' &&
        data?.id
      ) {
        try {
          let groupsToPersist = visitGroups
          if (recurringScheduleType === 'maintenance_dsny') {
            // Always rebuild from the live schedule on create. Debounced board state /
            // visitGroupsCustomized can still hold orphan Drains groups or miss
            // Exterior/Interior assignees that live on schedule rows.
            const packageItem = findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)
            groupsToPersist = buildDsnyVisitGroupsFromSchedule(dsnyScheduleRows as any, {
              contractId: data.id,
              contractStartDate: formData.start_date || null,
              contractEndDate: formData.end_date || null,
              contractServices: servicesToSave,
              maintenanceCategoryId: maintenanceDSNYCategoryId,
              packageLabel:
                packageItem?.name ||
                (dsnyPackageKind === 'exterior_maintenance'
                  ? 'Exterior Maintenance'
                  : 'Building Upkeep'),
            })
            // Prefer schedule-row assignees; fall back to any board overrides still in state.
            groupsToPersist = syncVisitGroupAssigneesFromScheduleRows(
              groupsToPersist,
              dsnyScheduleRows as any
            ).map((group) => {
              const match = visitGroups.find(
                (g) =>
                  !!g.dsny_integration === !!group.dsny_integration &&
                  JSON.stringify([...(g.recurrence_days || [])].sort()) ===
                    JSON.stringify([...(group.recurrence_days || [])].sort()) &&
                  (!!g.dsny_integration ||
                    /^Hallway Cleaning\b/i.test(g.name || '') ===
                      /^Hallway Cleaning\b/i.test(group.name || ''))
              )
              if (!match) return group
              return {
                ...group,
                assigned_crew_id:
                  group.assigned_crew_id || match.assigned_crew_id || null,
                assigned_employee_id:
                  group.assigned_employee_id || match.assigned_employee_id || null,
              }
            })
            groupsToPersist = ensureDsnyVisitGroupsForDisplay(
              groupsToPersist,
              servicesToSave,
              {
                scheduleRows: dsnyScheduleRows as any,
                contractId: data.id,
                contractStartDate: formData.start_date || null,
                contractEndDate: formData.end_date || null,
              }
            )

            const unresolved = countUnresolvedVisitGroupAssignments(
              groupsToPersist,
              serviceIdMap
            )
            if (groupsToPersist.length === 0) {
              throw new Error(
                'Could not build visit groups from the DSNY schedule. Check collection days and try again.'
              )
            }
            if (unresolved > 0) {
              throw new Error(
                `Could not link ${unresolved} visit-group service(s) to saved contract services. Visit groups were not saved.`
              )
            }
          } else if (groupsToPersist.length === 0) {
            const services = getServicesForVisitGroupSeed()
            if (services.length > 0) {
              const servicesWithCategory = services.map((s: any) => ({
                ...s,
                service_type_id:
                  s.service_type_id ||
                  s.category_id ||
                  `standalone_${s.id}`,
              }))
              groupsToPersist = suggestGroupsDayCentric(
                servicesWithCategory,
                data.id,
                formData.start_date || null,
                formData.end_date || null
              )
            }
          }

          // Shared persistence (atomic RPC when available, sequential fallback)
          await persistContractVisitGroups(data.id, groupsToPersist, serviceIdMap)
          console.log('✅ Visit groups saved for contract:', data.id)
        } catch (vgError) {
          console.error('Error saving visit groups:', vgError)
          if (recurringScheduleType === 'maintenance_dsny') {
            throw vgError
          }
        }
      }

      // Save contract line items if any exist
      if (data?.id) {
        try {
          const lineItemsToPersist =
            recurringScheduleType === 'maintenance_dsny'
              ? syncNonWeeklyDsnyLineItems(lineItems as any, servicesToSave as any)
              : lineItems

          if (lineItemsToPersist.length === 0) {
            // nothing to save
          } else {
          console.log('Saving contract line items:', lineItemsToPersist)
          console.log('Service ID mapping:', serviceIdMap)

          // Fetch existing contract services to check for matches
          const { data: existingServices } = await supabase
            .from('contract_services')
            .select('id, service_name, service_item_id')
            .eq('contract_id', data.id)

          const lineItemInserts = lineItemsToPersist
            .filter((item) => item.description.trim() !== '') // Only save items with descriptions
            .map((item, index) => {
              // Map linked_service_id to actual contract_service_id
              let contract_service_id = item.linked_service_id
                ? serviceIdMap.get(item.linked_service_id) || null
                : null

              // Don't auto-link - only link if linked_service_id is explicitly set (from "Add from Services" button)
              // Auto-linking is now handled via dialogs when items are added

              return {
                contract_id: data.id,
                contract_service_id: contract_service_id,
                service_item_id: item.service_item_id || null,
                proposal_line_item_id: item.proposal_line_item_id || null, // Link to proposal line item
                description: item.description.trim(),
                quantity: item.is_variable ? 0 : item.quantity || 1,
                is_variable: item.is_variable || false,
                unit_type: item.unit_type || 'flat_rate',
                unit_price: item.unit_price || 0,
                total_price: item.is_variable ? 0 : (item.quantity || 1) * (item.unit_price || 0),
                sort_order: index, // Use index to ensure sequential sort_order
              }
            })

          if (lineItemInserts.length > 0) {
            const { error: lineItemsError } = await supabase
              .from('contract_line_items')
              .insert(lineItemInserts)

            if (lineItemsError) {
              console.error('Error saving contract line items:', lineItemsError)
              // Don't fail the whole operation, just log the error
              // Table might not exist yet - user can create it later
            } else {
              console.log('Contract line items saved successfully')
            }
          }
          }
        } catch (lineItemsError) {
          console.error('Error saving line items:', lineItemsError)
          // Don't fail the whole operation, just log the error
        }
      }

      // If contract was created from a proposal linked to a lead, convert lead to customer
      if (contractData.proposal_id) {
        try {
          // Get the proposal to check if it has a lead_id
          const { data: proposal } = await supabase
            .from('proposals')
            .select('lead_id')
            .eq('id', contractData.proposal_id)
            .single()

          if (proposal?.lead_id) {
            await convertLeadToCustomer(proposal.lead_id)
            console.log('Lead converted to customer automatically')
          }
        } catch (conversionError) {
          console.error('Error converting lead:', conversionError)
          // Don't fail contract creation if conversion fails
        }
      }

      // Update proposal status if this contract was created from a proposal
      // This MUST run even if there were errors in other parts, so we do it after contract creation
      if (formData.proposal_id && data?.id) {
        try {
          console.log('🔄 Updating proposal status for proposal:', formData.proposal_id)

          // First, check current proposal status
          const { data: proposal, error: proposalFetchError } = await supabase
            .from('proposals')
            .select('status')
            .eq('id', formData.proposal_id)
            .single()

          if (proposalFetchError) {
            console.error('❌ Error fetching proposal for status update:', proposalFetchError)
            throw proposalFetchError
          }

          if (proposal) {
            console.log('📋 Current proposal status:', proposal.status)

            // Update to approved first if not already approved or converted
            if (proposal.status !== 'approved' && proposal.status !== 'converted') {
              const { error: approveError } = await supabase
                .from('proposals')
                .update({ status: 'approved' })
                .eq('id', formData.proposal_id)

              if (approveError) {
                console.error('❌ Error updating proposal status to approved:', approveError)
                throw approveError
              } else {
                console.log('✅ Proposal status updated to approved:', formData.proposal_id)
              }
            }

            // Then update to converted (if not already converted)
            // Also write the converted_to_contract_id back-link (Phase 8 conversion glue)
            if (proposal.status !== 'converted') {
              const { error: proposalUpdateError } = await supabase
                .from('proposals')
                .update({
                  status: 'converted',
                  converted_to_contract_id: data.id,
                  conversion_date: new Date().toISOString(),
                })
                .eq('id', formData.proposal_id)

              if (proposalUpdateError) {
                console.error(
                  '❌ Error updating proposal status to converted:',
                  proposalUpdateError
                )
                // Show error to user but don't fail contract creation
                setNotification({
                  type: 'error',
                  message: `Contract created, but failed to update proposal status: ${proposalUpdateError.message}`,
                })
                setTimeout(() => setNotification(null), 5000)
              } else {
                console.log('✅ Proposal status updated to converted with back-link:', formData.proposal_id)
              }
            } else {
              // Even if already converted, make sure the back-link is set
              await supabase
                .from('proposals')
                .update({
                  converted_to_contract_id: data.id,
                  conversion_date: new Date().toISOString(),
                })
                .eq('id', formData.proposal_id)
                .is('converted_to_contract_id', null)
              console.log('ℹ️ Proposal already converted, ensured back-link is set')
            }

            // Log activity for contract creation from proposal
            if (data?.id) {
              try {
                const { data: proposalData } = await supabase
                  .from('proposals')
                  .select('proposal_number, title')
                  .eq('id', formData.proposal_id)
                  .single()

                await logActivity({
                  activity_type: ActivityTypes.CONTRACT_CREATED,
                  entity_type: 'contract',
                  entity_id: data.id,
                  description: `Contract created from proposal ${proposalData?.proposal_number || formData.proposal_id}`,
                  metadata: {
                    proposal_id: formData.proposal_id,
                    proposal_number: proposalData?.proposal_number,
                    proposal_title: proposalData?.title,
                  },
                })
                console.log('✅ Activity logged for contract creation from proposal')
              } catch (activityError) {
                console.error('❌ Error logging activity:', activityError)
                // Don't fail contract creation if activity logging fails
              }
            }
          } else {
            console.warn('⚠️ Proposal not found for status update:', formData.proposal_id)
          }
        } catch (proposalError) {
          console.error('❌ Error updating proposal status:', proposalError)
          // Show error to user but don't fail contract creation
          setNotification({
            type: 'error',
            message: `Contract created, but failed to update proposal status: ${proposalError instanceof Error ? proposalError.message : 'Unknown error'}`,
          })
          setTimeout(() => setNotification(null), 5000)
        }
      } else if (formData.proposal_id && !data?.id) {
        console.error('❌ Cannot update proposal status: Contract was not created successfully')
      }

      // If we showed the link modal, don't navigate yet - wait for user decision
      // Otherwise, show success and navigate
      if (!shouldShowLinkModal) {
        setNotification({
          type: 'success',
          message: 'Contract created successfully!',
        })
        setTimeout(() => {
          if (returnTo) {
            navigate(returnTo)
          } else {
            navigate('/contracts')
          }
        }, 1500)
      } else {
        setNotification({
          type: 'success',
          message: 'Contract created successfully! Please review jobs to link.',
        })
      }
    } catch (error) {
      console.error('Error creating contract:', error)
      console.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
        fullError: error,
      })

      // Handle Supabase-specific errors
      if (error && typeof error === 'object' && 'code' in error) {
        const supabaseError = error as any
        console.error('Supabase error details:', {
          code: supabaseError.code,
          message: supabaseError.message,
          details: supabaseError.details,
          hint: supabaseError.hint,
        })
        setNotification({
          type: 'error',
          message: `Contract creation failed: ${supabaseError.message || 'Database error'}`,
        })
        setTimeout(() => setNotification(null), 5000)
      } else {
        setNotification({
          type: 'error',
          message: `Error creating contract: ${error instanceof Error ? error.message : 'Unknown error'}`,
        })
        setTimeout(() => setNotification(null), 5000)
      }
    } finally {
      setLoading(false)
    }
  }

  // Scroll to top when tab changes
  useEffect(() => {
    // Scroll the main content area (from Layout component) to top
    const mainElement = document.querySelector('main')
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' })
    } else {
      // Fallback to window scroll if main element not found
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [activeTab])

  // Build the agreement state used by the consistency rules engine
  const agreementState: AgreementState = React.useMemo(() => {
    const selectedProp = properties.find((p) => p.id === formData.property_id)
    return {
      contract_type: (formData.contract_type as any) || '',
      service_type: formData.service_type as any,
      billing_frequency: formData.billing_frequency as any,
      billing_method: (formData as any).billing_method as any,
      payment_method: (formData as any).payment_method as any,
      template_type: formData.template_type as any,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      property_address: selectedProp?.address || '',
      line_items: lineItems.map((li) => {
        const resolved = resolveLineItemRecurrenceForConsistency(li as any, contractServices, {
          garbage_schedule: formData.garbage_schedule,
          recycling_schedule: formData.recycling_schedule,
          organics_schedule: formData.organics_schedule,
          bulk_schedule: formData.bulk_schedule,
          visit_groups: visitGroups as any,
        })
        return {
          id: li.id,
          service_item_id: li.service_item_id,
          description: li.description,
          quantity: li.quantity,
          is_variable: li.is_variable,
          unit_type: li.unit_type,
          unit_price: li.unit_price,
          total_price: li.total_price,
          sort_order: li.sort_order,
          recurrence_type: resolved.recurrence_type as any,
          recurrence_days: resolved.recurrence_days,
        }
      }),
      dsny_integration: formData.dsny_integration,
      garbage_schedule: formData.garbage_schedule,
      recycling_schedule: formData.recycling_schedule,
      organics_schedule: formData.organics_schedule,
      bulk_schedule: formData.bulk_schedule,
      visit_groups: visitGroups.map((vg: any) => ({
        id: vg.id,
        name: vg.name,
        start_date: vg.start_date,
        end_date: vg.end_date,
        recurrence_type: vg.recurrence_type,
        recurrence_days: vg.recurrence_days,
      })),
      billing_milestones: billingMilestones,
      pricing_mode: formData.document_data?.pricingMode,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, lineItems, contractServices, properties, visitGroups, billingMilestones])

  const consistencyIssues = React.useMemo(
    () => checkConsistency(agreementState, 'contract'),
    [agreementState]
  )

  const handleNavigateIssue = (target: IssueTarget) => {
    if (!target) return
    // Visit groups now live inside the Services & Schedule tab
    const tab = target.tab === 'visit_groups' ? 'services' : target.tab
    if (target.tab === 'visit_groups') setShowVisitBoard(true)
    if (tab && tab !== activeTab) {
      setActiveTab(tab as any)
    }
  }

  return (
    <MobilePage className="max-w-6xl mx-auto pb-28 lg:pb-8">
        <MobilePageHeader
          sticky
          title="Create Contract"
          subtitle="Create a new service contract from a proposal or from scratch"
          onBack={() => navigate('/contracts')}
          backLabel="Back to Contracts"
        />

        <div className={`${mobile.surface} sticky top-[3.25rem] z-10 overflow-hidden`}>
          {/* Step strip — read-only wizard progress */}
          <div className="flex overflow-x-auto scrollbar-none border-b border-gray-200 bg-white/95 backdrop-blur-md">
              <div
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'overview'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                Overview
              </div>
              <div
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'services'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : activeTab === 'billing' || activeTab === 'notes'
                      ? 'text-blue-600'
                      : 'text-gray-400'
                }`}
              >
                <span className="sm:hidden">Services</span>
                <span className="hidden sm:inline">Services & Schedule</span>
              </div>
              <div
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'billing'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : activeTab === 'notes'
                      ? 'text-blue-600'
                      : 'text-gray-400'
                }`}
              >
                <span className="sm:hidden">Billing</span>
                <span className="hidden sm:inline">Billing & Financials</span>
              </div>
              <div
                className={`px-4 sm:px-5 min-h-[48px] flex items-center whitespace-nowrap text-sm font-medium ${
                  activeTab === 'notes'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-400'
                }`}
              >
                <span className="sm:hidden">Notes</span>
                <span className="hidden sm:inline">Notes & Activity</span>
              </div>
          </div>
        </div>

        <div className={mobile.surface}>

          {/* Consistency rules panel (Section I) */}
          {consistencyIssues.length > 0 && (
            <div className="px-4 sm:px-6 pt-4">
              <ConsistencyWarnings
                issues={consistencyIssues}
                onNavigate={handleNavigateIssue}
                currentTab={activeTab}
              />
            </div>
          )}

          <div className="p-4 sm:p-6">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Contract Title and Status - At the top */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={mobile.label}>
                      Contract Title *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className={mobile.field}
                      placeholder="e.g., 149 Skillman Street - Maintenance"
                    />
                  </div>

                  <div>
                    <label className={mobile.label}>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          status: e.target.value,
                        })
                      }
                      className={mobile.field}
                    >
                      {CONTRACT_STATUS_OPTIONS[
                        contractWorkflowType({
                          is_recurring: formData.is_recurring,
                          contract_type: formData.contract_type,
                        })
                      ].map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Cancel Date - Only show when status is cancelled */}
                  {formData.status === 'cancelled' && (
                    <div>
                      <label className={mobile.label}>
                        Cancel Date *
                      </label>
                      <input
                        type="date"
                        required={formData.status === 'cancelled'}
                        value={formData.cancel_date}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            cancel_date: e.target.value,
                          })
                        }
                        className={mobile.field}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Jobs scheduled after this date will be automatically deleted.
                      </p>
                    </div>
                  )}
                </div>

                {/* Two Column Layout: Customer/Property on left, Template/Proposal on right */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Left Column: Customer & Property */}
                  <div className="flex flex-col justify-between">
                    <div className="space-y-6">
                      <div className="space-y-4">
                        <h2 className="text-lg font-semibold text-gray-900">Customer & Property</h2>

                        <div className="space-y-4">
                          <div>
                            <label className={mobile.label}>
                              Customer *
                            </label>
                            <div className="flex gap-2">
                              <select
                                required
                                value={formData.customer_id}
                                onChange={(e) => {
                                  setFormData({
                                    ...formData,
                                    customer_id: e.target.value,
                                    property_id: '',
                                  })
                                  setSelectedPropertyIds([])
                                }}
                                className={`${mobile.field} flex-1`}
                              >
                                <option value="">Select customer...</option>
                                {customers.map((customer) => (
                                  <option key={customer.id} value={customer.id}>
                                    {customer.company_name ||
                                      `${customer.contact_first_name} ${customer.contact_last_name}`}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => setShowCreateCustomer(true)}
                                className="min-h-[48px] px-3 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors whitespace-nowrap text-sm font-semibold"
                              >
                                + New
                              </button>
                            </div>
                          </div>

                          <ContractPropertyScopeFields
                            contractType={formData.contract_type as ContractType | ''}
                            customerId={formData.customer_id}
                            scopeType={scopeType}
                            selectedPropertyIds={selectedPropertyIds}
                            properties={properties}
                            onScopeChange={(nextScope) => {
                              setScopeType(nextScope)
                              if (nextScope === 'single_property') {
                                const primary = selectedPropertyIds[0] || ''
                                setSelectedPropertyIds(primary ? [primary] : [])
                                setFormData({ ...formData, property_id: primary })
                              }
                            }}
                            onSelectionChange={(ids) => {
                              setSelectedPropertyIds(ids)
                              setFormData({ ...formData, property_id: ids[0] || '' })
                            }}
                            onCreateProperty={() => setShowCreateProperty(true)}
                          />
                        </div>
                      </div>
                    </div>
                    {/* Spacer to align with right column height */}
                    <div className="flex-grow"></div>
                  </div>

                  {/* Right Column: Template Type & Proposal Selection */}
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <h2 className="text-lg font-semibold text-gray-900">
                        Contract Type & Proposal
                      </h2>

                      <div className="space-y-4">
                        <div data-consistency-anchor="template-type">
                          <label className={mobile.label}>
                            Service Type *
                          </label>
                          <select
                            required
                            value={formData.service_type}
                            onChange={(e) => {
                              const newServiceType = e.target.value
                              const newDocData = newServiceType
                                ? buildDefaultDocumentData(newServiceType)
                                : ({} as DocumentData)
                              setFormData({
                                ...formData,
                                service_type: newServiceType,
                                template_type: newServiceType
                                  ? getDefaultTemplateTypeForServiceType(newServiceType)
                                  : '',
                                document_data: newDocData,
                                billing_method:
                                  newDocData.pricingMode === 'rate_card'
                                    ? 'actual_usage'
                                    : formData.billing_method,
                              })
                              if (
                                newDocData.pricingMode === 'rate_card' &&
                                hasRateCardSeed(newServiceType)
                              ) {
                                setLineItems(
                                  buildRateCardLineItems(newServiceType) as ContractLineItem[]
                                )
                              }
                            }}
                            className={mobile.field}
                          >
                            <option value="">Please select...</option>
                            {SERVICE_TYPE_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Drives the document template and DSNY scheduling.
                          </p>
                        </div>

                        <div>
                          <label className={mobile.label}>
                            Create from Proposal (Optional)
                          </label>
                          <select
                            value={formData.proposal_id}
                            onChange={(e) => handleProposalSelect(e.target.value)}
                            className={mobile.field}
                          >
                            <option value="">Select a proposal...</option>
                            {filteredProposals.map((proposal) => (
                              <option key={proposal.id} value={proposal.id}>
                                {proposal.proposal_number} - {proposal.title}
                              </option>
                            ))}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            Selecting a proposal will auto-fill service details and amount
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contract Description - Below the two columns */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-gray-900">Contract Description</h2>

                  <div>
                    <label className={mobile.label}>
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className={mobile.field}
                      placeholder="Enter contract description"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Services & Schedule Tab */}
            {activeTab === 'services' && (
              <div className="space-y-6">
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Job Assignment</h2>
                  <ContractAssignmentFields
                    value={{
                      assignment_source: formData.assignment_source,
                      default_assigned_crew_id: formData.default_assigned_crew_id,
                      default_assigned_employee_id: formData.default_assigned_employee_id,
                    }}
                    onChange={(assignment) => setFormData({ ...formData, ...assignment })}
                    hasProperty={!!formData.property_id}
                    propertyDefaultLabels={propertyAssignmentLabels}
                    required
                  />
                </div>
                {/* Contract Type */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract Type</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div data-consistency-anchor="contract-type">
                      <label className={mobile.label}>Type *</label>
                      <select
                        required
                        value={formData.contract_type}
                        onChange={(e) => {
                          const newContractType = e.target.value as '' | ContractType
                          // Reset status if it isn't valid for the new type's workflow
                          const statusOptions =
                            CONTRACT_STATUS_OPTIONS[
                              contractWorkflowType({
                                contract_type: newContractType,
                                is_recurring: newContractType === 'recurring',
                              })
                            ]
                          const statusValid = statusOptions.some((o) => o.value === formData.status)
                          setFormData({
                            ...formData,
                            contract_type: newContractType,
                            is_recurring: newContractType === 'recurring',
                            status: statusValid ? formData.status : 'draft',
                          })
                          // Reset recurring schedule type when contract type changes
                          if (newContractType !== 'recurring') {
                            setRecurringScheduleType(null)
                          }
                        }}
                        className={mobile.field}
                      >
                        <option value="">Select type...</option>
                        <option value="one_time">One Time</option>
                        <option value="as_needed">Ongoing (As Needed)</option>
                        <option value="recurring">Recurring</option>
                      </select>
                      {formData.contract_type === 'as_needed' && (
                        <p className="text-xs text-gray-500 mt-1">
                          Open-ended agreement. Jobs are created when needed — no auto-recurring
                          schedule.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Services & Schedule - Conditional based on contract type */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Services & Schedule</h2>
                  </div>

                  {formData.contract_type !== 'recurring' ? (
                    /* One-time / Ongoing — manual jobs, no visit board */
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 mb-4">
                        {formData.contract_type === 'as_needed'
                          ? 'Add services for this ongoing agreement. Create jobs as work is needed.'
                          : 'Select a service type and add a schedule. This will create a job for the service.'}
                      </p>

                      {contractServices.length === 0 ? (
                        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                          <p className="mb-4">No services added yet.</p>
                          <button
                            type="button"
                            onClick={() => {
                              if (formData.contract_type === 'recurring') {
                                setNewService((prev) => ({
                                  ...prev,
                                  recurrence_type: 'weekly',
                                }))
                              }
                              setShowAddServiceModal(true)
                            }}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Add Service & Schedule
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {contractServices.map((service) => (
                            <div
                              key={service.id}
                              className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <h3 className="font-medium text-gray-900">
                                    {service.service_name}
                                  </h3>
                                  <p className="text-sm text-gray-600">
                                    ${service.amount.toFixed(2)}
                                  </p>
                                  {service.schedule.recurrence_type && (
                                    <p className="text-xs text-blue-600 mt-1">
                                      Schedule: {service.schedule.recurrence_type}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleEditService(service.id)}
                                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleRemoveService(service.id)}
                                    className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              if (formData.contract_type === 'recurring') {
                                setNewService((prev) => ({
                                  ...prev,
                                  recurrence_type: 'weekly',
                                }))
                              }
                              setShowAddServiceModal(true)
                            }}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                          >
                            <PlusIcon className="w-4 h-4" />
                            Add Another Service
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Recurring Contract - Two options */
                    <div className="space-y-4">
                      {!recurringScheduleType ? (
                        <div className="space-y-3">
                          <p className="text-sm text-gray-600 mb-4">
                            Choose the type of recurring schedule you want to create:
                          </p>
                          <button
                            type="button"
                            onClick={() => handleSelectDSNYSchedule()}
                            className="w-full p-4 text-left border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                                <span className="text-2xl">🧹</span>
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">
                                  Create recurring Maintenance schedule with DSNY
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  Automatically sync with DSNY pickup schedules for garbage,
                                  recycling, organics, and bulk collection
                                </p>
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={openOtherRecurringSchedule}
                            className="w-full p-4 text-left border-2 border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                <span className="text-2xl">🔧</span>
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900">
                                  Create Other recurring schedule
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  Create a custom recurring schedule for non-maintenance services
                                </p>
                              </div>
                            </div>
                          </button>
                        </div>
                      ) : recurringScheduleType === 'maintenance_dsny' ? (
                        /* Maintenance DSNY Schedule */
                        <div className="space-y-4" data-consistency-anchor="dsny-schedule">
                          {/* In-app Notification */}
                          {notification && (
                            <div
                              className={`p-4 rounded-lg border ${
                                notification.type === 'success'
                                  ? 'bg-green-50 border-green-200 text-green-800'
                                  : 'bg-red-50 border-red-200 text-red-800'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium">{notification.message}</p>
                                <button
                                  onClick={() => setNotification(null)}
                                  className="ml-4 text-gray-400 hover:text-gray-600"
                                >
                                  ✕
                                </button>
                              </div>
                            </div>
                          )}
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              DSNY package
                            </label>
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  if (dsnyPackageKind === 'building_upkeep') return
                                  setDsnyPackageKind('building_upkeep')
                                  setDsnyScheduleRows((rows) =>
                                    rebaseDsnyRowsForPackageKind(
                                      'building_upkeep',
                                      rows
                                    ) as DsnyScheduleRowState[]
                                  )
                                }}
                                className={`px-3 py-1.5 text-sm rounded-md border ${
                                  dsnyPackageKind === 'building_upkeep'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                Building Upkeep
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  if (dsnyPackageKind === 'exterior_maintenance') return
                                  setDsnyPackageKind('exterior_maintenance')
                                  setDsnyScheduleRows((rows) =>
                                    rebaseDsnyRowsForPackageKind(
                                      'exterior_maintenance',
                                      rows
                                    ) as DsnyScheduleRowState[]
                                  )
                                }}
                                className={`px-3 py-1.5 text-sm rounded-md border ${
                                  dsnyPackageKind === 'exterior_maintenance'
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                Exterior Maintenance
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                              {dsnyPackageKind === 'exterior_maintenance'
                                ? 'Pickups only (Garbage, Recycling, Organics, Bulk) — no hallway cleaning.'
                                : 'Includes hallway cleaning plus DSNY pickup types.'}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                Maintenance Schedule with DSNY
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {dsnyPackageKind === 'exterior_maintenance'
                                  ? 'Configure DSNY pickup schedules for exterior maintenance'
                                  : 'Configure maintenance schedules for DSNY collection types and hallway cleaning'}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              {formData.property_id && (
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const selectedProperty = properties.find(
                                      (p) => p.id === formData.property_id
                                    )
                                    if (!selectedProperty) {
                                      alert('Property not found. Please select a property first.')
                                      return
                                    }
                                    if (!selectedProperty.address) {
                                      alert(
                                        'Selected property does not have an address. Please add an address to the property first.'
                                      )
                                      return
                                    }

                                    setFetchingDSNY(true)
                                    try {
                                      console.log(
                                        'Fetching DSNY schedule for:',
                                        selectedProperty.address
                                      )

                                      const dsnyData = await fetchDSNYPickupSchedule(
                                        selectedProperty.address
                                      )

                                      if (!dsnyData || !dsnyData.schedules) {
                                        throw new Error('Invalid DSNY data received')
                                      }

                                      const garbageDays = dsnyData.schedules.garbage || []
                                      const recyclingDays = dsnyData.schedules.recycling || []
                                      const organicsDays = dsnyData.schedules.organics || []
                                      const bulkDays = dsnyData.schedules.bulk || []

                                      setDsnyScheduleRows((currentRows) =>
                                        applyDsnyApiDaysToRows(
                                          dsnyPackageKind,
                                          dsnyData.schedules,
                                          currentRows
                                        ) as DsnyScheduleRowState[]
                                      )

                                      setDsnyScheduleFetched(true)

                                      const totalDays =
                                        garbageDays.length +
                                        recyclingDays.length +
                                        organicsDays.length +
                                        bulkDays.length
                                      if (totalDays > 0) {
                                        setNotification({
                                          type: 'success',
                                          message: `DSNY schedule loaded! Garbage: ${garbageDays.length} days, Recycling: ${recyclingDays.length} days, Organics: ${organicsDays.length} days, Bulk: ${bulkDays.length} days`,
                                        })
                                        setTimeout(() => setNotification(null), 5000)
                                      } else {
                                        setNotification({
                                          type: 'error',
                                          message:
                                            'DSNY schedule fetched but no collection days found. Please check the property address.',
                                        })
                                        setTimeout(() => setNotification(null), 5000)
                                      }
                                    } catch (error) {
                                      console.error('Error fetching DSNY schedule:', error)
                                      const errorMessage =
                                        error instanceof Error ? error.message : 'Unknown error'
                                      setNotification({
                                        type: 'error',
                                        message: `Error fetching DSNY schedule: ${errorMessage}. Please check the property address, internet connection, and DSNY API accessibility.`,
                                      })
                                      setTimeout(() => setNotification(null), 7000)
                                    } finally {
                                      setFetchingDSNY(false)
                                    }
                                  }}
                                  disabled={fetchingDSNY}
                                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {fetchingDSNY ? 'Fetching...' : 'Fetch DSNY Schedule'}
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  setRecurringScheduleType(null)
                                  setDsnyScheduleRows([])
                                  setDsnyScheduleFetched(false)
                                }}
                                className="text-sm text-gray-600 hover:text-gray-900"
                              >
                                Change Type
                              </button>
                            </div>
                          </div>

                          {!formData.property_id ? (
                            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-sm text-yellow-800">
                                ⚠️ Please select a property in the Overview tab to fetch DSNY
                                schedule
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <DsnyVisitRepAssignment
                                rows={dsnyScheduleRows}
                                crews={crews}
                                employees={employees}
                                onAssign={updateDsnyRowAssignments}
                              />
                              {dsnyScheduleRows.map((row) => {
                                const rowDays = normalizeRowDays(row.days)
                                return (
                                <div
                                  key={row.id}
                                  data-dsny-schedule-row={row.id}
                                  className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                                >
                                  <div className="flex items-start justify-between gap-2 mb-3">
                                    {row.type === 'custom' ? (
                                      <div className="flex-1 space-y-2 min-w-0">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">
                                            Catalogue item
                                          </label>
                                          <select
                                            value={row.service_item_id || ''}
                                            onChange={(e) =>
                                              selectCustomDsnyCatalogItem(row.id, e.target.value)
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                          >
                                            <option value="">Select from catalogue…</option>
                                            {customDsnyCatalogOptions.map((item) => (
                                              <option key={item.id} value={item.id}>
                                                {item.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <input
                                          type="text"
                                          value={row.name}
                                          onChange={(e) =>
                                            updateDsnyScheduleRowName(row.id, e.target.value)
                                          }
                                          placeholder="Display name (optional override)"
                                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                        />
                                      </div>
                                    ) : (
                                      <h4 className="font-medium text-gray-900">{row.name}</h4>
                                    )}
                                    {row.type === 'custom' && (
                                      <button
                                        type="button"
                                        onClick={() => removeDsnyScheduleRow(row.id)}
                                        className="ml-2 px-2 py-1 text-sm text-red-600 hover:text-red-800 flex-shrink-0"
                                      >
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                  {(row.type === 'custom' ||
                                    row.type === 'hallway_cleaning') && (
                                    <div className="mb-3">
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Recurrence
                                      </label>
                                      <select
                                        value={getRowRecurrenceType(row)}
                                        onChange={(e) => {
                                          const val = e.target.value as DsnyRowRecurrenceType
                                          updateDsnyScheduleRow(row.id, {
                                            recurrence_type: val,
                                            ...(val === 'monthly'
                                              ? {
                                                  monthly_mode:
                                                    row.monthly_mode || ('nth_weekday' as DsnyMonthlyMode),
                                                  // Selects show "1st Monday" via ?? defaults — persist those
                                                  // into state so create/save does not write null cadence.
                                                  recurrence_weekday: row.recurrence_weekday ?? 1,
                                                  recurrence_occurrence: row.recurrence_occurrence ?? 1,
                                                  recurrence_day_of_month:
                                                    row.monthly_mode === 'day_of_month'
                                                      ? row.recurrence_day_of_month ?? 1
                                                      : null,
                                                }
                                              : {}),
                                          })
                                        }}
                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      >
                                        {RECURRENCE_TYPE_OPTIONS.map((o) => (
                                          <option key={o.value} value={o.value}>
                                            {o.label}
                                          </option>
                                        ))}
                                      </select>
                                    </div>
                                  )}

                                  {getRowRecurrenceType(row) === 'daily' ? (
                                    <p className="text-sm text-gray-600">
                                      Runs every day (no day selection needed).
                                    </p>
                                  ) : getRowRecurrenceType(row) === 'monthly' ? (
                                    <div className="space-y-3">
                                      <div className="flex flex-wrap gap-4">
                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                          <input
                                            type="radio"
                                            name={`monthly_mode_${row.id}`}
                                            checked={(row.monthly_mode || 'nth_weekday') === 'nth_weekday'}
                                            onChange={() =>
                                              updateDsnyScheduleRow(row.id, {
                                                monthly_mode: 'nth_weekday',
                                              })
                                            }
                                          />
                                          <span>On the nth weekday (e.g. 2nd Monday)</span>
                                        </label>
                                        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                          <input
                                            type="radio"
                                            name={`monthly_mode_${row.id}`}
                                            checked={row.monthly_mode === 'day_of_month'}
                                            onChange={() =>
                                              updateDsnyScheduleRow(row.id, {
                                                monthly_mode: 'day_of_month',
                                              })
                                            }
                                          />
                                          <span>On a day of the month (e.g. the 15th)</span>
                                        </label>
                                      </div>

                                      {(row.monthly_mode || 'nth_weekday') === 'nth_weekday' ? (
                                        <div className="flex flex-wrap gap-2">
                                          <select
                                            value={row.recurrence_occurrence ?? 1}
                                            onChange={(e) =>
                                              updateDsnyScheduleRow(row.id, {
                                                recurrence_occurrence: parseInt(e.target.value, 10),
                                              })
                                            }
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                          >
                                            {OCCURRENCE_OPTIONS.map((o) => (
                                              <option key={o.value} value={o.value}>
                                                {o.label}
                                              </option>
                                            ))}
                                          </select>
                                          <select
                                            value={row.recurrence_weekday ?? 1}
                                            onChange={(e) =>
                                              updateDsnyScheduleRow(row.id, {
                                                recurrence_weekday: parseInt(e.target.value, 10),
                                              })
                                            }
                                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                          >
                                            {WEEKDAY_OPTIONS.map((o) => (
                                              <option key={o.value} value={o.value}>
                                                {o.label}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      ) : (
                                        <div className="space-y-2">
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-gray-700">Day of month</span>
                                            <input
                                              type="number"
                                              min={1}
                                              max={31}
                                              value={row.recurrence_day_of_month ?? 1}
                                              onChange={(e) =>
                                                updateDsnyScheduleRow(row.id, {
                                                  recurrence_day_of_month:
                                                    Math.min(
                                                      31,
                                                      Math.max(1, parseInt(e.target.value, 10) || 1)
                                                    ),
                                                })
                                              }
                                              className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                            />
                                          </div>
                                          {resolveDsnyCombineTarget(row) != null && (
                                            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                              <input
                                                type="checkbox"
                                                checked={!!row.snap_to_closest_visit}
                                                onChange={(e) =>
                                                  updateDsnyScheduleRow(row.id, {
                                                    snap_to_closest_visit: e.target.checked,
                                                  })
                                                }
                                                className="h-4 w-4 text-blue-600 rounded border-gray-300"
                                              />
                                              <span>Generate on the closest existing visit day</span>
                                            </label>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="grid grid-cols-7 gap-1.5">
                                      {dayNames.map((day) => {
                                        const selected = rowDays.includes(day.key)
                                        return (
                                          <button
                                            key={day.key}
                                            type="button"
                                            aria-pressed={selected}
                                            onClick={() => toggleDayForRow(row.id, day.key)}
                                            className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                              selected
                                                ? 'bg-green-100 border-green-500 text-green-800'
                                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                          >
                                            {day.label.slice(0, 3)}
                                          </button>
                                        )
                                      })}
                                    </div>
                                  )}
                                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
                                    <div>
                                      <label className="block text-xs font-medium text-gray-600 mb-1">
                                        Visit target {row.type === 'custom' ? '*' : ''}
                                      </label>
                                      <select
                                        value={visitTargetSelectValue(row)}
                                        onChange={(e) =>
                                          updateDsnyScheduleRow(row.id, {
                                            combine_target: visitTargetFromSelectValue(
                                              e.target.value
                                            ),
                                          })
                                        }
                                        className={`w-full px-3 py-2 border rounded-lg text-sm ${
                                          dsnyRowNeedsCombineTarget(row)
                                            ? 'border-amber-400'
                                            : 'border-gray-300'
                                        }`}
                                      >
                                        {row.type === 'custom' && (
                                          <option value="">Select visit target…</option>
                                        )}
                                        <option value="separate">Separate visit</option>
                                        <option value="dsny">Combine with DSNY Exterior</option>
                                        <option value="hallway">Combine with Hallway visits</option>
                                      </select>
                                    </div>
                                    {resolveDsnyCombineTarget(row) === null && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">
                                            Crew
                                          </label>
                                          <select
                                            value={row.assigned_crew_id || ''}
                                            onChange={(e) =>
                                              updateDsnyRowAssignments([row.id], {
                                                assigned_crew_id: e.target.value || null,
                                              })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                          >
                                            <option value="">Inherit from contract / property</option>
                                            {crews.map((crew) => (
                                              <option key={crew.id} value={crew.id}>
                                                {crew.name}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                        <div>
                                          <label className="block text-xs font-medium text-gray-600 mb-1">
                                            Employee
                                          </label>
                                          <select
                                            value={row.assigned_employee_id || ''}
                                            onChange={(e) =>
                                              updateDsnyRowAssignments([row.id], {
                                                assigned_employee_id: e.target.value || null,
                                              })
                                            }
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                          >
                                            <option value="">Inherit from contract / property</option>
                                            {employees.map((employee) => (
                                              <option key={employee.id} value={employee.id}>
                                                {formatEmployeeLabel(employee)}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                )
                              })}
                              <button
                                type="button"
                                onClick={addDsnyScheduleRow}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                              >
                                <PlusIcon className="w-4 h-4" />
                                Add Another Schedule Line
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        /* Other Recurring Schedule */
                        <div className="space-y-4">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                Other Recurring Schedule
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                Set how often each service runs. Proposal items are listed below —
                                complete any missing schedules to build crew visits.
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => setRecurringScheduleType(null)}
                              className="text-sm text-gray-600 hover:text-gray-900"
                            >
                              Change Type
                            </button>
                          </div>

                          {contractServices.length > 0 &&
                            contractServices.some((s) => serviceNeedsRecurringSchedule(s)) && (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 mb-2">
                                {contractServices.filter((s) => serviceNeedsRecurringSchedule(s)).length}{' '}
                                service
                                {contractServices.filter((s) => serviceNeedsRecurringSchedule(s))
                                  .length === 1
                                  ? ''
                                  : 's'}{' '}
                                still need a recurring schedule (days / cadence). Use{' '}
                                <span className="font-medium">Set schedule</span> on each row.
                              </div>
                            )}

                          {contractServices.length === 0 ? (
                            <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                              <p className="mb-4">No services added to the schedule yet.</p>
                              <button
                                type="button"
                                onClick={() => {
                                  const defaultRecurrenceType =
                                    formData.contract_type === 'one_time' ? 'none' : 'weekly'
                                  setNewService({
                                    service_type: '',
                                    service_name: '',
                                    amount: 0,
                                    description: '',
                                    scheduled_date: '',
                                    scheduled_time: '',
                                    recurrence_type: defaultRecurrenceType,
                                    recurrence_days: [],
                                    recurrence_day_of_month: null,
                                    recurrence_weekday: null,
                                    recurrence_occurrence: null,
                                    recurrence_month: null,
                                    recurrence_interval_months: 1,
                                    recurrence_interval_weeks: 1,
                                    recurrence_interval_days: 1,
                                    dsny_integration: false, // Always false for "Other" recurring schedule
                                    garbage_schedule: [],
                                    recycling_schedule: [],
                                    organics_schedule: [],
                                    bulk_schedule: [],
                                    interior_cleaning_schedule: [],
                                  })
                                  setShowAddServiceModal(true)
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                              >
                                <PlusIcon className="w-4 h-4" />
                                Add Service
                              </button>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {contractServices.map((service) => {
                                const needsSchedule = serviceNeedsRecurringSchedule(service)
                                const scheduleLabel = describeContractServiceSchedule(
                                  service as any,
                                  visitGroups
                                )
                                return (
                                  <div
                                    key={service.id}
                                    className={`p-4 border rounded-lg transition-colors ${
                                      needsSchedule
                                        ? 'border-amber-300 bg-amber-50/40'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-gray-900">
                                          {service.service_name}
                                        </h3>
                                        <p className="text-sm text-gray-600">
                                          ${service.amount.toFixed(2)}
                                        </p>
                                        {scheduleLabel && !needsSchedule ? (
                                          <p className="text-xs text-blue-600 mt-1">
                                            Schedule: {scheduleLabel}
                                          </p>
                                        ) : needsSchedule ? (
                                          <p className="text-xs text-amber-700 mt-1 font-medium">
                                            {service.schedule?.recurrence_type
                                              ? `Marked ${String(service.schedule.recurrence_type).replace(/_/g, ' ')} — pick days to build crew visits`
                                              : 'No recurring schedule set yet'}
                                          </p>
                                        ) : null}
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <button
                                          type="button"
                                          onClick={() => handleEditService(service.id)}
                                          className={`px-3 py-1 text-sm rounded transition-colors ${
                                            needsSchedule
                                              ? 'bg-blue-600 text-white hover:bg-blue-700'
                                              : 'border border-gray-300 hover:bg-gray-50'
                                          }`}
                                        >
                                          {needsSchedule ? 'Set schedule' : 'Edit'}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveService(service.id)}
                                          className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                              <button
                                type="button"
                                onClick={() => {
                                  const defaultRecurrenceType =
                                    formData.contract_type === 'one_time' ? 'none' : 'weekly'
                                  setNewService({
                                    service_type: '',
                                    service_name: '',
                                    amount: 0,
                                    description: '',
                                    scheduled_date: '',
                                    scheduled_time: '',
                                    recurrence_type: defaultRecurrenceType,
                                    recurrence_days: [],
                                    recurrence_day_of_month: null,
                                    recurrence_weekday: null,
                                    recurrence_occurrence: null,
                                    recurrence_month: null,
                                    recurrence_interval_months: 1,
                                    recurrence_interval_weeks: 1,
                                    recurrence_interval_days: 1,
                                    dsny_integration: false, // Always false for "Other" recurring schedule
                                    garbage_schedule: [],
                                    recycling_schedule: [],
                                    organics_schedule: [],
                                    bulk_schedule: [],
                                    interior_cleaning_schedule: [],
                                  })
                                  setShowAddServiceModal(true)
                                }}
                                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                              >
                                <PlusIcon className="w-4 h-4" />
                                Add Another Service Line
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Crew Visits - derived live from the schedule above */}
                {formData.contract_type === 'recurring' && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">Crew Visits</h2>
                        <p className="text-sm text-gray-600 mt-1">
                          Built automatically from the schedule above. Each line is one recurring
                          crew visit that generates jobs on the calendar. Saved when you create
                          the contract.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {visitGroupsCustomized && (
                          <button
                            type="button"
                            onClick={handleReDeriveVisitGroups}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                          >
                            Reset to schedule
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowVisitBoard(!showVisitBoard)}
                          className="px-3 py-1.5 text-sm border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-colors"
                        >
                          {showVisitBoard ? 'Hide customization' : 'Customize visits'}
                        </button>
                      </div>
                    </div>

                    {visitGroupsCustomized && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-3">
                        Visits were customized manually, so schedule changes above no longer update
                        them automatically. Use "Reset to schedule" to rebuild.
                      </p>
                    )}

                    {visitGroups.length === 0 ? (
                      <p className="text-sm text-gray-500 border-2 border-dashed border-gray-200 rounded-lg p-4 text-center">
                        Select days on the schedule above and crew visits will appear here
                        automatically.
                      </p>
                    ) : (
                      !showVisitBoard && (
                        <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                          {visitGroups.map((group) => {
                            const assignmentCount = getGroupAssignments(group).length
                            return (
                              <li
                                key={group.id}
                                className="px-4 py-2.5 flex items-center justify-between"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <span className="text-sm font-medium text-gray-900 truncate">
                                    {group.name || 'Visit'}
                                  </span>
                                  <span className="text-xs text-gray-500 flex-shrink-0">
                                    {describeVisitGroupCadence(group)}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500 flex-shrink-0">
                                  {assignmentCount} service{assignmentCount === 1 ? '' : 's'}
                                </span>
                              </li>
                            )
                          })}
                        </ul>
                      )
                    )}

                    {showVisitBoard && (
                      <div className="space-y-4 mt-2">
                        <p className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-2">
                          Visit groups are saved when you create the contract. There is no separate
                          Save until a contract record exists.
                        </p>
                        <VisitScheduleBoard
                          visitGroups={visitGroups}
                          onChange={handleVisitGroupsManualChange}
                          contractServices={getEffectiveServicesForVisitGroups()}
                          contractStartDate={formData.start_date || null}
                          contractEndDate={formData.end_date || null}
                          contractId=""
                          onReDerive={handleReDeriveVisitGroups}
                          onAdvancedSettings={(groupId) => setAdvancedVisitGroupId(groupId)}
                          templates={visitGroupTemplates}
                        />

                        {advancedVisitGroupId && (
                          <div className="bg-white rounded-lg border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-sm font-semibold text-gray-900">
                                Checklist &amp; advanced
                              </h3>
                              <button
                                type="button"
                                onClick={() => setAdvancedVisitGroupId(null)}
                                className="text-xs text-gray-500 hover:text-gray-700"
                              >
                                Close
                              </button>
                            </div>
                            <VisitGroupEditor
                              visitGroups={visitGroups.filter((g) => g.id === advancedVisitGroupId)}
                              onChange={(updated) => {
                                const updatedGroup = updated[0]
                                if (!updatedGroup) return
                                handleVisitGroupsManualChange(
                                  visitGroups.map((g) =>
                                    g.id === updatedGroup.id ? updatedGroup : g
                                  )
                                )
                              }}
                              contractServices={getEffectiveServicesForVisitGroups()}
                              contractStartDate={formData.start_date || null}
                              contractEndDate={formData.end_date || null}
                              contractId=""
                              showAddButton={false}
                              scheduleDriven={recurringScheduleType === 'maintenance_dsny'}
                              crewOptions={crews}
                              employeeOptions={employees}
                              templates={visitGroupTemplates}
                              onSaveAsTemplate={saveGroupAsTemplate}
                              title=""
                              subtitle=""
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {formData.status === 'active' && validateVisitGroups(visitGroups).length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                        <h3 className="text-sm font-semibold text-amber-900 mb-1">
                          Activation requirements not met
                        </h3>
                        <ul className="list-disc list-inside text-sm text-amber-800 space-y-0.5">
                          {validateVisitGroups(visitGroups).map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                        <p className="text-xs text-amber-700 mt-2">
                          You can fix these now, or save the contract as a draft and finish setup
                          before signing.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Billing & Financials Tab */}
            {activeTab === 'billing' && (
              <div className="space-y-6">
                {/* In-app Notification */}
                {notification && (
                  <div
                    className={`p-4 rounded-lg border ${
                      notification.type === 'success'
                        ? 'bg-green-50 border-green-200 text-green-800'
                        : 'bg-red-50 border-red-200 text-red-800'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium">{notification.message}</p>
                      <button
                        onClick={() => setNotification(null)}
                        className="ml-4 text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                {/* 1. LINE ITEMS SECTION */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Define billable items for this contract. Check "Variable Item" for items
                        billed based on actual usage (e.g., per hour, per unit).
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={openServiceSelectionModal}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                        title="Select services from Services & Schedule tab to add as line items"
                      >
                        <ArrowDownIcon className="w-4 h-4" />
                        Add from Services
                      </button>
                      <button
                        type="button"
                        onClick={addLineItem}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <PlusIcon className="w-4 h-4" />
                        Add Line Item
                      </button>
                    </div>
                  </div>

                  <PricingPresentationFields
                    serviceType={formData.service_type}
                    pricingMode={formData.document_data?.pricingMode}
                    onPricingModeChange={handlePricingModeChange}
                    onSeedRateCard={handleSeedRateCard}
                    lineItemCount={lineItems.length}
                  />

                  {lineItems.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                      <p className="text-gray-500 mb-4">No line items added yet.</p>
                      <p className="text-sm text-gray-400">
                        Click "Add Line Item" to create a new billing item, or "Add from Services"
                        to import from the Services & Schedule tab.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {lineItems.map((item, index) => (
                        <div
                          key={item.id}
                          className={`rounded-lg p-4 border ${
                            item.linked_service_id
                              ? 'bg-purple-50 border-purple-200'
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          {item.linked_service_id && (
                            <div className="mb-2 flex items-center gap-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                Linked to Service
                              </span>
                            </div>
                          )}
                          <div className="mb-4">
                            <label className={mobile.label}>
                              Link to Service Catalog{' '}
                              <span className="text-gray-400 font-normal">(Optional)</span>
                            </label>
                            <ServiceItemSelect
                              value={item.service_item_id || ''}
                              onChange={(id) => handleServiceItemSelect(index, id)}
                              grouped={serviceItemsGrouped}
                              allowEmpty
                              emptyValueLabel="None - Custom Entry"
                              onCatalogRefresh={fetchServiceItems}
                            />
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-4 items-start">
                            <div className="md:col-span-2">
                              <label className={mobile.label}>
                                Description *
                              </label>
                              <input
                                type="text"
                                value={item.description}
                                onChange={(e) =>
                                  handleLineItemChange(index, 'description', e.target.value)
                                }
                                className={mobile.field}
                                placeholder="Service description"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2 relative pr-24">
                                Quantity
                                <div className="absolute right-0 -top-1 flex items-start gap-1.5">
                                  <input
                                    type="checkbox"
                                    id={`variable-${item.id}`}
                                    checked={item.is_variable}
                                    onChange={(e) =>
                                      handleLineItemChange(index, 'is_variable', e.target.checked)
                                    }
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-0.5"
                                  />
                                  <label
                                    htmlFor={`variable-${item.id}`}
                                    className="text-xs text-gray-600 font-normal leading-tight"
                                  >
                                    Variable
                                    <br />
                                    Quantity
                                  </label>
                                </div>
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.quantity}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    'quantity',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                disabled={item.is_variable}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                  item.is_variable
                                    ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                                    : 'border-gray-300'
                                }`}
                                placeholder={item.is_variable ? 'Variable' : '0'}
                              />
                              {item.is_variable && (
                                <p className="mt-1 text-xs text-gray-500">Based on actual usage</p>
                              )}
                            </div>
                            <div>
                              <label className={mobile.label}>
                                Unit Type
                              </label>
                              <LineItemUnitTypeSelect
                                value={item.unit_type}
                                contractType={(formData.contract_type || 'one_time') as ContractType}
                                onChange={(value) =>
                                  handleLineItemChange(index, 'unit_type', value)
                                }
                              />
                            </div>
                            <div>
                              <label className={mobile.label}>
                                Unit Price
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={item.unit_price}
                                onChange={(e) =>
                                  handleLineItemChange(
                                    index,
                                    'unit_price',
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                                className={mobile.field}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <label className={mobile.label}>
                                  Total
                                </label>
                                <div
                                  className={`px-3 py-2 rounded-lg border text-sm font-medium ${
                                    item.is_variable
                                      ? 'bg-orange-50 border-orange-300 text-orange-700'
                                      : 'bg-white border-gray-300'
                                  }`}
                                >
                                  {item.is_variable
                                    ? `$${(item.unit_price || 0).toFixed(2)} (Variable)`
                                    : `$${item.total_price.toFixed(2)}`}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeLineItem(index)}
                                className="p-2 text-red-600 hover:text-red-800 transition-colors"
                                title="Remove item"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {item.is_variable && (
                            <p className="mt-2 text-xs text-orange-600">
                              ⚠️ Variable item: Invoice amount will be calculated based on actual{' '}
                              {item.unit_type.replace('_', ' ')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Line Items Summary */}
                  {lineItems.length > 0 && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-lg font-semibold text-gray-900">
                            Fixed Line Items Total:
                          </span>
                          {calculateBillingAmounts().variableCount > 0 && (
                            <p className="text-sm text-orange-600 mt-1">
                              + {calculateBillingAmounts().variableCount} variable item(s) billed
                              based on usage
                            </p>
                          )}
                        </div>
                        <span className="text-2xl font-bold text-blue-600">
                          ${calculateBillingAmounts().fixedTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Contract document (PDF) */}
                {formData.service_type && (
                  <div className="bg-white p-6 rounded-lg border border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Contract document (PDF)</h2>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Scope of Work (intro shown under Compensation)
                      </label>
                      <textarea
                        rows={3}
                        value={formData.scope_of_work}
                        onChange={(e) => setFormData({ ...formData, scope_of_work: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Optional narrative describing the work to be performed"
                      />
                    </div>
                    <DocumentDataEditor
                      serviceType={formData.service_type}
                      phase="contract"
                      value={formData.document_data}
                      onChange={(dd) => {
                        // A new workSpecs array reference means the user edited the
                        // spec table by hand; stop auto-drafting from the schedule.
                        if (dd.workSpecs !== formData.document_data?.workSpecs) {
                          setWorkSpecsCustomized(true)
                        }
                        setFormData({ ...formData, document_data: dd })
                      }}
                    />
                  </div>
                )}

                {/* 2. CONTRACT DURATION SECTION */}
                <div className="bg-white p-6 rounded-lg border border-gray-200" data-consistency-anchor="contract-dates">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h2 className="text-lg font-semibold text-gray-900">Contract Duration</h2>
                    <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                      <button
                        type="button"
                        onClick={() => setDurationMode('months')}
                        className={`px-3 py-1.5 ${durationMode === 'months' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Number of months
                      </button>
                      <button
                        type="button"
                        onClick={() => setDurationMode('end_date')}
                        className={`px-3 py-1.5 border-l border-gray-300 ${durationMode === 'end_date' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Specific end date
                      </button>
                    </div>
                  </div>
                  {formData.contract_type === 'one_time' && (
                    <p className="text-sm text-gray-600 mb-4">
                      Contract start and end dates are optional for one-time jobs. Optional scheduled
                      dates on each service (Services tab) control when that work is planned.
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={mobile.label}>
                        Start Date{formData.contract_type === 'recurring' ? ' *' : ''}
                      </label>
                      <input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                        className={mobile.field}
                      />
                    </div>
                    {durationMode === 'months' ? (
                      <div>
                        <label className={mobile.label}>
                          Duration (months){formData.contract_type === 'recurring' ? ' *' : ''}
                        </label>
                        <input
                          type="number"
                          min={1}
                          value={durationMonths}
                          onChange={(e) =>
                            setDurationMonths(Math.max(1, parseInt(e.target.value, 10) || 1))
                          }
                          className={mobile.field}
                        />
                        {formData.start_date && formData.end_date && (
                          <p className="text-xs text-gray-500 mt-1">
                            Ends {formData.end_date} ({durationMonths} month
                            {durationMonths === 1 ? '' : 's'})
                          </p>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className={mobile.label}>
                          End Date {formData.contract_type === 'recurring' ? '*' : ''}
                        </label>
                        <input
                          type="date"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                          className={mobile.field}
                        />
                        {formData.start_date && formData.end_date && (
                          <p className="text-xs text-gray-500 mt-1">
                            Duration:{' '}
                            {Math.ceil(
                              (new Date(formData.end_date).getTime() -
                                new Date(formData.start_date).getTime()) /
                                (1000 * 60 * 60 * 24)
                            )}{' '}
                            days
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 3. BILLING CONFIGURATION SECTION */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Billing Configuration
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div data-consistency-anchor="billing-frequency">
                      <label className={mobile.label}>
                        Billing Frequency *
                      </label>
                      <select
                        value={formData.billing_frequency}
                        onChange={(e) => {
                          const billing_frequency = e.target.value
                          const updates: any = { billing_frequency }
                          if (billing_frequency === 'progress') {
                            updates.billing_method = 'progress'
                            if (billingMilestones.length === 0) {
                              setBillingMilestones([createEmptyMilestone(0)])
                            }
                          }
                          setFormData({ ...formData, ...updates })
                        }}
                        className={mobile.field}
                      >
                        <option value="">Select frequency...</option>
                        <option value="one_time">One-Time (Single Invoice)</option>
                        <option value="after_each_service">After Each Service</option>
                        <option value="progress">Progress (Milestone Billing)</option>
                        <option value="weekly">Weekly</option>
                        <option value="bi_weekly">Bi-Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="quarterly">Quarterly</option>
                        <option value="annually">Annually</option>
                      </select>
                      {formData.start_date &&
                        formData.end_date &&
                        formData.billing_frequency !== 'one_time' &&
                        formData.billing_frequency !== 'after_each_service' && (
                          <p className="text-xs text-gray-500 mt-1">
                            ≈ {calculateBillingPeriods()} billing periods during contract duration
                          </p>
                        )}
                    </div>

                    <BillingDayOfMonthSelect
                      billingFrequency={formData.billing_frequency}
                      value={(formData as any).billing_day_of_month ?? 1}
                      onChange={(day) =>
                        setFormData({ ...formData, billing_day_of_month: day } as typeof formData)
                      }
                    />

                    {formData.billing_frequency !== 'one_time' &&
                      formData.billing_frequency !== 'after_each_service' &&
                      formData.billing_frequency !== 'progress' && (
                        <div>
                          <label className={mobile.label}>
                            Billing Method *
                          </label>
                          <select
                            value={formData.billing_method}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                billing_method: e.target.value as
                                  | 'per_period'
                                  | 'divide_total'
                                  | 'actual_usage',
                              })
                            }
                            className={mobile.field}
                          >
                            <option value="per_period">Amount per Billing Period</option>
                            <option value="divide_total">Divide Total Contract Amount</option>
                            {calculateBillingAmounts().hasVariableItems && (
                              <option value="actual_usage">Based on Actual Usage</option>
                            )}
                          </select>
                          <p className="text-xs text-gray-500 mt-1">
                            {formData.billing_method === 'per_period' &&
                              'Each invoice = Line Items Total'}
                            {formData.billing_method === 'divide_total' &&
                              'Each invoice = Contract Total ÷ Number of Periods'}
                            {formData.billing_method === 'actual_usage' &&
                              'Invoices generated based on recorded time/quantity'}
                          </p>
                        </div>
                      )}
                  </div>

                  {formData.billing_frequency === 'progress' && (
                    <MilestoneScheduleEditor
                      className="mt-6 p-4 bg-white border border-gray-200 rounded-lg"
                      milestones={billingMilestones}
                      totalAmount={
                        Number(formData.contract_total_amount) || Number(formData.total_amount) || 0
                      }
                      onChange={setBillingMilestones}
                    />
                  )}

                  {/* Billing Amount Configuration */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-gray-900">Billing Amounts</h3>
                      {formData.billing_method !== 'actual_usage' && (
                        <label className="flex items-center gap-2 text-xs text-gray-600">
                          <input
                            type="checkbox"
                            checked={billingAmountOverride}
                            onChange={(e) => setBillingAmountOverride(e.target.checked)}
                          />
                          Override amounts manually
                        </label>
                      )}
                    </div>

                    {formData.billing_method === 'divide_total' &&
                    formData.billing_frequency !== 'one_time' &&
                    formData.billing_frequency !== 'after_each_service' ? (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className={mobile.label}>
                            Total Contract Amount *
                          </label>
                          {billingAmountOverride ? (
                            <div className="relative">
                              <span className="absolute left-3 top-2 text-gray-500">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.contract_total_amount}
                                onChange={(e) => {
                                  const total = parseFloat(e.target.value) || 0
                                  const periods = calculateBillingPeriods()
                                  setFormData({
                                    ...formData,
                                    contract_total_amount: total,
                                    total_amount: periods > 0 ? total / periods : total,
                                  })
                                }}
                                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                            </div>
                          ) : (
                            <div className="px-3 py-2 bg-white rounded-lg border border-gray-300 text-sm font-semibold">
                              ${Number(formData.contract_total_amount || 0).toFixed(2)}
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {billingAmountOverride
                              ? 'Full contract value'
                              : 'Auto-calculated from line items'}
                          </p>
                        </div>
                        <div>
                          <label className={mobile.label}>
                            ÷ Billing Periods
                          </label>
                          <div className="px-3 py-2 bg-white rounded-lg border border-gray-300 text-sm">
                            {calculateBillingPeriods() || '—'}
                          </div>
                        </div>
                        <div>
                          <label className={mobile.label}>
                            = Amount Per Invoice
                          </label>
                          <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-300 text-lg font-bold text-blue-600">
                            ${formData.total_amount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    ) : formData.billing_method === 'actual_usage' ? (
                      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <h4 className="font-medium text-orange-800 mb-2">Variable Billing</h4>
                        <p className="text-sm text-orange-700">
                          Invoices will be generated based on actual recorded usage (time, visits,
                          sq ft, etc.). Fixed-price line items will be billed as specified. Variable
                          items (Qty=0) will be calculated from job records.
                        </p>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <span className="text-sm text-gray-600">Fixed items per invoice:</span>
                            <span className="ml-2 font-semibold">
                              ${calculateBillingAmounts().fixedTotal.toFixed(2)}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm text-gray-600">Variable items:</span>
                            <span className="ml-2 font-semibold">
                              {calculateBillingAmounts().variableCount} item(s)
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={mobile.label}>
                            {formData.billing_frequency === 'one_time'
                              ? 'Total Amount'
                              : 'Amount Per Invoice'}{' '}
                            *
                          </label>
                          {billingAmountOverride ? (
                            <div className="relative">
                              <span className="absolute left-3 top-2 text-gray-500">$</span>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={formData.total_amount}
                                onChange={(e) =>
                                  setFormData({
                                    ...formData,
                                    total_amount: parseFloat(e.target.value) || 0,
                                  })
                                }
                                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="0.00"
                              />
                            </div>
                          ) : (
                            <div className="px-3 py-2 bg-blue-50 rounded-lg border border-blue-300 text-lg font-bold text-blue-600">
                              ${Number(formData.total_amount || 0).toFixed(2)}
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {billingAmountOverride
                              ? `Line items total: $${calculateBillingAmounts().lineItemsTotal.toFixed(2)}`
                              : 'Auto-calculated from line items'}
                          </p>
                        </div>
                        {formData.billing_frequency !== 'one_time' &&
                          formData.billing_frequency !== 'after_each_service' && (
                            <div>
                              <label className={mobile.label}>
                                Estimated Contract Total
                              </label>
                              <div className="px-3 py-2 bg-gray-100 rounded-lg border border-gray-300 text-lg font-semibold">
                                ${(formData.total_amount * calculateBillingPeriods()).toFixed(2)}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                {formData.total_amount.toFixed(2)} × {calculateBillingPeriods()}{' '}
                                periods
                              </p>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>

                {/* 4. PAYMENT TERMS SECTION */}
                <div className="bg-white p-6 rounded-lg border border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Terms</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className={mobile.label}>
                        Payment Terms *
                      </label>
                      <select
                        value={formData.payment_terms}
                        onChange={(e) =>
                          setFormData({ ...formData, payment_terms: e.target.value })
                        }
                        className={mobile.field}
                      >
                        <option value="Due on Receipt">Due on Receipt</option>
                        <option value="Net 15">Net 15</option>
                        <option value="Net 30">Net 30</option>
                        <option value="Net 45">Net 45</option>
                        <option value="Net 60">Net 60</option>
                      </select>
                    </div>

                    <div>
                      <label className={mobile.label}>
                        Late Fee %
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={formData.late_fee_percentage}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            late_fee_percentage: parseFloat(e.target.value) || 0,
                          })
                        }
                        className={mobile.field}
                        placeholder="0.00"
                      />
                      <p className="text-xs text-gray-500 mt-1">Applied to overdue invoices</p>
                    </div>

                    <div>
                      <label className={mobile.label}>
                        Sales Tax Status
                      </label>
                      <select
                        value={formData.sales_tax_status}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            sales_tax_status: e.target.value as '' | 'taxable' | 'exempt',
                          })
                        }
                        className={mobile.field}
                      >
                        <option value="">Use Customer Default</option>
                        <option value="taxable">Taxable</option>
                        <option value="exempt">Tax Exempt</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notes Tab */}
            {activeTab === 'notes' && (
              <div className="space-y-6">
                {/* Notes Section */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Notes</h2>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={5}
                    className={mobile.field}
                    placeholder="Enter any additional notes or comments for this contract."
                  />
                </div>

                {/* Activity Section */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-4">Activity</h2>
                  {activities.length > 0 ? (
                    <div className="space-y-3">
                      {activities.map((activity) => (
                        <div key={activity.id} className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-700">{activity.description}</p>
                          <p className="text-xs text-gray-500 mt-2">
                            {new Date(activity.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-gray-500 py-8">
                      No recent activity. Activity will appear here after the contract is created.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Service Selection Modal */}
            {showServiceSelectionModal && (
              <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
                <div className="relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-2xl w-full mx-0 sm:mx-4 max-h-[90dvh] overflow-y-auto shadow-xl">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">
                      Select Services to Add as Line Items
                    </h3>
                    <button
                      onClick={() => {
                        setShowServiceSelectionModal(false)
                        setSelectedServicesForImport(new Set())
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    {/* Regular Services */}
                    {contractServices.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-700 mb-2">Services</h4>
                        <div className="space-y-2">
                          {contractServices.map((service) => (
                            <label
                              key={service.id}
                              className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedServicesForImport.has(service.id)}
                                onChange={() => toggleServiceSelection(service.id)}
                                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                              />
                              <div className="flex-1">
                                <div className="font-medium text-gray-900">
                                  {service.service_name || service.description || 'Unnamed Service'}
                                </div>
                                {service.amount > 0 && (
                                  <div className="text-sm text-gray-500">
                                    ${service.amount.toFixed(2)}
                                  </div>
                                )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* DSNY Maintenance Package */}
                    {recurringScheduleType === 'maintenance_dsny' &&
                      dsnyScheduleRows.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">
                            Maintenance Package
                          </h4>
                          <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedServicesForImport.has('dsny_package')}
                              onChange={() => toggleServiceSelection('dsny_package')}
                              className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">
                                {findDsnyPackageItem(dsnyPackageKind, serviceItemsGrouped)?.name ||
                                  (dsnyPackageKind === 'exterior_maintenance'
                                    ? 'Exterior Maintenance'
                                    : 'Building Upkeep')}
                              </div>
                                <div className="text-sm text-gray-500">
                                Includes: {dsnyScheduleRows.map((r) => r.name).join(', ')}
                              </div>
                            </div>
                          </label>
                        </div>
                      )}

                    {contractServices.length === 0 &&
                      recurringScheduleType !== 'maintenance_dsny' && (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No services available. Please add services in the Services & Schedule tab
                          first.
                        </p>
                      )}
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => {
                        setShowServiceSelectionModal(false)
                        setSelectedServicesForImport(new Set())
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={importSelectedServicesAsLineItems}
                      disabled={selectedServicesForImport.size === 0}
                      className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Add Selected ({selectedServicesForImport.size})
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

            {/* Navigation Buttons */}
            <div className="native-action-bar flex items-center justify-between gap-3 max-w-6xl mx-auto lg:mt-4 lg:rounded-xl lg:border lg:border-gray-200">
              {/* Left side: Cancel button */}
              <button
                type="button"
                onClick={() => navigate('/contracts')}
                className={mobile.secondaryBtn}
              >
                Cancel
              </button>

              {/* Right side: Back and Next/Create buttons */}
              <div className="flex gap-2 sm:gap-3">
                {activeTab !== 'overview' && (
                  <button
                    type="button"
                    onClick={handleBack}
                    className={mobile.secondaryBtn}
                  >
                    Back
                  </button>
                )}
                {activeTab === 'notes' ? (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className={mobile.primaryBtn}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="w-4 h-4" />
                        <span className="sm:hidden">Create</span>
                        <span className="hidden sm:inline">Create Contract</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!validateTab(activeTab)}
                    className={mobile.primaryBtn}
                  >
                    Next
                  </button>
                )}
              </div>
            </div>

        {/* Add Service Modal */}
        {showAddServiceModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
            <div className="relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-2xl w-full mx-0 sm:mx-4 max-h-[90dvh] overflow-y-auto shadow-xl">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                {editingServiceId
                  ? formData.contract_type === 'one_time'
                    ? 'Edit Service & Schedule'
                    : recurringScheduleType === 'maintenance_dsny'
                      ? 'Edit Maintenance Service with DSNY'
                      : recurringScheduleType === 'other'
                        ? 'Set Recurring Schedule'
                        : 'Edit Service in Recurring Schedule'
                  : formData.contract_type === 'one_time'
                    ? 'Add Service & Schedule'
                    : recurringScheduleType === 'maintenance_dsny'
                      ? 'Add Maintenance Service with DSNY'
                      : 'Add Service to Recurring Schedule'}
              </h2>

              <div className="space-y-4">
                {/* Auto-fetch DSNY schedule for Maintenance DSNY recurring when modal opens */}
                {formData.contract_type === 'recurring' &&
                  recurringScheduleType === 'maintenance_dsny' &&
                  formData.property_id &&
                  newService.dsny_integration &&
                  newService.garbage_schedule.length === 0 && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-800">
                      <p>Fetching DSNY schedule...</p>
                    </div>
                  )}

                {/* Simplified UI for One Time contracts */}
                {formData.contract_type === 'one_time' ? (
                  <>
                    <div>
                      <label className={mobile.label}>
                        Service Item *
                      </label>
                      <ServiceItemSelect
                        value={newService.service_type}
                        onChange={(serviceItemId, meta) => {
                          setNewService({
                            ...newService,
                            service_type: serviceItemId,
                            service_name: meta?.name || '',
                            amount: meta?.base_price || 0,
                            description: meta?.name || '',
                          })
                        }}
                        grouped={serviceItemsGrouped}
                        onCatalogRefresh={fetchServiceItems}
                      />
                    </div>

                    <div>
                      <label className={mobile.label}>
                        Amount <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newService.amount}
                        onChange={(e) =>
                          setNewService({ ...newService, amount: parseFloat(e.target.value) || 0 })
                        }
                        className={mobile.field}
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className={mobile.label}>
                        Description <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <textarea
                        value={newService.description}
                        onChange={(e) =>
                          setNewService({ ...newService, description: e.target.value })
                        }
                        rows={3}
                        className={mobile.field}
                        placeholder="Service description"
                      />
                    </div>

                    <div>
                      <label className={mobile.label}>
                        Schedule <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Date</label>
                          <input
                            type="date"
                            value={newService.scheduled_date}
                            onChange={(e) =>
                              setNewService({ ...newService, scheduled_date: e.target.value })
                            }
                            className={mobile.field}
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Time</label>
                          <input
                            type="time"
                            value={newService.scheduled_time}
                            onChange={(e) =>
                              setNewService({ ...newService, scheduled_time: e.target.value })
                            }
                            className={mobile.field}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    {/* Recurring contract fields */}
                    <div>
                      <label className={mobile.label}>
                        Service Item *
                      </label>
                      <ServiceItemSelect
                        value={newService.service_type}
                        onChange={(serviceItemId, meta) => {
                          setNewService((prev) => ({
                            ...prev,
                            service_type: serviceItemId,
                            service_name: meta?.name || '',
                            amount: meta?.base_price || 0,
                            dsny_integration:
                              recurringScheduleType === 'maintenance_dsny'
                                ? true
                                : prev.dsny_integration,
                            ...(formData.contract_type === 'recurring' &&
                            recurringScheduleType !== 'maintenance_dsny' &&
                            serviceItemId &&
                            (!prev.recurrence_type || prev.recurrence_type === 'none')
                              ? { recurrence_type: 'weekly' as const }
                              : {}),
                          }))
                        }}
                        grouped={serviceItemsGrouped}
                        flatItems={serviceItems}
                        showPrice={false}
                        onCatalogRefresh={fetchServiceItems}
                      />
                    </div>

                    <div>
                      <label className={mobile.label}>
                        Amount *
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newService.amount}
                        onChange={(e) =>
                          setNewService({ ...newService, amount: parseFloat(e.target.value) || 0 })
                        }
                        className={mobile.field}
                        placeholder="0.00"
                      />
                    </div>
                  </>
                )}

                {/* DSNY Integration - Only for Maintenance DSNY recurring schedule type, never for "Other" or One Time */}
                {formData.contract_type === 'recurring' &&
                  recurringScheduleType === 'maintenance_dsny' && (
                    <div className="pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id="dsny_integration_new"
                            checked={
                              recurringScheduleType === 'maintenance_dsny'
                                ? true
                                : newService.dsny_integration
                            }
                            onChange={(e) =>
                              setNewService({ ...newService, dsny_integration: e.target.checked })
                            }
                            disabled={recurringScheduleType === 'maintenance_dsny'}
                            className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500 disabled:opacity-50"
                          />
                          <label
                            htmlFor="dsny_integration_new"
                            className="text-sm font-medium text-gray-700"
                          >
                            Enable DSNY Schedule Integration
                            {recurringScheduleType === 'maintenance_dsny' && (
                              <span className="text-xs text-gray-500 ml-2">
                                (Required for Maintenance DSNY)
                              </span>
                            )}
                          </label>
                        </div>
                        {(recurringScheduleType === 'maintenance_dsny' ||
                          newService.dsny_integration) &&
                          formData.property_id && (
                            <button
                              type="button"
                              onClick={async () => {
                                const selectedProperty = properties.find(
                                  (p) => p.id === formData.property_id
                                )
                                if (!selectedProperty) {
                                  alert('Please select a property first')
                                  return
                                }

                                try {
                                  const dsnyData = await fetchDSNYPickupSchedule(
                                    selectedProperty.address || ''
                                  )
                                  const garbageDays = dsnyData.schedules.garbage || []
                                  const recyclingDays = dsnyData.schedules.recycling || []
                                  const organicsDays = dsnyData.schedules.organics || []
                                  const bulkDays = dsnyData.schedules.bulk || []

                                  // Combine all collection days to determine when to visit
                                  const allCollectionDays = Array.from(
                                    new Set([
                                      ...garbageDays,
                                      ...recyclingDays,
                                      ...organicsDays,
                                      ...bulkDays,
                                    ])
                                  )

                                  // Update newService with ALL DSNY schedules
                                  setNewService({
                                    ...newService,
                                    recurrence_days:
                                      allCollectionDays.length > 0
                                        ? allCollectionDays
                                        : newService.recurrence_days,
                                    garbage_schedule: garbageDays,
                                    recycling_schedule: recyclingDays,
                                    organics_schedule: organicsDays,
                                    bulk_schedule: bulkDays,
                                    interior_cleaning_schedule: [], // Optional - user can add manually
                                  })

                                  alert(
                                    `DSNY schedule loaded for ${selectedProperty.address}!\nGarbage: ${garbageDays.length} days\nRecycling: ${recyclingDays.length} days\nOrganics: ${organicsDays.length} days\nBulk: ${bulkDays.length} days`
                                  )
                                } catch (error) {
                                  console.error('Error fetching DSNY schedule:', error)
                                  alert('Error fetching DSNY schedule')
                                }
                              }}
                              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                            >
                              Fetch DSNY Schedule
                            </button>
                          )}
                      </div>
                      <p className="text-xs text-gray-500 mb-3">
                        When enabled, this maintenance service will automatically sync with DSNY
                        pickup schedules for garbage, recycling, organics, and bulk collection.
                      </p>
                      {(recurringScheduleType === 'maintenance_dsny' ||
                        newService.dsny_integration) &&
                        !formData.property_id && (
                          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                            ⚠️ Please select a property in the Overview tab to fetch DSNY schedule
                          </div>
                        )}

                      {/* DSNY Schedule Display - Show fetched schedules */}
                      {(recurringScheduleType === 'maintenance_dsny' ||
                        newService.dsny_integration) &&
                        (newService.garbage_schedule.length > 0 ||
                          newService.recycling_schedule.length > 0 ||
                          newService.organics_schedule.length > 0 ||
                          newService.bulk_schedule.length > 0) && (
                          <div className="mt-6 space-y-4 border-l-2 border-green-200 pl-4">
                            <h4 className="text-sm font-medium text-gray-700">
                              DSNY Collection Schedules
                            </h4>

                            {/* Garbage Schedule */}
                            {newService.garbage_schedule.length > 0 && (
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-600">
                                  Garbage Collection
                                </label>
                                                                <div className="grid grid-cols-7 gap-1.5">
                                  {dayNames.map((day) => {
                                    const selected = newService.garbage_schedule.includes(day.key)
                                    return (
                                      <span
                                        key={day.key}
                                        className={`min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                          selected
                                            ? 'bg-red-100 text-red-700 border-red-300'
                                            : 'bg-gray-50 text-gray-400 border-gray-200'
                                        }`}
                                      >
                                        {day.label.slice(0, 3)}
                                      </span>
                                    )
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Recycling Schedule */}
                            {newService.recycling_schedule.length > 0 && (
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-600">
                                  Recycling Collection
                                </label>
                                                                <div className="grid grid-cols-7 gap-1.5">
                                  {dayNames.map((day) => {
                                    const selected = newService.recycling_schedule.includes(day.key)
                                    return (
                                      <span
                                        key={day.key}
                                        className={`min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                          selected
                                            ? 'bg-blue-100 text-blue-700 border-blue-300'
                                            : 'bg-gray-50 text-gray-400 border-gray-200'
                                        }`}
                                      >
                                        {day.label.slice(0, 3)}
                                      </span>
                                    )
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Organics Schedule */}
                            {newService.organics_schedule.length > 0 && (
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-600">
                                  Organics/Compost Collection
                                </label>
                                                                <div className="grid grid-cols-7 gap-1.5">
                                  {dayNames.map((day) => {
                                    const selected = newService.organics_schedule.includes(day.key)
                                    return (
                                      <span
                                        key={day.key}
                                        className={`min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                          selected
                                            ? 'bg-green-100 text-green-700 border-green-300'
                                            : 'bg-gray-50 text-gray-400 border-gray-200'
                                        }`}
                                      >
                                        {day.label.slice(0, 3)}
                                      </span>
                                    )
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Bulk Schedule */}
                            {newService.bulk_schedule.length > 0 && (
                              <div className="space-y-2">
                                <label className="text-sm font-medium text-gray-600">
                                  Bulk Items Collection
                                </label>
                                                                <div className="grid grid-cols-7 gap-1.5">
                                  {dayNames.map((day) => {
                                    const selected = newService.bulk_schedule.includes(day.key)
                                    return (
                                      <span
                                        key={day.key}
                                        className={`min-h-[40px] flex items-center justify-center text-xs font-semibold rounded-xl border ${
                                          selected
                                            ? 'bg-purple-100 text-purple-700 border-purple-300'
                                            : 'bg-gray-50 text-gray-400 border-gray-200'
                                        }`}
                                      >
                                        {day.label.slice(0, 3)}
                                      </span>
                                    )
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                    </div>
                  )}

                {/* Schedule Configuration - Only for Recurring contracts */}
                {formData.contract_type === 'recurring' &&
                  recurringScheduleType !== 'maintenance_dsny' &&
                  !newService.service_type && (
                    <p className="pt-4 border-t border-gray-200 text-sm text-gray-500">
                      Select a service above to configure its recurrence schedule.
                    </p>
                  )}
                {formData.contract_type === 'recurring' &&
                  (recurringScheduleType === 'maintenance_dsny' || newService.service_type) && (
                  <div className="pt-4 border-t border-gray-200">
                    <h3 className="text-sm font-medium text-gray-700 mb-3">
                      Recurrence Schedule Configuration
                    </h3>

                    <div className="space-y-4">
                      <div>
                        <label className={mobile.label}>
                          Recurrence Type *
                        </label>
                        <select
                          value={newService.recurrence_type}
                          onChange={(e) => {
                            const newType = e.target.value
                            setNewService({
                              ...newService,
                              recurrence_type: newType,
                              recurrence_days: [],
                              recurrence_day_of_month: null,
                              recurrence_weekday: null,
                              recurrence_occurrence: null,
                              recurrence_month: null,
                              recurrence_interval_months: 1,
                              recurrence_interval_days: 1,
                            })
                          }}
                          className={mobile.field}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="bi_weekly">Bi-Weekly</option>
                          <option value="monthly">Monthly</option>
                          <option value="custom_interval">Custom Interval</option>
                        </select>
                      </div>

                      {/* Weekly & Bi-Weekly: Days of Week */}
                      {(newService.recurrence_type === 'weekly' ||
                        newService.recurrence_type === 'bi_weekly') && (
                        <div>
                          <label className={mobile.label}>
                            Select Days of Week
                          </label>
                                                    <div className="grid grid-cols-7 gap-1.5">
                            {dayNames.map((day) => {
                              const selected = newService.recurrence_days.includes(day.key)
                              return (
                                <button
                                  key={day.key}
                                  type="button"
                                  onClick={() => {
                                    const newDays = selected
                                      ? newService.recurrence_days.filter((d) => d !== day.key)
                                      : [...newService.recurrence_days, day.key]
                                    setNewService({ ...newService, recurrence_days: newDays })
                                  }}
                                  className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                    selected
                                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {day.label.slice(0, 3)}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )}

                      {/* Monthly: Multiple Days of Month OR Nth Weekday */}
                      {newService.recurrence_type === 'monthly' && (
                        <div className="space-y-4">
                          <div>
                            <label className={mobile.label}>
                              Schedule Type
                            </label>
                            <select
                              value={
                                newService.recurrence_weekday === null
                                  ? 'day_of_month'
                                  : 'nth_weekday'
                              }
                              onChange={(e) => {
                                if (e.target.value === 'day_of_month') {
                                  setNewService({
                                    ...newService,
                                    recurrence_days:
                                      newService.recurrence_days.length > 0
                                        ? newService.recurrence_days
                                        : [1],
                                    recurrence_weekday: null,
                                    recurrence_occurrence: null,
                                  })
                                } else {
                                  setNewService({
                                    ...newService,
                                    recurrence_days: [],
                                    recurrence_weekday: newService.recurrence_weekday ?? 1,
                                    recurrence_occurrence: newService.recurrence_occurrence ?? 1,
                                  })
                                }
                              }}
                              className={mobile.field}
                            >
                              <option value="day_of_month">
                                Days of Month (e.g., 1st, 15th, 30th)
                              </option>
                              <option value="nth_weekday">Nth Weekday (e.g., 2nd Monday)</option>
                            </select>
                          </div>

                          {newService.recurrence_weekday === null ? (
                            <div>
                              <label className={mobile.label}>
                                Select Days of Month (1-31)
                              </label>
                              <div className="grid grid-cols-8 gap-2 max-h-48 overflow-y-auto p-2 border border-gray-200 rounded-lg">
                                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                  <div key={day} className="flex items-center">
                                    <input
                                      type="checkbox"
                                      id={`monthly_day_${day}`}
                                      checked={newService.recurrence_days.includes(day)}
                                      onChange={(e) => {
                                        const newDays = e.target.checked
                                          ? [...newService.recurrence_days, day]
                                          : newService.recurrence_days.filter((d) => d !== day)
                                        setNewService({ ...newService, recurrence_days: newDays })
                                      }}
                                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                    />
                                    <label
                                      htmlFor={`monthly_day_${day}`}
                                      className="ml-2 text-sm text-gray-700"
                                    >
                                      {day}
                                    </label>
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-gray-500 mt-2">
                                Selected:{' '}
                                {newService.recurrence_days.length > 0
                                  ? newService.recurrence_days.sort((a, b) => a - b).join(', ')
                                  : 'None'}
                              </p>
                            </div>
                          ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div>
                                <label className={mobile.label}>
                                  Occurrence
                                </label>
                                <select
                                  value={newService.recurrence_occurrence || 1}
                                  onChange={(e) =>
                                    setNewService({
                                      ...newService,
                                      recurrence_occurrence: parseInt(e.target.value),
                                    })
                                  }
                                  className={mobile.field}
                                >
                                  <option value="1">1st</option>
                                  <option value="2">2nd</option>
                                  <option value="3">3rd</option>
                                  <option value="4">4th</option>
                                  <option value="5">Last</option>
                                </select>
                              </div>
                              <div className="col-span-2">
                                <label className={mobile.label}>
                                  Day of Week
                                </label>
                                <select
                                  value={newService.recurrence_weekday ?? 1}
                                  onChange={(e) =>
                                    setNewService({
                                      ...newService,
                                      recurrence_weekday: parseInt(e.target.value),
                                    })
                                  }
                                  className={mobile.field}
                                >
                                  {dayNames.map((day) => (
                                    <option key={day.key} value={day.key}>
                                      {day.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Custom Interval: Flexible scheduling */}
                      {newService.recurrence_type === 'custom_interval' && (
                        <div className="space-y-4">
                          {/* Repeats Every - Simple inline format */}
                          <div>
                            <label className={mobile.label}>
                              Repeats Every
                            </label>
                            <div className="flex items-center gap-2">
                              <input
                                type="number"
                                min="1"
                                value={
                                  newService.recurrence_interval_days > 1
                                    ? newService.recurrence_interval_days
                                    : newService.recurrence_interval_weeks > 1
                                      ? newService.recurrence_interval_weeks
                                      : newService.recurrence_interval_months || 1
                                }
                                onChange={(e) => {
                                  const val = parseInt(e.target.value) || 1
                                  if (newService.recurrence_interval_days > 1) {
                                    setNewService({ ...newService, recurrence_interval_days: val })
                                  } else if (newService.recurrence_interval_weeks > 1) {
                                    setNewService({ ...newService, recurrence_interval_weeks: val })
                                  } else {
                                    setNewService({
                                      ...newService,
                                      recurrence_interval_months: val,
                                    })
                                  }
                                }}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              />
                              <select
                                value={
                                  newService.recurrence_interval_days > 1
                                    ? 'days'
                                    : newService.recurrence_interval_weeks > 1
                                      ? 'weeks'
                                      : 'months'
                                }
                                onChange={(e) => {
                                  if (e.target.value === 'days') {
                                    setNewService({
                                      ...newService,
                                      recurrence_interval_days: 2,
                                      recurrence_interval_weeks: 1,
                                      recurrence_interval_months: 1,
                                      recurrence_days: [],
                                      recurrence_weekday: null,
                                      recurrence_occurrence: null,
                                      recurrence_day_of_month: null,
                                      recurrence_month: null,
                                    })
                                  } else if (e.target.value === 'weeks') {
                                    setNewService({
                                      ...newService,
                                      recurrence_interval_weeks: 2,
                                      recurrence_interval_days: 1,
                                      recurrence_interval_months: 1,
                                      recurrence_days: [],
                                      recurrence_weekday: null,
                                      recurrence_occurrence: null,
                                      recurrence_day_of_month: null,
                                      recurrence_month: null,
                                    })
                                  } else {
                                    setNewService({
                                      ...newService,
                                      recurrence_interval_months: 1,
                                      recurrence_interval_weeks: 1,
                                      recurrence_interval_days: 1,
                                    })
                                  }
                                }}
                                className={`${mobile.field} flex-1`}
                              >
                                <option value="days">Day(s)</option>
                                <option value="weeks">Week(s)</option>
                                <option value="months">Month(s)</option>
                              </select>
                            </div>
                            {/* Quick presets for months */}
                            {newService.recurrence_interval_days <= 1 &&
                              newService.recurrence_interval_weeks <= 1 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {[
                                    { label: 'Quarterly', value: 3 },
                                    { label: 'Semi-Annual', value: 6 },
                                    { label: 'Annual', value: 12 },
                                  ].map((preset) => (
                                    <button
                                      key={preset.value}
                                      type="button"
                                      onClick={() =>
                                        setNewService({
                                          ...newService,
                                          recurrence_interval_months: preset.value,
                                        })
                                      }
                                      className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                                        newService.recurrence_interval_months === preset.value
                                          ? 'bg-blue-100 border-blue-500 text-blue-700'
                                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                                      }`}
                                    >
                                      {preset.label}
                                    </button>
                                  ))}
                                </div>
                              )}
                          </div>

                          {/* Day selection - For weeks: day-of-week checkboxes, for months: day-of-month selection */}
                          {newService.recurrence_interval_weeks > 1 && (
                            <div>
                              <label className={mobile.label}>
                                Select Days of Week
                              </label>
                                                        <div className="grid grid-cols-7 gap-1.5">
                            {dayNames.map((day) => {
                              const selected = newService.recurrence_days.includes(day.key)
                              return (
                                <button
                                  key={day.key}
                                  type="button"
                                  onClick={() => {
                                    const newDays = selected
                                      ? newService.recurrence_days.filter((d) => d !== day.key)
                                      : [...newService.recurrence_days, day.key]
                                    setNewService({ ...newService, recurrence_days: newDays })
                                  }}
                                  className={`min-h-[44px] flex items-center justify-center text-xs font-semibold rounded-xl border transition-colors active:scale-[0.98] ${
                                    selected
                                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                  }`}
                                >
                                  {day.label.slice(0, 3)}
                                </button>
                              )
                            })}
                          </div>
                              {newService.recurrence_days.length > 0 && (
                                <p className="text-xs text-blue-600 mt-2">
                                  Every {newService.recurrence_interval_weeks} week(s) on:{' '}
                                  {newService.recurrence_days
                                    .sort((a, b) => a - b)
                                    .map((d) => dayNames.find((day) => day.key === d)?.label)
                                    .join(', ')}
                                </p>
                              )}
                            </div>
                          )}

                          {newService.recurrence_interval_days <= 1 &&
                            newService.recurrence_interval_weeks <= 1 && (
                              <>
                                <div>
                                  <label className={mobile.label}>
                                    On Which Day?
                                  </label>
                                  <select
                                    value={
                                      newService.recurrence_weekday === null
                                        ? 'day_of_month'
                                        : 'nth_weekday'
                                    }
                                    onChange={(e) => {
                                      if (e.target.value === 'day_of_month') {
                                        setNewService({
                                          ...newService,
                                          recurrence_days:
                                            newService.recurrence_days.length > 0
                                              ? newService.recurrence_days
                                              : [1],
                                          recurrence_weekday: null,
                                          recurrence_occurrence: null,
                                        })
                                      } else {
                                        setNewService({
                                          ...newService,
                                          recurrence_days: [],
                                          recurrence_weekday: 1,
                                          recurrence_occurrence: 1,
                                        })
                                      }
                                    }}
                                    className={mobile.field}
                                  >
                                    <option value="day_of_month">
                                      Specific Day(s) of the Month
                                    </option>
                                    <option value="nth_weekday">
                                      A Specific Weekday (e.g., 2nd Monday)
                                    </option>
                                  </select>
                                </div>

                                {newService.recurrence_weekday === null ? (
                                  <div>
                                    <label className={mobile.label}>
                                      Select Day(s) of Month
                                    </label>
                                    <div className="grid grid-cols-8 gap-2 max-h-40 overflow-y-auto p-2 border border-gray-200 rounded-lg bg-gray-50">
                                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                                        <div key={day} className="flex items-center">
                                          <input
                                            type="checkbox"
                                            id={`custom_day_${day}`}
                                            checked={newService.recurrence_days.includes(day)}
                                            onChange={(e) => {
                                              const newDays = e.target.checked
                                                ? [...newService.recurrence_days, day]
                                                : newService.recurrence_days.filter(
                                                    (d) => d !== day
                                                  )
                                              setNewService({
                                                ...newService,
                                                recurrence_days: newDays,
                                              })
                                            }}
                                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                          />
                                          <label
                                            htmlFor={`custom_day_${day}`}
                                            className="ml-1 text-sm text-gray-700"
                                          >
                                            {day}
                                          </label>
                                        </div>
                                      ))}
                                    </div>
                                    {newService.recurrence_days.length > 0 && (
                                      <p className="text-xs text-blue-600 mt-2">
                                        Every {newService.recurrence_interval_months} month(s) on
                                        day(s):{' '}
                                        {newService.recurrence_days
                                          .sort((a, b) => a - b)
                                          .join(', ')}
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-700">On the</span>
                                    <select
                                      value={newService.recurrence_occurrence || 1}
                                      onChange={(e) =>
                                        setNewService({
                                          ...newService,
                                          recurrence_occurrence: parseInt(e.target.value),
                                        })
                                      }
                                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      <option value="1">1st</option>
                                      <option value="2">2nd</option>
                                      <option value="3">3rd</option>
                                      <option value="4">4th</option>
                                      <option value="5">Last</option>
                                    </select>
                                    <select
                                      value={newService.recurrence_weekday ?? 1}
                                      onChange={(e) =>
                                        setNewService({
                                          ...newService,
                                          recurrence_weekday: parseInt(e.target.value),
                                        })
                                      }
                                      className={`${mobile.field} flex-1`}
                                    >
                                      {dayNames.map((day) => (
                                        <option key={day.key} value={day.key}>
                                          {day.label}
                                        </option>
                                      ))}
                                    </select>
                                    <span className="text-sm text-gray-700">of each period</span>
                                  </div>
                                )}
                              </>
                            )}

                          {/* Summary */}
                          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-sm text-blue-800">
                              <strong>Schedule:</strong>{' '}
                              {newService.recurrence_interval_days > 1
                                ? `Every ${newService.recurrence_interval_days} day(s)`
                                : newService.recurrence_interval_weeks > 1
                                  ? newService.recurrence_days.length > 0
                                    ? `Every ${
                                        newService.recurrence_interval_weeks
                                      } week(s) on: ${newService.recurrence_days
                                        .sort((a, b) => a - b)
                                        .map((d) => dayNames.find((day) => day.key === d)?.label)
                                        .join(', ')}`
                                    : 'Please select days of week'
                                  : newService.recurrence_weekday !== null
                                    ? `Every ${newService.recurrence_interval_months} month(s) on the ${
                                        ['1st', '2nd', '3rd', '4th', 'Last'][
                                          newService.recurrence_occurrence! - 1
                                        ] || '1st'
                                      } ${
                                        dayNames.find(
                                          (d) => d.key === newService.recurrence_weekday
                                        )?.label || 'Monday'
                                      }`
                                    : newService.recurrence_days.length > 0
                                      ? `Every ${
                                          newService.recurrence_interval_months
                                        } month(s) on day(s) ${newService.recurrence_days
                                          .sort((a, b) => a - b)
                                          .join(', ')}`
                                      : 'Please select days'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 pt-6 border-t border-gray-200 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddServiceModal(false)
                    setEditingServiceId(null)
                    // Reset form
                    const defaultRecurrenceType =
                      formData.contract_type === 'one_time' ? 'none' : 'weekly'
                    const isMaintenanceDSNY = recurringScheduleType === 'maintenance_dsny'
                    setNewService({
                      service_type: '',
                      service_name: '',
                      amount: 0,
                      description: '',
                      scheduled_date: '',
                      scheduled_time: '',
                      recurrence_type: defaultRecurrenceType,
                      recurrence_days: [],
                      recurrence_day_of_month: null,
                      recurrence_weekday: null,
                      recurrence_occurrence: null,
                      recurrence_month: null,
                      recurrence_interval_months: 1,
                      recurrence_interval_weeks: 1,
                      recurrence_interval_days: 1,
                      dsny_integration: isMaintenanceDSNY,
                      garbage_schedule: [],
                      recycling_schedule: [],
                      organics_schedule: [],
                      bulk_schedule: [],
                      interior_cleaning_schedule: [],
                    })
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddService}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingServiceId
                    ? recurringScheduleType === 'other'
                      ? 'Save Schedule'
                      : 'Update Service'
                    : 'Add Service'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Link Confirmation Dialog */}
        {linkDialog?.show && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
            <div className="relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-md w-full mx-0 sm:mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {linkDialog.type === 'service-to-proposal' && 'Link Service to Proposal Line Item?'}
                {linkDialog.type === 'line-item-to-service' && 'Link Line Item to Service?'}
                {linkDialog.type === 'dsny-to-proposal' && 'Link DSNY Building Upkeep to Proposal?'}
              </h3>

              <div className="mb-4">
                {linkDialog.type === 'service-to-proposal' &&
                  linkDialog.service &&
                  linkDialog.matchingProposalItem && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-700">
                        The service <strong>"{linkDialog.service.service_name}"</strong> matches a
                        proposal line item:
                      </p>
                      <div className="bg-gray-50 p-3 rounded border border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {linkDialog.matchingProposalItem.description}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">From proposal line item</p>
                      </div>
                      <p className="text-sm text-gray-600">
                        Would you like to link this service to the proposal line item?
                      </p>
                    </div>
                  )}

                {linkDialog.type === 'line-item-to-service' &&
                  linkDialog.lineItem &&
                  linkDialog.matchingService && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-700">
                        The line item <strong>"{linkDialog.lineItem.description}"</strong> matches
                        an existing service:
                      </p>
                      <div className="bg-gray-50 p-3 rounded border border-gray-200">
                        <p className="text-sm font-medium text-gray-900">
                          {linkDialog.matchingService.service_name}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">From Services & Schedule tab</p>
                      </div>
                      <p className="text-sm text-gray-600">
                        Would you like to link this line item to the service?
                      </p>
                    </div>
                  )}

                {linkDialog.type === 'dsny-to-proposal' && linkDialog.matchingProposalItem && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-700">
                      The DSNY Building Upkeep service matches a proposal line item:
                    </p>
                    <div className="bg-gray-50 p-3 rounded border border-gray-200">
                      <p className="text-sm font-medium text-gray-900">
                        {linkDialog.matchingProposalItem.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">From proposal line item</p>
                    </div>
                    <p className="text-sm text-gray-600">
                      Would you like to link the DSNY Building Upkeep service to this proposal line
                      item?
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={linkDialog.onCancel}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  No, Don't Link
                </button>
                <button
                  onClick={linkDialog.onConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Yes, Link
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Link Jobs to Contract Modal */}
        {linkJobsModal && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
            <div className="relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-2xl w-full mx-0 sm:mx-4 shadow-xl max-h-[90dvh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Link Existing Jobs to Contract?
                </h3>
                <button
                  onClick={() => {
                    setLinkJobsModal(null)
                    setSelectedJobsToLink(new Set())
                    // Navigate after closing modal
                    setTimeout(() => {
                      navigate('/contracts')
                    }, 300)
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-700 mb-4">
                  Found {linkJobsModal.jobs.length} job(s) from the same proposal that are not yet
                  linked to a contract. Would you like to link them to this contract?
                </p>

                <div className="border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                  <div className="native-desktop-table">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={
                                selectedJobsToLink.size === linkJobsModal.jobs.length &&
                                linkJobsModal.jobs.length > 0
                              }
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedJobsToLink(new Set(linkJobsModal.jobs.map((j) => j.id)))
                                } else {
                                  setSelectedJobsToLink(new Set())
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Job Number
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Title
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Scheduled Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {linkJobsModal.jobs.map((job) => (
                          <tr key={job.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={selectedJobsToLink.has(job.id)}
                                onChange={(e) => {
                                  const newSet = new Set(selectedJobsToLink)
                                  if (e.target.checked) {
                                    newSet.add(job.id)
                                  } else {
                                    newSet.delete(job.id)
                                  }
                                  setSelectedJobsToLink(newSet)
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">#{job.job_number}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{job.title}</td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {job.scheduled_date
                                ? new Date(job.scheduled_date).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  job.status === 'completed'
                                    ? 'bg-green-100 text-green-800'
                                    : job.status === 'in_progress'
                                      ? 'bg-blue-100 text-blue-800'
                                      : job.status === 'scheduled'
                                        ? 'bg-yellow-100 text-yellow-800'
                                        : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {job.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ul className="native-mobile-list divide-y divide-gray-100 hidden">
                    {linkJobsModal.jobs.map((job) => (
                      <li key={job.id}>
                        <label className="flex items-start gap-3 px-3 py-3 min-h-[52px] active:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedJobsToLink.has(job.id)}
                            onChange={(e) => {
                              const newSet = new Set(selectedJobsToLink)
                              if (e.target.checked) {
                                newSet.add(job.id)
                              } else {
                                newSet.delete(job.id)
                              }
                              setSelectedJobsToLink(newSet)
                            }}
                            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              #{job.job_number} · {job.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {job.scheduled_date
                                ? new Date(job.scheduled_date).toLocaleDateString()
                                : 'No date'}{' '}
                              · {job.status}
                            </p>
                          </div>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={async () => {
                    // Link selected jobs
                    if (selectedJobsToLink.size > 0) {
                      try {
                        const { error } = await supabase
                          .from('jobs')
                          .update({ contract_id: linkJobsModal.contractId })
                          .in('id', Array.from(selectedJobsToLink))

                        if (error) throw error

                        console.log(`✅ Linked ${selectedJobsToLink.size} job(s) to contract`)
                      } catch (error) {
                        console.error('Error linking jobs:', error)
                        alert('Error linking jobs: ' + (error as any).message)
                      }
                    }

                    setLinkJobsModal(null)
                    setSelectedJobsToLink(new Set())
                    // Navigate after linking
                    setTimeout(() => {
                      navigate('/contracts')
                    }, 300)
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Link Selected ({selectedJobsToLink.size})
                </button>
                <button
                  onClick={() => {
                    setLinkJobsModal(null)
                    setSelectedJobsToLink(new Set())
                    // Navigate after closing modal
                    setTimeout(() => {
                      navigate('/contracts')
                    }, 300)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Skip
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notification Popup Modal */}
        {notification && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
            <div
              className={`relative z-10 bg-white rounded-t-2xl sm:rounded-2xl p-4 sm:p-6 max-w-md w-full mx-0 sm:mx-4 shadow-xl ${
                notification.type === 'success'
                  ? 'border-2 border-green-500'
                  : 'border-2 border-red-500'
              }`}
            >
              <div className="flex items-center gap-4">
                <div
                  className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                    notification.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                  }`}
                >
                  {notification.type === 'success' ? (
                    <svg
                      className="w-6 h-6 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="w-6 h-6 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p
                    className={`text-lg font-semibold ${
                      notification.type === 'success' ? 'text-green-800' : 'text-red-800'
                    }`}
                  >
                    {notification.message}
                  </p>
                </div>
                <button
                  onClick={() => setNotification(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
    </MobilePage>
  )
}
